/**
 * XNuuP Dashboard v2.4
 * Dashboard + Parts Analytics + Supplier Analytics + Performance + Workshops
 * With date filtering, authentication, and role-based access control
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import i18n from './i18n/index';
import { formatCurrencyLocale } from './i18n/formatters';
import EDADashboard from './EDADashboard';
import ETLDashboard from './ETLDashboard';
import { useAuth, ProtectedRoute } from './AuthContext';
import UserManagementPage from './UserManagementPage';
import OperationsPage from './OperationsPage';
import SupplierProfilePage from './SupplierProfilePage';
import InsurerProfilePage from './Insurerprofilepage';
import WorkshopProfilePage from './Workshopprofilepage';
import DashboardWidgetManager from './components/DashboardWidgetManager';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  AreaChart, Area, ComposedChart, Line
} from 'recharts';

// ============================================================================
// Global Auth Fetch Helper
// ============================================================================

const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('xnuup_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  const res = await fetch(url, { ...options, headers });
  
  // Handle 401 Unauthorized
  if (res.status === 401) {
    localStorage.removeItem('xnuup_token');
    localStorage.removeItem('xnuup_user');
    window.location.href = '/';
    throw new Error('Session expired');
  }
  
  // Check JSON response for token errors
  const cloned = res.clone();
  try {
    const json = await cloned.json();
    if (json.success === false && 
        (json.error?.toLowerCase().includes('invalid token') || 
         json.error?.toLowerCase().includes('token expired') ||
         json.error?.toLowerCase().includes('jwt'))) {
      localStorage.removeItem('xnuup_token');
      localStorage.removeItem('xnuup_user');
      window.location.href = '/';
      throw new Error('Session expired');
    }
  } catch (e) {
    // Not JSON or already handled
  }
  
  return res;
};



// ============================================================================
// Types
// ============================================================================

type Page = 'dashboard' | 'parts' | 'suppliers' | 'performance' | 'workshops' | 'patterns' | 'operations' | 'claims' | 'orders' | 'reports' | 'shareholders' | 'eda' | 'etl' | 'users' | 'supplier-profile' | 'insurer-profile' | 'workshop-profile';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface Insurer {
  insurer_key: number;
  insurer_name: string;
}

interface ClaimsSummary {
  total_claims: number;
  total_parts: number;
  total_value: number;
  avg_part_price: number;
  avg_parts_per_claim: number;
  avg_suppliers_per_claim: number;
}

interface PartsDistribution {
  parts_bucket: string;
  claim_count: number;
}

interface StatusBreakdown {
  status_code: string;
  status_name_es: string;
  status_name_en: string;
  status_category: string;
  part_count: number;
  pct_of_total: number;
  total_value: number;
}

interface ClaimFulfillment {
  total_claims: number;
  fulfilled_claims: number;
  unfulfilled_claims: number;
  partial_claims: number;
  fulfillment_rate: number;
}

interface SupplierPerformance {
  supplier_guid: string;
  supplier_name: string;
  total_parts: number;
  authorized_parts: number;      // ADD
  authorized_amount: number;     // ADD
  avg_cost: number;              // ADD
  delivered_parts: number;
  delivery_rate: number;
  avg_delivery_days: number;     // ADD
  total_value: number;
}

interface VehicleStats {
  marca: string;
  modelo: string;
  year: number;
  total_parts: number;
  total_claims: number;
  delivery_rate: number;
  total_value: number;
}

interface InsurerPerformance {
  insurer_name: string;
  total_claims: number;
  total_parts: number;
  delivery_rate: number;
  total_value: number;
  unique_workshops: number;
}

interface DashboardData {
  generated_at: string;
  claims_summary: ClaimsSummary | null;
  network_counts: NetworkCounts | null;           // ADD
  automation_metrics: AutomationMetrics | null;   // ADD
  parts_distribution: PartsDistribution[];
  status_breakdown: StatusBreakdown[];
  claim_fulfillment: ClaimFulfillment | null;
  supplier_performance: SupplierPerformance[];
  vehicle_stats: VehicleStats[];
  insurer_performance: InsurerPerformance[];
  suppliers_per_claim: SuppliersPerClaim | null;
  state_performance: StatePerformance[];          // ADD
}

interface NetworkCounts {
  total_suppliers: number;
  total_workshops: number;
  total_insurers: number;
}

interface AutomationMetrics {
  total_parts: number;
  auto_quoted_parts: number;
  manual_quoted_parts: number;
  auto_quote_pct: number;
  manual_quote_pct: number;
  auto_assigned_parts: number;
  manual_assigned_parts: number;
  auto_assign_pct: number;
  manual_assign_pct: number;
}

interface StatePerformance {
  state: string;
  auto_assigned_parts: number;
  manual_assigned_parts: number;
  total_parts: number;
  auto_assign_pct: number;
  generic_parts: number;
  generic_pct: number;
  original_parts: number;
  original_pct: number;
  other_parts: number;
  other_pct: number;
  total_value: number;
  avg_price: number;
}

// Part Analytics Types
interface PartByVolume {
  part_number: string;
  part_name: string;
  part_type: string;
  order_count: number;
  total_quantity: number;
  unique_claims: number;
  unique_suppliers: number;
  total_value: number;
  avg_price: number;
  min_price: number;
  max_price: number;
}

interface PartByValue {
  part_number: string;
  part_name: string;
  part_type: string;
  order_count: number;
  total_value: number;
  avg_price: number;
  delivery_rate: number;
}

interface PartType {
  part_type: string;
  order_count: number;
  unique_parts: number;
  total_value: number;
  avg_price: number;
  min_price: number;
  max_price: number;
  delivery_rate: number;
  cancel_rate: number;
}

interface PartPriceVariance {
  part_number: string;
  part_name: string;
  order_count: number;
  avg_price: number;
  min_price: number;
  max_price: number;
  price_range: number;
  variance_pct: number;
}

interface PartCancellation {
  part_number: string;
  part_name: string;
  total_orders: number;
  cancelled_count: number;
  cancel_rate: number;
  cancelled_value: number;
}

interface PartsAnalyticsData {
  by_volume: {
    part_number: string;
    part_name: string;
    part_type: string;
    order_count: number;
    total_quantity: number;
    unique_claims: number;
    unique_suppliers: number;
    total_value: number;
    avg_price: number;
    min_price: number;
    max_price: number;
  }[];
  by_value: {
    part_number: string;
    part_name: string;
    part_type: string;
    order_count: number;
    total_value: number;
    avg_price: number;
    delivery_rate: number;
  }[];
  part_types: {
    part_type: string;
    order_count: number;
    unique_parts: number;
    total_value: number;
    avg_price: number;
    min_price: number;
    max_price: number;
    delivery_rate: number;
    cancel_rate: number;
  }[];
  price_variance: {
    part_number: string;
    part_name: string;
    order_count: number;
    avg_price: number;
    min_price: number;
    max_price: number;
    price_range: number;
    variance_pct: number;
  }[];
  cancellations: {
    part_number: string;
    part_name: string;
    total_orders: number;
    cancelled_count: number;
    cancel_rate: number;
    cancelled_value: number;
  }[];
}
// Supplier Analytics Types
interface SupplierRanking {
  supplier_guid: string;
  supplier_name: string;
  supplier_score: number | null;
  total_orders: number;
  total_quantity: number;
  unique_claims: number;
  unique_workshops: number;
  unique_parts: number;
  total_value: number;
  avg_price: number;
  delivery_rate: number;
  cancel_rate: number;
  avg_delivery_days: number | null;
}

interface SupplierDelivery {
  supplier_name: string;
  total_orders: number;
  avg_delivery_days: number;
  min_delivery_days: number;
  max_delivery_days: number;
  median_delivery_days: number;
  same_day_count: number;
  within_3_days: number;
  within_7_days: number;
  over_7_days: number;
}

interface SupplierPrice {
  supplier_name: string;
  compared_parts: number;
  total_orders: number;
  avg_supplier_price: number;
  avg_market_price: number;
  price_diff_pct: number;
  price_position: string;
}

interface SupplierSpecialization {
  supplier_name: string;
  top_part_type: string;
  order_count: number;
  total_value: number;
  specialization_pct: number;
}

interface SupplierCancellation {
  supplier_name: string;
  total_orders: number;
  cancelled_count: number;
  cancel_rate: number;
  cancelled_value: number;
  affected_claims: number;
}

interface SupplierAnalyticsData {
  generated_at: string;
  kpis: {
    total_suppliers: number;
    total_orders: number;
    total_value: number;
    avg_order_value: number;
    avg_delivery_days: number;
  };
  ranking: SupplierRanking[];
  delivery_analysis: SupplierDelivery[];
  price_competitiveness: SupplierPrice[];
  specialization: SupplierSpecialization[];
  cancellations: SupplierCancellation[];
  // Enhanced Suppliers Page
  state_performance: StatePerformance[];
  top_suppliers: SupplierPerformance[];
  suppliers_per_claim: SuppliersPerClaim;
}

// Performance Analytics Types
interface SupplierDeliveryPerf {
  supplier_name: string;
  total_orders: number;
  delivered_orders: number;
  avg_delivery_days: number | null;
  min_delivery_days: number | null;
  max_delivery_days: number | null;
  median_delivery_days: number | null;
  same_day: number;
  days_1_3: number;
  days_4_7: number;
  days_over_7: number;
  on_time_count: number;
  on_time_rate: number | null;
}

interface PartDeliveryTime {
  part_name: string;
  part_type: string;
  total_orders: number;
  avg_delivery_days: number | null;
  min_days: number | null;
  max_days: number | null;
  delayed_count: number;
  delay_rate: number | null;
}

interface ClaimCycleTime {
  claim_number: string;
  insurer_name: string;
  total_parts: number;
  delivered_parts: number;
  cancelled_parts: number;
  first_order_date: string | null;
  last_delivery_date: string | null;
  claim_cycle_days: number | null;
  avg_part_delivery_days: number | null;
  total_value: number;
  status: string;
}

interface OrderLifecycle {
  total_orders: number;
  with_quote: number;
  with_order: number;
  with_pickup: number;
  with_delivery: number;
  with_received: number;
  avg_quote_days: number | null;
  avg_delivered_days: number | null;
  avg_quote_to_order: number | null;
  avg_order_to_pickup: number | null;
  avg_pickup_to_delivery: number | null;
  on_time_count: number;
  late_count: number;
  on_time_rate: number | null;
}

interface EfficiencyMetrics {
  total_orders: number;
  auto_assigned: number;
  auto_quoted: number;
  auto_process: number;
  manual_integration: number;
  auto_assign_rate: number | null;
  auto_quote_rate: number | null;
  auto_process_rate: number | null;
  manual_rate: number | null;
  supplier_cancels: number;
  insurer_reassigns: number;
  manual_quotes: number;
}

interface DeliveryDistribution {
  delivery_bucket: string;
  order_count: number;
  pct_of_total: number;
  total_value: number;
}

interface PartTypeDelivery {
  part_type: string;
  total_orders: number;
  avg_delivery_days: number | null;
  median_days: number | null;
  fast_deliveries: number;
  slow_deliveries: number;
  slow_rate: number | null;
}

interface PendingPart {
  claim_number: string;
  part_name: string;
  supplier_name: string;
  status: string;
  order_date: string | null;
  deadline_date: string | null;
  days_past_deadline: number | null;
  value: number;
}

interface SuppliersPerClaim {
  avg_suppliers_per_claim: number;
  min_suppliers: number;
  max_suppliers: number;
  single_supplier_claims: number;
  two_supplier_claims: number;
  multi_supplier_claims: number;
  total_claims: number;
}

interface SupplierSegmentation {
  supplier_name: string;
  total_orders: number;
  delivery_rate: number;
  total_value: number;
  avg_delivery_days: number | null;
  part_types_served: number;
  workshops_served: number;
  claims_served: number;
  performance_tier: string;
  value_tier: string;
  reach_tier: string;
}


interface PerformanceData {
  kpis: {
    total_orders: number;
    total_claims: number;
    active_suppliers: number;
    active_workshops: number;
    total_value: number;
    avg_order_value: number;
    avg_delivery_days: number;
    on_time_rate?: number;
    auto_assign_rate?: number;
    auto_quote_rate?: number;
    on_time_orders?: number;
    late_orders?: number;
  };
  fulfillment_distribution: { bucket: string; count: number }[];
  delivery_distribution?: { bucket: string; count: number }[];
  supplier_performance: {
    supplier_name: string;
    total_orders: number;
    avg_delivery: number;
    total_value: number;
    delivered: number;
    delivery_rate: number;
  }[];
  supplier_delivery?: {
    supplier_name: string;
    delivered_orders: number;
    avg_delivery_days: number;
    same_day: number;
    on_time_rate: number;
  }[];
  part_delivery_time?: {
    part_name: string;
    total_orders: number;
    avg_delivery_days: number;
    delayed_count: number;
    delay_rate: number;
  }[];
  claim_cycle_time?: {
    claim_number: string;
    insurer_name: string;
    total_parts: number;
    claim_cycle_days: number | null;
    total_value: number;
    status: string;
  }[];
  pending_parts?: {
    claim_number: string;
    part_name: string;
    supplier_name: string;
    deadline_date: string;
    days_past_deadline: number | null;
    value: number;
  }[];
  supplier_segmentation?: SupplierSegmentation[];
  suppliers_per_claim?: SuppliersPerClaim | null;
  insurer_performance: {
    insurer_name: string;
    total_claims: number;
    total_orders: number;
    total_value: number;
    avg_claim_value: number;
    avg_delivery: number;
  }[];
  daily_trend: {
    date: string;
    orders: number;
    avg_delivery: number;
    value: number;
  }[];
  status_breakdown: {
    status_code: string;
    status_name: string;
    count: number;
    percentage: number;
  }[];
}


// Workshop Analytics Types
interface WorkshopRanking {
  workshop_name: string;
  workshop_location: string;
  total_claims: number;
  total_orders: number;
  total_value: number;
  delivery_rate: number;
  cancel_rate: number;
  avg_parts_per_claim: number;
  unique_insurers: number;
  unique_suppliers: number;
}

interface WorkshopByInsurer {
  workshop_name: string;
  insurer_name: string;
  total_claims: number;
  total_orders: number;
  total_value: number;
  delivery_rate: number;
}

interface WorkshopPartTypes {
  workshop_name: string;
  top_part_type: string;
  order_count: number;
  total_value: number;
  pct_of_orders: number;
}

interface WorkshopDelivery {
  workshop_name: string;
  total_orders: number;
  avg_delivery_days: number | null;
  on_time_count: number;
  late_count: number;
  on_time_rate: number | null;
}

interface WorkshopTrend {
  month: string;
  workshop_name: string;
  order_count: number;
  total_value: number;
}

interface WorkshopsData {
  kpis: {
    total_workshops: number;
    total_claims: number;
    total_orders: number;
    total_value: number;
    overall_delivery_rate: number;
    overall_cancel_rate: number;
  };
  ranking: {
    workshop_name: string;
    workshop_location: string;
    total_claims: number;
    total_orders: number;
    total_value: number;
    delivery_rate: number;
    cancel_rate: number;
    avg_parts_per_claim: number;
    unique_insurers: number;
    unique_suppliers: number;
  }[];
  by_insurer: {
    workshop_name: string;
    insurer_name: string;
    total_claims: number;
    total_orders: number;
    total_value: number;
    delivery_rate: number;
  }[];
  delivery_performance: {
    workshop_name: string;
    total_orders: number;
    avg_delivery_days: number;
    on_time_count: number;
    late_count: number;
    on_time_rate: number;
  }[];
  monthly_trend: {
    month: string;
    workshop_name: string;
    order_count: number;
    total_value: number;
  }[];
}

// Claim Search & Detail Types
interface ClaimSearchResult {
  claim_key: number;
  claim_number: string;
  insurer_name: string;
  workshop_name: string;
  total_parts: number;
  total_value: number;
  first_order_date: string | null;
  last_order_date: string | null;
}

interface ClaimPart {
  part_order_key: number;
  part_number: string;
  part_name: string;
  part_type: string;
  supplier_name: string;
  status: string;
  status_category: string;
  price: number;
  quantity: number;
  order_date: string | null;
  delivery_date: string | null;
  deadline_date: string | null;
  delivery_days: number | null;
}

interface ClaimStatusBreakdown {
  status: string;
  status_category: string;
  count: number;
  value: number;
}

interface ClaimSupplier {
  supplier_name: string;
  parts: number;
  value: number;
  delivery_rate: number;
}

interface ClaimHeader {
  claim_key: number;
  claim_number: string;
  insurer_name: string;
  workshop_name: string;
  workshop_location: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_year: number | null;
  total_parts: number;
  total_value: number;
  delivered_parts: number;
  cancelled_parts: number;
  pending_parts: number;
  first_order_date: string | null;
  last_delivery_date: string | null;
}

interface ClaimDetail {
  header: ClaimHeader;
  parts: ClaimPart[];
  status_breakdown: ClaimStatusBreakdown[];
  suppliers: ClaimSupplier[];
}

// Order Search & Detail Types
interface OrderSearchResult {
  part_order_key: number;
  claim_number: string;
  part_number: string;
  part_name: string;
  supplier_name: string;
  status: string;
  price: number;
  order_date: string | null;
}

interface OrderDetail {
  part_order_key: number;
  quantity: number;
  current_price: number;
  quote_days: number | null;
  is_auto_assigned: boolean;
  is_auto_quoted: boolean;
  is_auto_process: boolean;
  supplier_cancel_reason: string | null;
  insurer_reassign_reason: string | null;
  manual_quote_reason: string | null;
  claim_key: number;
  claim_number: string;
  part_number: string;
  part_name: string;
  part_type: string;
  supplier_name: string;
  supplier_guid: string | null;
  supplier_score: number | null;
  status_code: string;
  status: string;
  status_es: string;
  status_category: string;
  insurer_name: string;
  workshop_name: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_year: number | null;
  quote_date: string | null;
  order_date: string | null;
  pickup_date: string | null;
  delivery_date: string | null;
  received_date: string | null;
  deadline_date: string | null;
  delivery_days: number | null;
  days_vs_deadline: number | null;
}

interface LatestClaim {
  claim_key: number;
  claim_number: string;
  location: string;
  total_parts: number;
  delivered: number;
  pending: number;
  cancelled: number;
  supplier_count: number;
  last_order_date: string | null;
  claim_status: 'Complete' | 'Partial' | 'Pending';
}

interface ClaimsStatusSummary {
  total_claims: number;
  complete_claims: number;
  partial_claims: number;
  pending_claims: number;
}

interface LatestClaimsResponse {
  claims: LatestClaim[];
  summary: ClaimsStatusSummary;
}

interface LatestOrder {
  part_order_key: number;
  order_number: string | null;
  claim_number: string;
  part_name: string;
  location: string;
  status: string;
  status_category: string;
  price: number;
  order_date: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error: string | null;
}

// Pattern Recognition Types
interface PriceAnomaly {
  part_type: string;
  part_name: string;
  supplier_name: string;
  claim_number: string;
  order_key: number;
  claim_parts_count: number;
  price: number;
  avg_price: number;
  std_dev: number;
  z_score: number;
  anomaly_type: 'HIGH' | 'LOW';
}

interface SupplierRisk {
  supplier_name: string;
  total_orders: number;
  delivered: number;
  cancelled: number;
  supplier_cancels: number;
  avg_delivery_days: number;
  delivery_rate: number;
  cancel_rate: number;
  supplier_cancel_rate: number;
  total_value: number;
  risk_score: number;
  risk_tier: 'LOW' | 'MEDIUM' | 'HIGH';
}

interface DeliveryPrediction {
  part_type: string;
  sample_size: number;
  avg_days: number;
  median_days: number;
  min_days: number;
  max_days: number;
  p25_days: number;
  p75_days: number;
  p90_days: number;
  std_dev: number;
}

interface PartCooccurrence {
  part_a: string;
  part_b: string;
  times_together: number;
  part_a_total: number;
  part_b_total: number;
  pct_a_with_b: number;
  pct_b_with_a: number;
  lift: number;
}

interface TrendData {
  week: string;
  order_count: number;
  claim_count: number;
  total_value: number;
  delivered: number;
  cancelled: number;
  delivery_rate: number;
  ma4_orders: number;
  ma4_value: number;
  wow_change: number | null;
  wow_pct_change: number | null;
}

interface SupplierCluster {
  supplier_name: string;
  total_orders: number;
  delivery_rate: number;
  avg_price: number;
  total_value: number;
  avg_delivery_days: number;
  part_types_served: number;
  workshops_served: number;
  claims_served: number;
  performance_tier: string;
  value_tier: string;
  reach_tier: string;
}

interface AutomationImpact {
  automation_level: string;
  order_count: number;
  pct_of_total: number;
  delivered: number;
  cancelled: number;
  delivery_rate: number;
  avg_delivery_days: number;
  avg_price: number;
  avg_quote_days: number;
}

interface PartGrouping {
  parts_group: string;
  num_parts: number;
  claims_count: number;
  avg_value: number;
  total_value: number;
  pct_of_groupings: number;
}

interface PartFrequency {
  part_name: string;
  claim_count: number;
}

interface PartGroupingsData {
  groupings: PartGrouping[];
  part_frequency: PartFrequency[];
}

interface VehicleStatsSummary {
  unique_vehicles: number;
  total_claims: number;
  total_orders: number;
  unique_makes: number;
  unique_models: number;
}

interface ComplexByVehicle {
  vehicle_make: string;
  complex_claims: number;
  avg_parts: number;
  avg_suppliers: number;
  single_supplier: number;
  two_suppliers: number;
  multi_suppliers: number;
}

interface PatternsSummary {
  price_anomalies: number;
  high_risk_suppliers: number;
  complex_claims: number;
  auto_assign_rate: number;
  auto_quote_rate: number;
  vehicle_stats: VehicleStatsSummary | null;
  complex_by_vehicle: ComplexByVehicle[];
}

// ============================================================================
// Constants & Utilities
// ============================================================================

const REFRESH_INTERVAL = 300;
const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];



// const formatNumber = (n: number | null | undefined): string => {
//   if (n == null || isNaN(n)) return '0';
//   return n.toLocaleString('en-US');
// };

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(value);
};

// const formatCurrency = (n: number | string | null | undefined): string => {
//   if (n == null) return '$0';
//   const num = typeof n === 'string' ? parseFloat(n) : n;
//   if (isNaN(num)) return '$0';
//   return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
// };

// Locale-aware currency formatter — reads current language from i18n singleton
// so all components that call this re-format on language switch (via re-render)
const formatCurrency = (value: number | null | undefined): string =>
  formatCurrencyLocale(value, i18n.resolvedLanguage ?? i18n.language ?? 'es-MX');


const formatPercent = (n: number | string | null | undefined): string => {
  if (n == null) return '0%';
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(num)) return '0%';
  return num.toFixed(1) + '%';
};

const formatDays = (n: number | string | null | undefined): string => {
  if (n == null) return '-';
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(num)) return '-';
  return num.toFixed(1) + 'd';
};

// const truncate = (s: string | null | undefined, len: number): string => {
//   if (!s) return '-';
//   return s.length > len ? s.slice(0, len) + '...' : s;
// };

const truncate = (str: string, len: number): string => {
  if (!str) return '';
  return str.length > len ? str.substring(0, len) + '...' : str;
};


const formatDateTime = (dt: string | null): string => {
  if (!dt) return '-';
  return new Date(dt).toLocaleString();
};

const formatDateOnly = (dt: string | null): string => {
  if (!dt) return '-';
  return new Date(dt).toLocaleDateString();
};

// Format date as dd/mm/yyyy HH:MM:SS in user's local timezone
const formatReportDateTime = (dt: string | null | undefined): string => {
  if (!dt) return '-';
  try {
    const date = new Date(dt);
    if (isNaN(date.getTime())) return '-';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    // If time is 00:00:00, just return date (likely date-only field)
    if (hours === '00' && minutes === '00' && seconds === '00') {
      return `${day}/${month}/${year}`;
    }
    
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  } catch {
    return '-';
  }
};

// Place this BEFORE ClaimsPage and OrdersPage components (around line 800)
const getStatusStyle = (status: string, category?: string) => {
  const statusLower = (status || '').toLowerCase();
  const catLower = (category || '').toLowerCase();
  
  if (catLower === 'delivered' || catLower === 'complete' || statusLower.includes('deliver')) {
    return 'bg-green-100 text-green-800 border border-green-200';
  }
  if (catLower === 'cancelled' || statusLower.includes('cancel')) {
    return 'bg-red-100 text-red-800 border border-red-200';
  }
  if (catLower === 'returned' || statusLower.includes('return') || statusLower.includes('devolucion')) {
    return 'bg-orange-100 text-orange-800 border border-orange-200';
  }
  if (statusLower.includes('transit') || statusLower.includes('tránsito')) {
    return 'bg-blue-100 text-blue-800 border border-blue-200';
  }
  if (statusLower.includes('assign')) {
    return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
  }
  if (statusLower.includes('received')) {
    return 'bg-cyan-100 text-cyan-800 border border-cyan-200';
  }
  if (statusLower.includes('transfer')) {
    return 'bg-purple-100 text-purple-800 border border-purple-200';
  }
  return 'bg-gray-100 text-gray-700 border border-gray-200';
};

const getDefaultDateRange = (): DateRange => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

// Sorting Hook - enables column sorting on any table
function useSortableData<T extends Record<string, any>>(
  items: T[] | undefined | null,
  initialSort?: { key: string; direction: 'asc' | 'desc' }
) {
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(
    initialSort || null
  );

  const sortedItems: T[] = React.useMemo(() => {
    if (!items || !Array.isArray(items) || items.length === 0) return [];
    if (!sortConfig) return [...items];
    return [...items].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      let cmp = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [items, sortConfig]);

  const requestSort = useCallback((key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const getSortIcon = useCallback((key: string) => {
    if (sortConfig?.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  }, [sortConfig]);

  return { sortedItems, requestSort, getSortIcon };
}

// Sortable Table Header Component
  const SortTh: React.FC<{
    label: string;
    sortKey: string;
    onSort: (key: string) => void;
    sortIcon: (key: string) => string;
    className?: string;
    info?: string;
  }> = ({ label, sortKey, onSort, sortIcon, className = '', info }) => (
    <th
      onClick={() => onSort(sortKey)}
      className={`cursor-pointer hover:bg-gray-100 select-none ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {info && <InfoTooltip text={info} />}
        <span className="text-gray-400 text-xs">{sortIcon(sortKey)}</span>
      </span>
    </th>
  );

// ============================================================================
// Reusable Components
// ============================================================================

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block ml-1">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-xs font-bold hover:bg-gray-300 focus:outline-none"
        aria-label="Info"
      >
        i
      </button>
      {show && (
        <div className="absolute z-50 w-64 p-2 text-xs text-gray-700 bg-white border border-gray-200 rounded shadow-lg -left-28 top-6">
          {text}
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string; color?: string; subtext?: string; info?: string }> = ({ label, value, color, subtext, info }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="text-sm text-gray-500 flex items-center">
      {label}
      {info && <InfoTooltip text={info} />}
    </div>
    <div className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</div>
    {subtext && <div className="text-xs text-gray-400 mt-1">{subtext}</div>}
  </div>
);

const Loading: React.FC = () => (
  <div className="flex items-center justify-center py-12">
    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode; info?: string }> = ({ children, info }) => (
  <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
    {children}
    {info && <InfoTooltip text={info} />}
  </h2>
);

// ============================================================================
// Widget Visibility System
// ============================================================================

interface WidgetConfig {
  id: string;
  label: string;
  category: 'kpi' | 'chart' | 'table';
  defaultVisible: boolean;
  defaultOrder: number;
  source: 'dashboard' | 'operations' | 'performance' | 'patterns' | 'shareholders';
  description?: string;
}

interface WidgetState {
  visible: boolean;
  order: number;
}

// Universal widget definitions - all available KPIs, Charts, and Tables from all pages
const UNIVERSAL_WIDGETS: WidgetConfig[] = [
  // ========== DASHBOARD SOURCE ==========
  // Primary KPIs Row
  { id: 'kpi_total_claims', label: 'Total Claims', category: 'kpi', defaultVisible: true, defaultOrder: 1, source: 'dashboard', description: 'Total insurance claims in period' },
  { id: 'kpi_total_parts', label: 'Total Parts', category: 'kpi', defaultVisible: true, defaultOrder: 2, source: 'dashboard', description: 'Total parts ordered' },
  { id: 'kpi_total_value', label: 'Total Value', category: 'kpi', defaultVisible: true, defaultOrder: 3, source: 'dashboard', description: 'Total monetary value of orders' },
  { id: 'kpi_fulfillment_rate', label: 'Fulfillment Rate', category: 'kpi', defaultVisible: true, defaultOrder: 4, source: 'dashboard', description: 'Percentage of orders fulfilled' },
  { id: 'kpi_total_suppliers', label: 'Total Suppliers', category: 'kpi', defaultVisible: true, defaultOrder: 5, source: 'dashboard', description: 'Active supplier count' },
  { id: 'kpi_total_workshops', label: 'Total Workshops', category: 'kpi', defaultVisible: true, defaultOrder: 6, source: 'dashboard', description: 'Active workshop count' },
  { id: 'kpi_auto_quote_pct', label: 'Auto Quote %', category: 'kpi', defaultVisible: true, defaultOrder: 7, source: 'dashboard', description: 'Automation rate for quotes' },
  // Secondary KPIs Row
  { id: 'kpi_avg_parts_claim', label: 'Avg Parts/Claim', category: 'kpi', defaultVisible: true, defaultOrder: 8, source: 'dashboard', description: 'Average parts per claim' },
  { id: 'kpi_avg_suppliers_claim', label: 'Avg Suppliers/Claim', category: 'kpi', defaultVisible: true, defaultOrder: 9, source: 'dashboard', description: 'Average suppliers per claim' },
  { id: 'kpi_avg_part_price', label: 'Avg Part Price', category: 'kpi', defaultVisible: true, defaultOrder: 10, source: 'dashboard', description: 'Average part price' },
  { id: 'kpi_fulfilled_claims', label: 'Fulfilled Claims', category: 'kpi', defaultVisible: true, defaultOrder: 11, source: 'dashboard', description: 'Fully completed claims' },
  { id: 'kpi_partial_claims', label: 'Partial Claims', category: 'kpi', defaultVisible: true, defaultOrder: 12, source: 'dashboard', description: 'Partially completed claims' },
  { id: 'kpi_manual_quote_pct', label: 'Manual Quote %', category: 'kpi', defaultVisible: true, defaultOrder: 13, source: 'dashboard', description: 'Manual quote percentage' },
  { id: 'kpi_auto_assign_pct', label: 'Auto Assign %', category: 'kpi', defaultVisible: true, defaultOrder: 14, source: 'dashboard', description: 'Auto-assignment rate' },
  // Dashboard Charts
  { id: 'chart_claims_status', label: 'Claims Status Breakdown', category: 'chart', defaultVisible: true, defaultOrder: 20, source: 'dashboard', description: 'Pie chart of claim statuses' },
  { id: 'chart_part_status', label: 'Part Status Breakdown', category: 'chart', defaultVisible: true, defaultOrder: 21, source: 'dashboard', description: 'Pie chart of part order statuses' },
  { id: 'chart_parts_per_claim', label: 'Parts per Claim Distribution', category: 'chart', defaultVisible: true, defaultOrder: 22, source: 'dashboard', description: 'Distribution of parts counts' },
  // Dashboard KPI Sections
  { id: 'kpi_suppliers_per_claim', label: 'Suppliers Per Claim Stats', category: 'kpi', defaultVisible: true, defaultOrder: 25, source: 'dashboard', description: 'Supplier distribution statistics' },
  // Dashboard Tables
  { id: 'table_performance_state', label: 'Performance by State', category: 'table', defaultVisible: true, defaultOrder: 30, source: 'dashboard', description: 'Regional performance metrics' },
  { id: 'table_top_suppliers', label: 'Top Suppliers', category: 'table', defaultVisible: true, defaultOrder: 31, source: 'dashboard', description: 'Top performing suppliers' },
  { id: 'table_vehicle_stats', label: 'Vehicle Statistics', category: 'table', defaultVisible: true, defaultOrder: 32, source: 'dashboard', description: 'Claims by vehicle type' },

  // ========== OPERATIONS SOURCE ==========
  // Operations KPIs
  { id: 'ops_kpi_parts_delivered', label: 'Parts Delivered', category: 'kpi', defaultVisible: false, defaultOrder: 40, source: 'operations', description: 'Parts successfully delivered' },
  { id: 'ops_kpi_parts_in_transit', label: 'Parts In Transit', category: 'kpi', defaultVisible: false, defaultOrder: 41, source: 'operations', description: 'Parts currently in transit' },
  { id: 'ops_kpi_parts_assigned', label: 'Parts Assigned', category: 'kpi', defaultVisible: false, defaultOrder: 42, source: 'operations', description: 'Parts awaiting pickup' },
  { id: 'ops_kpi_parts_cancelled', label: 'Parts Cancelled', category: 'kpi', defaultVisible: false, defaultOrder: 43, source: 'operations', description: 'Cancelled part orders' },
  { id: 'ops_kpi_active_suppliers', label: 'Active Suppliers', category: 'kpi', defaultVisible: false, defaultOrder: 44, source: 'operations', description: 'Suppliers with active orders' },
  { id: 'ops_kpi_delivery_rate', label: 'Delivery Rate', category: 'kpi', defaultVisible: false, defaultOrder: 45, source: 'operations', description: 'Overall delivery success rate' },
  { id: 'ops_kpi_on_time_rate', label: 'On-Time Rate', category: 'kpi', defaultVisible: false, defaultOrder: 46, source: 'operations', description: 'Deliveries within 3 days' },
  { id: 'ops_kpi_on_time_orders', label: 'On-Time Orders', category: 'kpi', defaultVisible: false, defaultOrder: 47, source: 'operations', description: 'Orders delivered on time' },
  { id: 'ops_kpi_late_orders', label: 'Late Orders', category: 'kpi', defaultVisible: false, defaultOrder: 48, source: 'operations', description: 'Orders delivered late' },
  // Operations Delivery Stats
  { id: 'ops_kpi_avg_delivery_days', label: 'Avg Delivery Days', category: 'kpi', defaultVisible: false, defaultOrder: 50, source: 'operations', description: 'Mean delivery time' },
  { id: 'ops_kpi_median_delivery', label: 'Median Delivery', category: 'kpi', defaultVisible: false, defaultOrder: 51, source: 'operations', description: '50th percentile delivery' },
  { id: 'ops_kpi_p90_delivery', label: 'P90 Delivery Days', category: 'kpi', defaultVisible: false, defaultOrder: 52, source: 'operations', description: '90% delivered within' },
  // Operations Automation
  { id: 'ops_kpi_manual_orders', label: 'Manual Orders', category: 'kpi', defaultVisible: false, defaultOrder: 55, source: 'operations', description: 'Manually processed orders' },
  { id: 'ops_kpi_automated_orders', label: 'Automated Orders', category: 'kpi', defaultVisible: false, defaultOrder: 56, source: 'operations', description: 'Auto-processed orders' },
  { id: 'ops_kpi_automation_effectiveness', label: 'Automation Effectiveness', category: 'kpi', defaultVisible: false, defaultOrder: 57, source: 'operations', description: 'Auto vs manual improvement' },
  // Operations Risk
  { id: 'ops_kpi_price_anomalies', label: 'Price Anomalies', category: 'kpi', defaultVisible: false, defaultOrder: 60, source: 'operations', description: 'Unusual price detections' },
  { id: 'ops_kpi_high_risk_suppliers', label: 'High Risk Suppliers', category: 'kpi', defaultVisible: false, defaultOrder: 61, source: 'operations', description: 'Suppliers needing review' },
  { id: 'ops_kpi_complex_claims', label: 'Complex Claims', category: 'kpi', defaultVisible: false, defaultOrder: 62, source: 'operations', description: 'Claims with 5+ parts' },
  { id: 'ops_kpi_above_market', label: 'Above Market Suppliers', category: 'kpi', defaultVisible: false, defaultOrder: 63, source: 'operations', description: 'Priced above average' },
  { id: 'ops_kpi_below_market', label: 'Below Market Suppliers', category: 'kpi', defaultVisible: false, defaultOrder: 64, source: 'operations', description: 'Priced at/below average' },
  // Operations Charts
  { id: 'ops_chart_parts_status', label: 'Parts Status Table', category: 'chart', defaultVisible: false, defaultOrder: 70, source: 'operations', description: 'Parts by status' },
  { id: 'ops_chart_automation', label: 'Automation Comparison', category: 'chart', defaultVisible: false, defaultOrder: 71, source: 'operations', description: 'Manual vs auto metrics' },
  { id: 'ops_chart_delivery_metrics', label: 'Delivery Metrics Grid', category: 'chart', defaultVisible: false, defaultOrder: 72, source: 'operations', description: '6-card delivery summary' },
  { id: 'ops_chart_weekly_trend', label: 'Weekly Trend', category: 'chart', defaultVisible: false, defaultOrder: 73, source: 'operations', description: 'Week-over-week trends' },
  // Operations Tables
  { id: 'ops_table_supplier_ranking', label: 'Supplier Ranking Buckets', category: 'table', defaultVisible: false, defaultOrder: 80, source: 'operations', description: 'Suppliers by delivery tier' },
  { id: 'ops_table_market_position', label: 'Market Position', category: 'table', defaultVisible: false, defaultOrder: 81, source: 'operations', description: 'Above/below market pricing' },
  { id: 'ops_table_risk_indicators', label: 'Risk Indicators', category: 'table', defaultVisible: false, defaultOrder: 82, source: 'operations', description: 'Anomalies and risks' },
  { id: 'ops_table_cost_by_part', label: 'Cost by Part Type', category: 'table', defaultVisible: false, defaultOrder: 83, source: 'operations', description: 'Average costs by part' },
  { id: 'ops_table_cancellations', label: 'Supplier Cancellations', category: 'table', defaultVisible: false, defaultOrder: 84, source: 'operations', description: 'Cancellation rates' },

  // ========== PERFORMANCE SOURCE ==========
  // Performance KPIs
  { id: 'perf_kpi_avg_delivery_days', label: 'Avg Delivery Days (Perf)', category: 'kpi', defaultVisible: false, defaultOrder: 100, source: 'performance', description: 'Mean delivery time' },
  { id: 'perf_kpi_on_time_rate', label: 'On-Time Rate (Perf)', category: 'kpi', defaultVisible: false, defaultOrder: 101, source: 'performance', description: 'Deadline compliance rate' },
  { id: 'perf_kpi_auto_assign_rate', label: 'Auto-Assign Rate (Perf)', category: 'kpi', defaultVisible: false, defaultOrder: 102, source: 'performance', description: 'Auto supplier assignment' },
  { id: 'perf_kpi_auto_quote_rate', label: 'Auto-Quote Rate (Perf)', category: 'kpi', defaultVisible: false, defaultOrder: 103, source: 'performance', description: 'Auto price generation' },
  { id: 'perf_kpi_on_time_orders', label: 'On-Time Orders (Perf)', category: 'kpi', defaultVisible: false, defaultOrder: 104, source: 'performance', description: 'Count of on-time deliveries' },
  { id: 'perf_kpi_late_orders', label: 'Late Orders (Perf)', category: 'kpi', defaultVisible: false, defaultOrder: 105, source: 'performance', description: 'Count of late deliveries' },
  // Performance Supplier Stats
  { id: 'perf_kpi_single_supplier', label: 'Single Supplier Claims', category: 'kpi', defaultVisible: false, defaultOrder: 110, source: 'performance', description: 'Claims using 1 supplier' },
  { id: 'perf_kpi_two_suppliers', label: 'Two Supplier Claims', category: 'kpi', defaultVisible: false, defaultOrder: 111, source: 'performance', description: 'Claims using 2 suppliers' },
  { id: 'perf_kpi_multi_suppliers', label: '3+ Supplier Claims', category: 'kpi', defaultVisible: false, defaultOrder: 112, source: 'performance', description: 'Claims using 3+ suppliers' },
  // Performance Charts
  { id: 'perf_chart_delivery_distribution', label: 'Delivery Time Distribution', category: 'chart', defaultVisible: false, defaultOrder: 120, source: 'performance', description: 'Histogram of delivery times' },
  // Performance Tables
  { id: 'perf_table_supplier_delivery', label: 'Supplier Delivery Performance', category: 'table', defaultVisible: false, defaultOrder: 130, source: 'performance', description: 'Supplier speed metrics' },
  { id: 'perf_table_slowest_parts', label: 'Slowest Parts to Deliver', category: 'table', defaultVisible: false, defaultOrder: 131, source: 'performance', description: 'Parts with longest delivery' },
  { id: 'perf_table_claim_cycle', label: 'Claim Cycle Time', category: 'table', defaultVisible: false, defaultOrder: 132, source: 'performance', description: 'Total claim duration' },
  { id: 'perf_table_pending_deadline', label: 'Pending Past Deadline', category: 'table', defaultVisible: false, defaultOrder: 133, source: 'performance', description: 'Overdue pending orders' },
  { id: 'perf_table_suppliers_per_claim', label: 'Suppliers per Claim Stats', category: 'table', defaultVisible: false, defaultOrder: 134, source: 'performance', description: 'Supplier distribution' },
  { id: 'perf_table_segmentation', label: 'Supplier Segmentation', category: 'table', defaultVisible: false, defaultOrder: 135, source: 'performance', description: 'Supplier tiers matrix' },

  // ========== PATTERNS SOURCE ==========
  // Patterns Summary KPIs
  { id: 'pat_kpi_price_anomalies', label: 'Price Anomalies (Pat)', category: 'kpi', defaultVisible: false, defaultOrder: 150, source: 'patterns', description: 'Unusual pricing detected' },
  { id: 'pat_kpi_high_risk_suppliers', label: 'High Risk Suppliers (Pat)', category: 'kpi', defaultVisible: false, defaultOrder: 151, source: 'patterns', description: '>15% cancellation rate' },
  { id: 'pat_kpi_complex_claims', label: 'Complex Claims (Pat)', category: 'kpi', defaultVisible: false, defaultOrder: 152, source: 'patterns', description: '>8 parts AND >4 suppliers' },
  { id: 'pat_kpi_unique_vehicles', label: 'Unique Vehicles', category: 'kpi', defaultVisible: false, defaultOrder: 153, source: 'patterns', description: 'Distinct vehicles in data' },
  { id: 'pat_kpi_unique_makes', label: 'Unique Makes', category: 'kpi', defaultVisible: false, defaultOrder: 154, source: 'patterns', description: 'Distinct vehicle makes' },
  { id: 'pat_kpi_unique_models', label: 'Unique Models', category: 'kpi', defaultVisible: false, defaultOrder: 155, source: 'patterns', description: 'Distinct vehicle models' },
  // Patterns Tables
  { id: 'pat_table_vehicle_stats', label: 'Vehicle Statistics (Pat)', category: 'table', defaultVisible: false, defaultOrder: 160, source: 'patterns', description: 'Vehicle overview metrics' },
  { id: 'pat_table_complex_by_vehicle', label: 'Complex Claims by Make', category: 'table', defaultVisible: false, defaultOrder: 161, source: 'patterns', description: 'Complexity by manufacturer' },
  { id: 'pat_table_price_anomalies', label: 'Price Anomalies Table', category: 'table', defaultVisible: false, defaultOrder: 162, source: 'patterns', description: 'Z-score outliers' },
  { id: 'pat_table_supplier_risk', label: 'Supplier Risk Scoring', category: 'table', defaultVisible: false, defaultOrder: 163, source: 'patterns', description: 'Risk scores 0-100' },
  { id: 'pat_table_delivery_prediction', label: 'Delivery Prediction', category: 'table', defaultVisible: false, defaultOrder: 164, source: 'patterns', description: 'P90 by part type' },
  { id: 'pat_table_part_associations', label: 'Part Associations', category: 'table', defaultVisible: false, defaultOrder: 165, source: 'patterns', description: 'Market basket analysis' },
  { id: 'pat_table_supplier_clusters', label: 'Supplier Clusters', category: 'table', defaultVisible: false, defaultOrder: 166, source: 'patterns', description: 'Performance groupings' },
  { id: 'pat_table_automation_impact', label: 'Automation Impact', category: 'table', defaultVisible: false, defaultOrder: 167, source: 'patterns', description: 'Auto vs manual results' },
  // Patterns Charts
  { id: 'pat_chart_trends', label: 'Pattern Trends', category: 'chart', defaultVisible: false, defaultOrder: 170, source: 'patterns', description: 'Monthly/weekly analysis' },

  // ========== SHAREHOLDERS SOURCE ==========
  // Shareholders KPIs
  { id: 'sh_kpi_total_order_value', label: 'Total Order Value', category: 'kpi', defaultVisible: false, defaultOrder: 200, source: 'shareholders', description: 'All orders pipeline value' },
  { id: 'sh_kpi_invoiced_revenue', label: 'Invoiced Revenue', category: 'kpi', defaultVisible: false, defaultOrder: 201, source: 'shareholders', description: 'Realized revenue' },
  { id: 'sh_kpi_pipeline_gap', label: 'Pipeline Gap', category: 'kpi', defaultVisible: false, defaultOrder: 202, source: 'shareholders', description: 'Unrealized value' },
  { id: 'sh_kpi_realization_rate', label: 'Realization Rate', category: 'kpi', defaultVisible: false, defaultOrder: 203, source: 'shareholders', description: 'Revenue conversion %' },
  { id: 'sh_kpi_pending_value', label: 'Pending Value', category: 'kpi', defaultVisible: false, defaultOrder: 204, source: 'shareholders', description: 'In-progress orders value' },
  { id: 'sh_kpi_cancelled_value', label: 'Cancelled Value', category: 'kpi', defaultVisible: false, defaultOrder: 205, source: 'shareholders', description: 'Lost order value' },
  { id: 'sh_kpi_avg_order_value', label: 'Avg Order Value', category: 'kpi', defaultVisible: false, defaultOrder: 206, source: 'shareholders', description: 'Mean order size' },
  // Shareholders Charts
  { id: 'sh_chart_growth_trend', label: 'Order Value Growth', category: 'chart', defaultVisible: false, defaultOrder: 210, source: 'shareholders', description: 'Cumulative value trend' },
  { id: 'sh_chart_orders_vs_invoiced', label: 'Orders vs Invoiced', category: 'chart', defaultVisible: false, defaultOrder: 211, source: 'shareholders', description: 'Revenue gap comparison' },
  { id: 'sh_chart_pipeline_status', label: 'Pipeline by Status', category: 'chart', defaultVisible: false, defaultOrder: 212, source: 'shareholders', description: 'Order status breakdown' },
  { id: 'sh_chart_monthly_growth', label: 'Month-over-Month Growth', category: 'chart', defaultVisible: false, defaultOrder: 213, source: 'shareholders', description: 'MoM growth rates' },
  { id: 'sh_chart_weekly_perf', label: 'Weekly Performance', category: 'chart', defaultVisible: false, defaultOrder: 214, source: 'shareholders', description: 'Last 8 weeks comparison' },
  // Shareholders Tables
  { id: 'sh_table_top_insurers', label: 'Top Insurers by Value', category: 'table', defaultVisible: false, defaultOrder: 220, source: 'shareholders', description: 'Insurer revenue ranking' },
  { id: 'sh_table_top_suppliers', label: 'Top Suppliers by Value', category: 'table', defaultVisible: false, defaultOrder: 221, source: 'shareholders', description: 'Supplier revenue ranking' },
];

// For backwards compatibility, keep DASHBOARD_WIDGETS as filtered view
const DASHBOARD_WIDGETS: WidgetConfig[] = UNIVERSAL_WIDGETS.filter(w => w.source === 'dashboard');

// Maps UNIVERSAL_WIDGETS id → user_widget_permissions widget_key.
// Required because UNIVERSAL_WIDGETS and WIDGET_REGISTRY use different naming schemes:
// - Dashboard UNIVERSAL_WIDGETS ids have no 'dash_' prefix; WIDGET_REGISTRY keys do.
// - Four non-dashboard entries have divergent names between the two registries.
// Entries omitted here fall back to w.id, which is correct for ops_/pat_/sh_ ids that already match.
const WIDGET_KEY_MAP: Record<string, string> = {
  // Dashboard widgets — UNIVERSAL_WIDGETS omits the 'dash_' prefix that WIDGET_REGISTRY uses
  kpi_total_claims:        'dash_kpi_total_claims',
  kpi_total_parts:         'dash_kpi_total_parts',
  kpi_total_value:         'dash_kpi_total_value',
  kpi_fulfillment_rate:    'dash_kpi_fulfillment_rate',
  kpi_total_suppliers:     'dash_kpi_total_suppliers',
  kpi_total_workshops:     'dash_kpi_total_workshops',
  kpi_auto_quote_pct:      'dash_kpi_auto_quote_pct',
  kpi_avg_parts_claim:     'dash_kpi_avg_parts_claim',
  kpi_avg_suppliers_claim: 'dash_kpi_avg_suppliers_claim',
  kpi_avg_part_price:      'dash_kpi_avg_part_price',
  kpi_fulfilled_claims:    'dash_kpi_fulfilled_claims',
  kpi_partial_claims:      'dash_kpi_partial_claims',
  kpi_manual_quote_pct:    'dash_kpi_manual_quote_pct',
  kpi_auto_assign_pct:     'dash_kpi_auto_assign_pct',
  kpi_suppliers_per_claim: 'dash_kpi_suppliers_per_claim',
  chart_claims_status:     'dash_chart_claims_status',
  chart_part_status:       'dash_chart_part_status',
  chart_parts_per_claim:   'dash_chart_parts_per_claim',
  table_performance_state: 'dash_table_state_perf',
  table_top_suppliers:     'dash_table_top_suppliers',
  table_vehicle_stats:     'dash_table_vehicle_stats',
  // Non-dashboard entries where UNIVERSAL_WIDGETS id ≠ WIDGET_REGISTRY widget_key
  ops_kpi_automation_effectiveness: 'ops_kpi_auto_effectiveness',
  perf_kpi_avg_delivery_days:       'perf_kpi_avg_delivery',
  perf_chart_delivery_distribution: 'perf_chart_delivery_dist',
  perf_table_suppliers_per_claim:   'perf_table_suppliers_claim',
};

// Hook for managing widget visibility and order
// Widget visibility version - increment when adding new widgets
const WIDGET_STATE_VERSION = 3;

const useWidgetVisibility = (pageId: string, widgets: WidgetConfig[]) => {
  const storageKey = `xnuup_widgets_v${WIDGET_STATE_VERSION}_${pageId}`;
  
  const [state, setState] = useState<Record<string, WidgetState>>(() => {
    // Start with defaults for all widgets
    const defaults = widgets.reduce((acc, w) => ({ 
      ...acc, 
      [w.id]: { visible: w.defaultVisible, order: w.defaultOrder } 
    }), {} as Record<string, WidgetState>);
    
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge stored with defaults (stored takes precedence for existing widgets)
        return { ...defaults, ...parsed };
      }
    } catch (e) { /* ignore */ }
    return defaults;
  });

  const saveState = (newState: Record<string, WidgetState>) => {
    localStorage.setItem(storageKey, JSON.stringify(newState));
  };

  const toggle = (widgetId: string) => {
    setState(prev => {
      const widget = widgets.find(w => w.id === widgetId);
      const currentState = prev[widgetId] || { visible: widget?.defaultVisible ?? false, order: widget?.defaultOrder ?? 999 };
      const wasVisible = currentState.visible;
      const next = { 
        ...prev, 
        [widgetId]: { ...currentState, visible: !wasVisible } 
      };
      saveState(next);
      return next;
    });
  };

  const moveUp = (widgetId: string, category: string) => {
    setState(prev => {
      const categoryWidgets = widgets
        .filter(w => w.category === category)
        .map(w => ({ ...w, ...prev[w.id] }))
        .sort((a, b) => a.order - b.order);
      
      const idx = categoryWidgets.findIndex(w => w.id === widgetId);
      if (idx <= 0) return prev;
      
      const currentOrder = prev[widgetId].order;
      const swapId = categoryWidgets[idx - 1].id;
      const swapOrder = prev[swapId].order;
      
      const next = {
        ...prev,
        [widgetId]: { ...prev[widgetId], order: swapOrder },
        [swapId]: { ...prev[swapId], order: currentOrder }
      };
      saveState(next);
      return next;
    });
  };

  const moveDown = (widgetId: string, category: string) => {
    setState(prev => {
      const categoryWidgets = widgets
        .filter(w => w.category === category)
        .map(w => ({ ...w, ...prev[w.id] }))
        .sort((a, b) => a.order - b.order);
      
      const idx = categoryWidgets.findIndex(w => w.id === widgetId);
      if (idx < 0 || idx >= categoryWidgets.length - 1) return prev;
      
      const currentOrder = prev[widgetId].order;
      const swapId = categoryWidgets[idx + 1].id;
      const swapOrder = prev[swapId].order;
      
      const next = {
        ...prev,
        [widgetId]: { ...prev[widgetId], order: swapOrder },
        [swapId]: { ...prev[swapId], order: currentOrder }
      };
      saveState(next);
      return next;
    });
  };

  const setAll = (visible: boolean) => {
    const next = widgets.reduce((acc, w) => ({ 
      ...acc, 
      [w.id]: { ...state[w.id], visible } 
    }), {} as Record<string, WidgetState>);
    setState(next);
    saveState(next);
  };

  const reset = () => {
    const defaults = widgets.reduce((acc, w) => ({ 
      ...acc, 
      [w.id]: { visible: w.defaultVisible, order: w.defaultOrder } 
    }), {} as Record<string, WidgetState>);
    setState(defaults);
    saveState(defaults);
  };

  const isVisible = (widgetId: string) => {
    const widget = widgets.find(w => w.id === widgetId);
    return state[widgetId]?.visible ?? widget?.defaultVisible ?? false;
  };
  
  const getOrder = (widgetId: string) => state[widgetId]?.order ?? 999;
  
  const getOrderedWidgets = (category?: string) => {
    return widgets
      .filter(w => !category || w.category === category)
      .map(w => ({ 
        ...w, 
        visible: state[w.id]?.visible ?? w.defaultVisible,
        order: state[w.id]?.order ?? w.defaultOrder
      }))
      .filter(w => w.visible)
      .sort((a, b) => a.order - b.order);
  };

  const getOrderedWidgetsForPanel = (category: string) => {
    return widgets
      .filter(w => w.category === category)
      .map(w => ({ 
        ...w, 
        visible: state[w.id]?.visible ?? w.defaultVisible,
        order: state[w.id]?.order ?? w.defaultOrder
      }))
      .sort((a, b) => a.order - b.order);
  };

  const visibleCount = (Object.values(state) as WidgetState[]).filter(s => s?.visible).length;

  const loadState = (newState: Record<string, WidgetState>) => {
    setState(newState);
    saveState(newState);
  };

  return {
    state, toggle, moveUp, moveDown, setAll, reset, loadState,
    isVisible, getOrder, getOrderedWidgets, getOrderedWidgetsForPanel, visibleCount
  };
};

// Widget Toggle Panel Component with reordering
const WidgetTogglePanel: React.FC<{
  widgets: WidgetConfig[];
  state: Record<string, WidgetState>;
  onToggle: (id: string) => void;
  onMoveUp: (id: string, category: string) => void;
  onMoveDown: (id: string, category: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
  onReset: () => void;
  visibleCount: number;
  getOrderedWidgetsForPanel: (category: string) => (WidgetConfig & WidgetState)[];
}> = ({ widgets, state, onToggle, onMoveUp, onMoveDown, onShowAll, onHideAll, onReset, visibleCount, getOrderedWidgetsForPanel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>('kpi');
  const [viewMode, setViewMode] = useState<'category' | 'source'>('category');
  const [expandedSource, setExpandedSource] = useState<string | null>('dashboard');
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const categories = [
    { key: 'kpi', label: 'KPIs & Metrics', icon: '📊' },
    { key: 'chart', label: 'Charts', icon: '📈' },
    { key: 'table', label: 'Tables', icon: '📋' },
  ];

  const sources = [
    { key: 'dashboard', label: 'Dashboard', color: 'blue' },
    { key: 'operations', label: 'Operations', color: 'green' },
    { key: 'performance', label: 'Performance', color: 'purple' },
    { key: 'patterns', label: 'Patterns', color: 'orange' },
    { key: 'shareholders', label: 'Shareholders', color: 'indigo' },
  ];

  const getWidgetsBySource = (source: string) => {
    return widgets
      .filter(w => w.source === source)
      .map(w => ({ ...w, visible: state[w.id]?.visible ?? w.defaultVisible, order: state[w.id]?.order ?? w.defaultOrder }))
      .sort((a, b) => a.order - b.order);
  };

  const sourceColors: Record<string, string> = {
    dashboard: 'bg-blue-100 text-blue-700',
    operations: 'bg-green-100 text-green-700',
    performance: 'bg-purple-100 text-purple-700',
    patterns: 'bg-orange-100 text-orange-700',
    shareholders: 'bg-indigo-100 text-indigo-700',
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        title="Configure visible widgets"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span>Widgets</span>
        <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs font-medium">{visibleCount}/{widgets.length}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border z-50">
          <div className="p-3 border-b flex justify-between items-center">
            <span className="font-semibold text-gray-700">Configure Widgets</span>
            <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="p-2 border-b bg-gray-50 flex gap-1">
            <button
              onClick={() => setViewMode('category')}
              className={`flex-1 px-2 py-1.5 text-xs rounded font-medium transition-colors ${viewMode === 'category' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
            >
              By Type
            </button>
            <button
              onClick={() => setViewMode('source')}
              className={`flex-1 px-2 py-1.5 text-xs rounded font-medium transition-colors ${viewMode === 'source' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
            >
              By Page
            </button>
          </div>
          
          <div className="p-2 border-b flex gap-2">
            <button onClick={onShowAll} className="flex-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">Show All</button>
            <button onClick={onHideAll} className="flex-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">Hide All</button>
            <button onClick={onReset} className="flex-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Reset</button>
          </div>

          <div className="max-h-[28rem] overflow-y-auto">
            {viewMode === 'category' ? (
              // Category View
              categories.map(cat => {
                const catWidgets = getOrderedWidgetsForPanel(cat.key);
                if (catWidgets.length === 0) return null;
                const isExpanded = expandedCategory === cat.key;
                const visibleInCat = catWidgets.filter(w => w.visible).length;
                
                return (
                  <div key={cat.key} className="border-b last:border-b-0">
                    <button
                      onClick={() => setExpandedCategory(isExpanded ? null : cat.key)}
                      className="w-full p-3 flex justify-between items-center hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{cat.icon}</span>
                        <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-xs font-semibold text-gray-600 uppercase">{cat.label}</span>
                      </div>
                      <span className="text-xs text-gray-500">{visibleInCat}/{catWidgets.length}</span>
                    </button>
                    
                    {isExpanded && (
                      <div className="pb-2 px-2">
                        {catWidgets.map((w, idx) => (
                          <div key={w.id} className="flex items-center gap-1 py-1 px-2 hover:bg-gray-50 rounded group">
                            <input
                              type="checkbox"
                              checked={w.visible}
                              onChange={() => onToggle(w.id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 flex items-center gap-1.5 min-w-0">
                              <span className={`flex-1 text-sm truncate ${w.visible ? 'text-gray-700' : 'text-gray-400'}`} title={w.description}>{w.label}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${sourceColors[w.source]}`}>{w.source.slice(0,3).toUpperCase()}</span>
                            </div>
                            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => { e.stopPropagation(); onMoveUp(w.id, cat.key); }}
                                disabled={idx === 0}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move up"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); onMoveDown(w.id, cat.key); }}
                                disabled={idx === catWidgets.length - 1}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move down"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              // Source View
              sources.map(src => {
                const srcWidgets = getWidgetsBySource(src.key);
                if (srcWidgets.length === 0) return null;
                const isExpanded = expandedSource === src.key;
                const visibleInSrc = srcWidgets.filter(w => w.visible).length;
                
                return (
                  <div key={src.key} className="border-b last:border-b-0">
                    <button
                      onClick={() => setExpandedSource(isExpanded ? null : src.key)}
                      className="w-full p-3 flex justify-between items-center hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full bg-${src.color}-500`}></span>
                        <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">{src.label}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${sourceColors[src.key]}`}>{visibleInSrc}/{srcWidgets.length}</span>
                    </button>
                    
                    {isExpanded && (
                      <div className="pb-2 px-2">
                        {srcWidgets.map((w) => (
                          <div key={w.id} className="flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={w.visible}
                              onChange={() => onToggle(w.id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm block truncate ${w.visible ? 'text-gray-700' : 'text-gray-400'}`}>{w.label}</span>
                              {w.description && <span className="text-[10px] text-gray-400 block truncate">{w.description}</span>}
                            </div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${w.category === 'kpi' ? 'bg-blue-50 text-blue-600' : w.category === 'chart' ? 'bg-green-50 text-green-600' : 'bg-purple-50 text-purple-600'}`}>
                              {w.category.toUpperCase()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          
          <div className="p-2 border-t bg-gray-50 text-xs text-gray-500 text-center">
            {viewMode === 'category' ? 'Hover to reorder • ' : ''}Click to toggle visibility
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Login Component
// ============================================================================

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const result = await login(email, password);

    if (!result.success) {
      setError(result.error || t('auth.invalidCredentials'));
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#181f26]">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-10">
            <img src="/assets/xnuup-main-logo-white.png" alt="XNuuP" className="h-12" />
            <p className="mt-3 text-gray-400 text-sm">{t('auth.subtitle')}</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#232d38] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3a7de4] focus:border-transparent transition-all"
                placeholder="you@example.com"
                autoFocus
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('auth.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#232d38] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3a7de4] focus:border-transparent transition-all"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-[#3a7de4] hover:bg-blue-600 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('auth.signingIn')}
                </>
              ) : (
                t('auth.signIn')
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-gray-700">
            <p className="text-center text-gray-500 text-sm">{t('auth.copyright')}</p>
          </div>
        </div>
      </div>

      {/* Right side - Background image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/assets/shutterstock_2261748231_right.jpg)' }}
      >
        <div className="h-full w-full bg-gradient-to-r from-[#181f26] via-[#181f26]/70 to-transparent flex items-center">
          <div className="p-12 max-w-lg">
            <h1 className="text-4xl font-bold text-white mb-4">Automotive Parts Intelligence</h1>
            <p className="text-gray-300 text-lg leading-relaxed">
              Real-time analytics for claims processing, supplier performance, and parts distribution across your network.
            </p>
            <div className="mt-8 flex gap-8">
              <div>
                <div className="text-3xl font-bold text-[#3a7de4]">21K+</div>
                <div className="text-gray-400 text-sm">Parts Tracked</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#3a7de4]">322</div>
                <div className="text-gray-400 text-sm">Suppliers</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#3a7de4]">85</div>
                <div className="text-gray-400 text-sm">Workshops</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Navigation Icons
// ============================================================================

const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
  </svg>
);

const ClaimsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const OrdersIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
  </svg>
);

const PartsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
  </svg>
);

const SuppliersIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.125-.504 1.125-1.125v-1.5a3 3 0 00-3-3h-.75l-.35-1.5a1.125 1.125 0 00-1.1-.875H9.375c-.621 0-1.125.504-1.125 1.125V14.25m8.625 4.5H9.375" />
  </svg>
);

const WorkshopsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
  </svg>
);

const PerformanceIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

const PatternsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const EDAIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
  </svg>
);

const ETLIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const ReportsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const ShareholdersIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
  </svg>
);

const OperationsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

// ============================================================================
// Navigation Sidebar Component
// ============================================================================

interface NavItem {
  id: Page;
  label: string;
  icon: React.ReactNode;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const Navigation: React.FC<{ 
  activePage: Page; 
  onPageChange: (page: Page) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onLogout: () => void;
  userRole: string;
  hasPageAccess: (page: string) => boolean;
  user: { first_name?: string; last_name?: string; email?: string; full_name?: string; supplier_key?: number | null; workshop_key?: number | null; insurer_keys?: number[] } | null;
}> = ({ activePage, onPageChange, collapsed, onToggleCollapse, onLogout, userRole, hasPageAccess, user }) => {
  


  const { t } = useTranslation();

  const navGroups: NavGroup[] = [
    {
      title: t('navGroup.overview'),
      items: ([
        { id: 'dashboard', label: t('nav.dashboard'), icon: <DashboardIcon /> },
        { id: 'operations', label: t('nav.operations'), icon: <OperationsIcon /> },
      ] as NavItem[]).filter(item => hasPageAccess(item.id))
    },
    {
      title: t('navGroup.operations'),
      items: ([
        { id: 'claims', label: t('nav.claims'), icon: <ClaimsIcon /> },
        { id: 'orders', label: t('nav.orders'), icon: <OrdersIcon /> },
        { id: 'parts', label: t('nav.parts'), icon: <PartsIcon /> },
      ] as NavItem[]).filter(item => hasPageAccess(item.id))
    },
    {
      title: t('navGroup.network'),
      items: ([
        { id: 'suppliers', label: t('nav.suppliers'), icon: <SuppliersIcon /> },
        { id: 'workshops', label: t('nav.workshops'), icon: <WorkshopsIcon /> },
      ] as NavItem[]).filter(item => hasPageAccess(item.id))
    },
    {
      title: t('navGroup.insights'),
      items: ([
        { id: 'performance', label: t('nav.performance'), icon: <PerformanceIcon /> },
        { id: 'patterns', label: t('nav.patterns'), icon: <PatternsIcon /> },
      ] as NavItem[]).filter(item => hasPageAccess(item.id))
    },
    {
      title: t('navGroup.tools'),
      items: ([
        { id: 'reports', label: t('nav.reports'), icon: <ReportsIcon /> },
        { id: 'eda', label: t('nav.eda'), icon: <EDAIcon /> },
        { id: 'etl', label: t('nav.etl'), icon: <ETLIcon /> },
      ] as NavItem[]).filter(item => hasPageAccess(item.id))
    },
    {
      title: t('navGroup.executive'),
      items: ([
        { id: 'shareholders', label: t('nav.shareholders'), icon: <ShareholdersIcon /> },
      ] as NavItem[]).filter(item => hasPageAccess(item.id))
    },
    {
      title: t('navGroup.profiles'),
      items: ([
        { id: 'supplier-profile', label: t('nav.supplierProfile'), icon: <DashboardIcon /> },
        { id: 'workshop-profile', label: t('nav.workshopProfile'), icon: <DashboardIcon /> },
        { id: 'insurer-profile', label: t('nav.insurerProfile'), icon: <DashboardIcon /> },
      ] as NavItem[]).filter(item => hasPageAccess(item.id))
    },
    {
      title: t('navGroup.admin'),
      items: ([
        { id: 'users', label: t('nav.users'), icon: <UsersIcon /> },
      ] as NavItem[]).filter(item => hasPageAccess(item.id))
    },
    ].filter(group => group.items.length > 0);
  return (
    <aside className={`hidden md:flex fixed left-0 top-0 h-screen bg-[#181f26] border-r border-gray-800 transition-all duration-300 z-40 flex-col ${collapsed ? 'w-16' : 'w-64'}`}>
      {/* Logo Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
        {!collapsed && (
          <img src="/assets/xnuup-main-logo-white.png" alt="XNuuP" className="h-7" />
        )}
        {collapsed && (
          <img src="/assets/xnuup-main-symbol-gradient.png" alt="XNuuP" className="h-8 mx-auto" />
        )}
        <button 
          onClick={onToggleCollapse}
          className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </button>
      </div>

      {/* Navigation Groups */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {navGroups.map((group, idx) => (
          <div key={group.title} className={idx > 0 ? 'mt-6' : ''}>
            {!collapsed && (
              <div className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {group.title}
              </div>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => onPageChange(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group
                      ${activePage === item.id 
                        ? 'bg-[#3a7de4] text-white' 
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }
                      ${collapsed ? 'justify-center' : ''}
                    `}
                    title={collapsed ? item.label : undefined}
                  >
                    <span className={`flex-shrink-0 ${activePage === item.id ? 'text-white' : 'text-gray-500 group-hover:text-white'}`}>
                      {item.icon}
                    </span>
                    {!collapsed && (
                      <span className="font-medium text-sm">{item.label}</span>
                    )}
                    {!collapsed && activePage === item.id && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white"></span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* User Section */}
      <div className="p-3 border-t border-gray-800">
        <div className={`flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800 transition-colors ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#3a7de4] to-blue-400 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
            {(user?.first_name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.full_name || user?.first_name || 'User'}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email || ''}</div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={onLogout}
              className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-gray-700 rounded transition-colors"
              title={t('common.logout')}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={onLogout}
            className="w-full mt-2 p-2 text-gray-500 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors flex justify-center"
            title={t('common.logout')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
            </svg>
          </button>
        )}
      </div>
    </aside>
  );
};


// ============================================================================
// Header Component
// ============================================================================

const Header: React.FC<{
  pageTitle: string;
  insurers: Insurer[];
  selectedInsurer: number | null;
  onInsurerChange: (id: number | null) => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  sidebarCollapsed: boolean;
}> = ({ pageTitle, insurers, selectedInsurer, onInsurerChange, dateRange, onDateRangeChange, sidebarCollapsed }) => {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  
  const quickDateRanges = [
    { label: 'Today', days: 0 },
    { label: '7D', days: 7 },
    { label: '30D', days: 30 },
    { label: '90D', days: 90 },
  ];

  const setQuickRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    onDateRangeChange({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
  };

  return (
    <>
      {/* Desktop Header */}
      <header className={`fixed top-0 right-0 h-16 bg-white border-b border-gray-200 z-30 transition-all duration-300 hidden md:block ${sidebarCollapsed ? 'left-16' : 'left-64'}`}>
        <div className="h-full px-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-[#181f26]">{pageTitle}</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden lg:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {quickDateRanges.map((range) => (
                <button
                  key={range.label}
                  onClick={() => setQuickRange(range.days)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md transition-all hover:bg-white hover:shadow-sm text-gray-600 hover:text-[#3a7de4]"
                >
                  {range.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => onDateRangeChange({ ...dateRange, startDate: e.target.value })}
                className="bg-transparent text-sm text-gray-700 border-none focus:outline-none w-28"
              />
              <span className="text-gray-400">–</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => onDateRangeChange({ ...dateRange, endDate: e.target.value })}
                className="bg-transparent text-sm text-gray-700 border-none focus:outline-none w-28"
              />
            </div>
            <div className="relative">
              <select
                value={selectedInsurer || ''}
                onChange={(e) => onInsurerChange(e.target.value ? parseInt(e.target.value) : null)}
                className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3a7de4] focus:border-transparent cursor-pointer min-w-[160px]"
              >
                <option value="">{t('common.allInsurers')}</option>
                {insurers.map((ins) => (
                  <option key={ins.insurer_key} value={ins.insurer_key}>{ins.insurer_name}</option>
                ))}
              </select>
              <svg className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="p-2 text-gray-400 hover:text-[#3a7de4] hover:bg-gray-100 rounded-lg transition-colors"
              title={t('common.refresh')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-[#181f26] z-30 md:hidden">
        <div className="h-full px-4 flex items-center justify-between">
          <img src="/assets/xnuup-main-logo-white.png" alt="XNuuP" className="h-6" />
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors ${showFilters ? 'bg-[#3a7de4] text-white' : 'text-gray-400 hover:text-white'}`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
              </svg>
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="p-2 text-gray-400 hover:text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Filters Drawer */}
      {showFilters && (
        <div className="fixed top-14 left-0 right-0 bg-[#181f26] border-b border-gray-800 z-20 p-4 space-y-4 md:hidden">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('common.filters')}</div>
          
          <div className="flex gap-2 flex-wrap">
            {quickDateRanges.map((range) => (
              <button
                key={range.label}
                onClick={() => setQuickRange(range.days)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-300 hover:bg-[#3a7de4] hover:text-white transition-colors"
              >
                {range.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t('common.from')}</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => onDateRangeChange({ ...dateRange, startDate: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7de4]"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t('common.to')}</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => onDateRangeChange({ ...dateRange, endDate: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7de4]"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">{t('common.insurer')}</label>
            <select
              value={selectedInsurer || ''}
              onChange={(e) => onInsurerChange(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7de4]"
            >
              <option value="">{t('common.allInsurers')}</option>
              {insurers.map((ins) => (
                <option key={ins.insurer_key} value={ins.insurer_key}>{ins.insurer_name}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setShowFilters(false)}
            className="w-full py-2 bg-[#3a7de4] text-white text-sm font-medium rounded-lg"
          >
            {t('common.applyFilters')}
          </button>
        </div>
      )}
    </>
  );
};

// ============================================================================
// Mobile Bottom Navigation
// ============================================================================

const MobileNav: React.FC<{
  activePage: Page;
  onPageChange: (page: Page) => void;
  onLogout: () => void;
  hasPageAccess: (page: string) => boolean;
}> = ({ activePage, onPageChange, onLogout, hasPageAccess }) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);

  const allPrimaryItems: { id: Page; label: string; icon: React.ReactNode }[] = [
    { id: 'dashboard', label: t('common.home'), icon: <DashboardIcon /> },
    { id: 'supplier-profile', label: t('nav.supplierProfile'), icon: <DashboardIcon /> },
    { id: 'workshop-profile', label: t('nav.workshopProfile'), icon: <DashboardIcon /> },
    { id: 'insurer-profile', label: t('common.analytics'), icon: <DashboardIcon /> },
    { id: 'claims', label: t('nav.claims'), icon: <ClaimsIcon /> },
    { id: 'orders', label: t('nav.orders'), icon: <OrdersIcon /> },
    { id: 'suppliers', label: t('nav.suppliers'), icon: <SuppliersIcon /> },
    { id: 'operations', label: t('nav.operations'), icon: <OperationsIcon /> },
  ];
  const primaryItems = allPrimaryItems.filter(item => hasPageAccess(item.id)).slice(0, 5);

  const allMoreItems: { id: Page; label: string; icon: React.ReactNode }[] = [
    { id: 'parts', label: t('nav.parts'), icon: <PartsIcon /> },
    { id: 'workshops', label: t('nav.workshops'), icon: <WorkshopsIcon /> },
    { id: 'performance', label: t('nav.performance'), icon: <PerformanceIcon /> },
    { id: 'patterns', label: t('nav.patterns'), icon: <PatternsIcon /> },
    { id: 'reports', label: t('nav.reports'), icon: <ReportsIcon /> },
    { id: 'eda', label: t('nav.eda'), icon: <EDAIcon /> },
    { id: 'etl', label: t('nav.etl'), icon: <ETLIcon /> },
    { id: 'users', label: t('nav.users'), icon: <UsersIcon /> },
  ];
  const moreItems = allMoreItems.filter(item => hasPageAccess(item.id));

  return (
    <>
      {showMore && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setShowMore(false)}>
          <div 
            className="absolute bottom-16 left-0 right-0 bg-[#181f26] rounded-t-2xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-3 gap-4">
              {moreItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { onPageChange(item.id); setShowMore(false); }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-colors ${
                    activePage === item.id ? 'bg-[#3a7de4] text-white' : 'text-gray-400 hover:bg-gray-800'
                  }`}
                >
                  {item.icon}
                  <span className="text-xs">{item.label}</span>
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-800">
              <button
                onClick={onLogout}
                className="w-full flex items-center justify-center gap-2 py-3 text-red-400 hover:bg-gray-800 rounded-xl transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                </svg>
                <span className="text-sm font-medium">{t('common.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-[#181f26] border-t border-gray-800 z-30 md:hidden">
        <div className="h-full flex items-center justify-around px-2">
          {primaryItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onPageChange(item.id)}
              className={`flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
                activePage === item.id ? 'text-[#3a7de4]' : 'text-gray-500'
              }`}
            >
              {item.icon}
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          ))}
          <button
            onClick={() => setShowMore(!showMore)}
            className={`flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              showMore ? 'text-[#3a7de4]' : 'text-gray-500'
            }`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
            <span className="text-[10px] font-medium">{t('common.more')}</span>
          </button>
        </div>
      </nav>
    </>
  );
};

// ============================================================================
// Dashboard Page
// ============================================================================

const DashboardPage: React.FC<{ data: DashboardData | null; loading: boolean; dateRange: DateRange }> = ({ data, loading, dateRange }) => {
  const { user } = useAuth();

  // Admin-granted widget permissions for this user (from DB)
  const [dbWidgetPerms, setDbWidgetPerms] = useState<{ widget_key: string; is_visible: boolean }[]>([]);
  useEffect(() => {
    if (!user?.user_key) return;
    const fetchPerms = () => {
      authFetch(`/api/users/widget-permissions?user_key=${user.user_key}`)
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then(res => setDbWidgetPerms(res?.data?.permissions ?? []))
        .catch(() => {}); // fail open — widget gate uses defaults if unavailable
    };
    fetchPerms();
    // Re-fetch if an admin changes this user's widget permissions in another session
    const handler = () => fetchPerms();
    window.addEventListener('widget-permissions-changed', handler);
    return () => window.removeEventListener('widget-permissions-changed', handler);
  }, [user?.user_key]);

  // Widgets the user is permitted to see: page access gate AND admin widget access gate
  const accessibleWidgets = useMemo(() => {
    const revokedSet = new Set(dbWidgetPerms.filter(p => !p.is_visible).map(p => p.widget_key));
    return UNIVERSAL_WIDGETS.filter(w =>
      user?.permissions?.[w.source]?.view === true && !revokedSet.has(WIDGET_KEY_MAP[w.id] ?? w.id)
    );
  }, [user, dbWidgetPerms]);

  // Widget visibility with granular control and ordering
  const {
    state, toggle, moveUp, moveDown, setAll, reset, loadState,
    isVisible, getOrderedWidgets, getOrderedWidgetsForPanel, visibleCount
  } = useWidgetVisibility('dashboard', accessibleWidgets);

  // Visible count scoped to only accessible widgets (for panel badge)
  const accessibleVisibleCount = accessibleWidgets.filter(
    w => state[w.id]?.visible ?? w.defaultVisible
  ).length;

  // Cross-page data state
  const [opsData, setOpsData] = useState<any>(null);
  const [perfData, setPerfData] = useState<any>(null);
  const [shData, setShData] = useState<any>(null);
  const [patData, setPatData] = useState<any>(null);
  const [crossPageLoading, setCrossPageLoading] = useState(false);

  // Determine which cross-page sources have visible widgets
  const visibleSources = useMemo(() => {
    const sources: string[] = [];
    accessibleWidgets.forEach(widget => {
      const widgetState = state[widget.id];
      const isVisible = widgetState?.visible ?? widget.defaultVisible;
      if (isVisible && widget.source !== 'dashboard' && !sources.includes(widget.source)) {
        sources.push(widget.source);
      }
    });
    return sources.sort();
  }, [accessibleWidgets, state]);

  // Fetch cross-page data when needed
  useEffect(() => {
    if (visibleSources.length === 0) {
      return;
    }

    const fetchCrossPageData = async () => {
      setCrossPageLoading(true);
      const params = new URLSearchParams();
      if (dateRange.startDate) params.append('startDate', dateRange.startDate);
      if (dateRange.endDate) params.append('endDate', dateRange.endDate);

      try {
        const promises: Promise<void>[] = [];

        if (visibleSources.includes('operations')) {
          promises.push(
            authFetch(`/api/analytics/operations?${params}`)
              .then(r => r.json())
              .then(d => { if (d.success) setOpsData(d.data); })
              .catch(e => console.error('[Dashboard] Operations error:', e))
          );
        }
        if (visibleSources.includes('performance')) {
          promises.push(
            authFetch(`/api/analytics/performance?${params}`)
              .then(r => r.json())
              .then(d => { if (d.success) setPerfData(d.data); })
              .catch(e => console.error('[Dashboard] Performance error:', e))
          );
        }
        if (visibleSources.includes('shareholders')) {
          promises.push(
            authFetch(`/api/shareholders?${params}`)
              .then(r => r.json())
              .then(d => { if (d.success) setShData(d.data); })
              .catch(e => console.error('[Dashboard] Shareholders error:', e))
          );
        }
        if (visibleSources.includes('patterns')) {
          promises.push(
            authFetch(`/api/patterns/summary?${params}`)
              .then(r => r.json())
              .then(d => { if (d.success) setPatData(d.data); })
              .catch(e => console.error('[Dashboard] Patterns error:', e))
          );
        }

        await Promise.all(promises);
      } catch (e) {
        console.error('Cross-page data fetch error:', e);
      } finally {
        setCrossPageLoading(false);
      }
    };

    fetchCrossPageData();
  }, [visibleSources.join(','), dateRange.startDate, dateRange.endDate]);

  // Sorting hooks for each table
  const { sortedItems: sortedSuppliers, requestSort: sortSuppliers, getSortIcon: supplierIcon } = useSortableData(
    data?.supplier_performance,
    { key: 'total_parts', direction: 'desc' }
  );
  const { sortedItems: sortedInsurers, requestSort: sortInsurers, getSortIcon: insurerIcon } = useSortableData(
    data?.insurer_performance,
    { key: 'total_claims', direction: 'desc' }
  );
  const { sortedItems: sortedVehicles, requestSort: sortVehicles, getSortIcon: vehicleIcon } = useSortableData(
    data?.vehicle_stats,
    { key: 'total_claims', direction: 'desc' }
  );
  const { sortedItems: sortedStatus, requestSort: sortStatus, getSortIcon: statusIcon } = useSortableData(
    data?.status_breakdown,
    { key: 'part_count', direction: 'desc' }
  );

  if (loading || !data) return <Loading />;

  const { 
    claims_summary = { total_claims: 0, total_parts: 0, total_value: 0, avg_part_price: 0, avg_parts_per_claim: 0, avg_suppliers_per_claim: 0 }, 
    parts_distribution = [], 
    status_breakdown = [], 
    claim_fulfillment = null,
    supplier_performance = [], 
    vehicle_stats = [], 
    insurer_performance = [], 
    suppliers_per_claim = null,
    network_counts = { total_suppliers: 0, total_workshops: 0, total_insurers: 0 },
    automation_metrics = { total_parts: 0, auto_quoted_parts: 0, manual_quoted_parts: 0, auto_quote_pct: 0, manual_quote_pct: 0, auto_assigned_parts: 0, manual_assigned_parts: 0, auto_assign_pct: 0, manual_assign_pct: 0 },
    state_performance = [] 
  } = data || {};

  // KPI definitions with their render data
  const kpiDefinitions: Record<string, { label: string; value: string; color?: string; info: string }> = {
    'kpi_total_claims': { label: 'Total Claims', value: formatNumber(claims_summary?.total_claims), color: 'text-blue-600', info: 'Number of unique insurance claims processed in the selected period.' },
    'kpi_total_parts': { label: 'Total Parts', value: formatNumber(claims_summary?.total_parts), info: 'Total number of individual part orders across all claims.' },
    'kpi_total_value': { label: 'Total Value', value: formatCurrency(claims_summary?.total_value), color: 'text-green-600', info: 'Sum of current prices for all part orders.' },
    'kpi_fulfillment_rate': { label: 'Fulfillment Rate', value: formatPercent(claim_fulfillment?.fulfillment_rate), color: (claim_fulfillment?.fulfillment_rate ?? 0) >= 50 ? 'text-green-600' : 'text-yellow-600', info: 'Percentage of claims where ALL parts were delivered.' },
    'kpi_total_suppliers': { label: 'Total Suppliers', value: formatNumber(network_counts?.total_suppliers), color: 'text-purple-600', info: 'Number of unique suppliers with orders.' },
    'kpi_total_workshops': { label: 'Total Workshops', value: formatNumber(network_counts?.total_workshops), color: 'text-orange-600', info: 'Number of unique body shops.' },
    'kpi_auto_quote_pct': { label: 'Auto Quote %', value: `${(automation_metrics?.auto_quote_pct || 0).toFixed(1)}%`, color: 'text-teal-600', info: 'Percentage of automatically quoted parts.' },
    'kpi_avg_parts_claim': { label: 'Avg Parts/Claim', value: Number(claims_summary?.avg_parts_per_claim || 0).toFixed(2), info: 'Average number of parts ordered per claim.' },
    'kpi_avg_suppliers_claim': { label: 'Avg Suppliers/Claim', value: Number(claims_summary?.avg_suppliers_per_claim || 0).toFixed(2), info: 'Average suppliers used per claim.' },
    'kpi_avg_part_price': { label: 'Avg Part Price', value: formatCurrency(claims_summary?.avg_part_price), info: 'Mean price per part order.' },
    'kpi_fulfilled_claims': { label: 'Fulfilled Claims', value: formatNumber(claim_fulfillment?.fulfilled_claims), color: 'text-green-600', info: 'Claims where 100% of parts were delivered.' },
    'kpi_partial_claims': { label: 'Partial Claims', value: formatNumber(claim_fulfillment?.partial_claims), color: 'text-yellow-600', info: 'Claims with partial delivery.' },
    'kpi_manual_quote_pct': { label: 'Manual Quote %', value: `${(automation_metrics?.manual_quote_pct || 0).toFixed(1)}%`, color: 'text-gray-600', info: 'Percentage of manually quoted parts.' },
    'kpi_auto_assign_pct': { label: 'Auto Assign %', value: `${(automation_metrics?.auto_assign_pct || 0).toFixed(1)}%`, color: 'text-indigo-600', info: 'Percentage of auto-assigned parts.' },
    // Operations KPIs
    'ops_kpi_parts_delivered': { label: 'Parts Delivered', value: formatNumber(opsData?.totals?.parts_delivered), color: 'text-green-600', info: 'Parts successfully delivered' },
    'ops_kpi_parts_in_transit': { label: 'Parts In Transit', value: formatNumber(opsData?.totals?.parts_in_transit), color: 'text-blue-600', info: 'Parts currently in transit' },
    'ops_kpi_parts_assigned': { label: 'Parts Assigned', value: formatNumber(opsData?.totals?.parts_assigned), color: 'text-amber-600', info: 'Parts awaiting pickup' },
    'ops_kpi_parts_cancelled': { label: 'Parts Cancelled', value: formatNumber(opsData?.totals?.parts_cancelled), color: 'text-red-600', info: 'Cancelled part orders' },
    'ops_kpi_active_suppliers': { label: 'Active Suppliers', value: formatNumber(opsData?.totals?.active_suppliers), color: 'text-purple-600', info: 'Suppliers with active orders' },
    'ops_kpi_delivery_rate': { label: 'Delivery Rate', value: formatPercent(opsData?.delivery?.supplier_delivery_rate), color: 'text-green-600', info: 'Overall delivery success rate' },
    'ops_kpi_on_time_rate': { label: 'On-Time Rate', value: formatPercent(opsData?.delivery?.on_time_rate), color: 'text-blue-600', info: 'Deliveries within 3 days' },
    'ops_kpi_on_time_orders': { label: 'On-Time Orders', value: formatNumber(opsData?.delivery?.on_time_orders), color: 'text-green-600', info: 'Orders delivered on time' },
    'ops_kpi_late_orders': { label: 'Late Orders', value: formatNumber(opsData?.delivery?.late_orders), color: 'text-red-600', info: 'Orders delivered late' },
    'ops_kpi_avg_delivery_days': { label: 'Avg Delivery Days', value: formatDays(opsData?.averages?.delivery_days), color: 'text-orange-600', info: 'Mean delivery time' },
    'ops_kpi_median_delivery': { label: 'Median Delivery', value: formatDays(opsData?.delivery_times?.median_delivery_days), color: 'text-green-600', info: '50th percentile delivery' },
    'ops_kpi_p90_delivery': { label: 'P90 Delivery', value: formatDays(opsData?.delivery_times?.p90_delivery_days), color: 'text-orange-600', info: '90% delivered within' },
    'ops_kpi_manual_orders': { label: 'Manual Orders', value: formatNumber(opsData?.automation?.manual?.total), color: 'text-gray-600', info: 'Manually processed orders' },
    'ops_kpi_automated_orders': { label: 'Automated Orders', value: formatNumber(opsData?.automation?.automated?.total), color: 'text-blue-600', info: 'Auto-processed orders' },
    'ops_kpi_automation_effectiveness': { label: 'Auto Effectiveness', value: `${opsData?.automation?.effectiveness >= 0 ? '+' : ''}${opsData?.automation?.effectiveness || 0}%`, color: opsData?.automation?.effectiveness >= 0 ? 'text-green-600' : 'text-red-600', info: 'Auto vs manual improvement' },
    'ops_kpi_price_anomalies': { label: 'Price Anomalies', value: formatNumber(opsData?.risk?.price_anomalies), color: 'text-yellow-600', info: 'Unusual price detections' },
    'ops_kpi_high_risk_suppliers': { label: 'High Risk Suppliers', value: formatNumber(opsData?.risk?.high_risk_suppliers), color: 'text-red-600', info: 'Suppliers needing review' },
    'ops_kpi_complex_claims': { label: 'Complex Claims', value: formatNumber(opsData?.complex_claims?.total), color: 'text-purple-600', info: 'Claims with 5+ parts' },
    'ops_kpi_above_market': { label: 'Above Market', value: formatNumber(opsData?.market_position?.above_market), color: 'text-red-600', info: 'Priced above average' },
    'ops_kpi_below_market': { label: 'Below Market', value: formatNumber(opsData?.market_position?.below_market), color: 'text-green-600', info: 'Priced at/below average' },
    // Performance KPIs
    'perf_kpi_avg_delivery_days': { label: 'Avg Delivery (Perf)', value: formatDays(perfData?.kpis?.avg_delivery_days), color: 'text-blue-600', info: 'Mean delivery time' },
    'perf_kpi_on_time_rate': { label: 'On-Time Rate (Perf)', value: formatPercent(perfData?.kpis?.on_time_rate), color: 'text-green-600', info: 'Deadline compliance rate' },
    'perf_kpi_auto_assign_rate': { label: 'Auto-Assign (Perf)', value: formatPercent(perfData?.kpis?.auto_assign_rate), color: 'text-purple-600', info: 'Auto supplier assignment' },
    'perf_kpi_auto_quote_rate': { label: 'Auto-Quote (Perf)', value: formatPercent(perfData?.kpis?.auto_quote_rate), color: 'text-purple-600', info: 'Auto price generation' },
    'perf_kpi_on_time_orders': { label: 'On-Time (Perf)', value: formatNumber(perfData?.kpis?.on_time_orders), color: 'text-green-600', info: 'On-time deliveries' },
    'perf_kpi_late_orders': { label: 'Late (Perf)', value: formatNumber(perfData?.kpis?.late_orders), color: 'text-red-600', info: 'Late deliveries' },
    'perf_kpi_single_supplier': { label: 'Single Supplier', value: formatNumber(perfData?.suppliers_per_claim?.single_supplier_claims), color: 'text-green-600', info: 'Claims using 1 supplier' },
    'perf_kpi_two_suppliers': { label: 'Two Suppliers', value: formatNumber(perfData?.suppliers_per_claim?.two_supplier_claims), color: 'text-yellow-600', info: 'Claims using 2 suppliers' },
    'perf_kpi_multi_suppliers': { label: '3+ Suppliers', value: formatNumber(perfData?.suppliers_per_claim?.multi_supplier_claims), color: 'text-orange-600', info: 'Claims using 3+ suppliers' },
    // Shareholders KPIs
    'sh_kpi_total_order_value': { label: 'Total Order Value', value: formatCurrency(shData?.kpis?.total_order_value), color: 'text-blue-600', info: 'All orders pipeline value' },
    'sh_kpi_invoiced_revenue': { label: 'Invoiced Revenue', value: formatCurrency(shData?.kpis?.invoiced_value), color: 'text-green-600', info: 'Realized revenue' },
    'sh_kpi_pipeline_gap': { label: 'Pipeline Gap', value: formatCurrency(shData?.kpis?.pipeline_gap), color: 'text-amber-600', info: 'Unrealized value' },
    'sh_kpi_realization_rate': { label: 'Realization Rate', value: formatPercent(shData?.kpis?.revenue_realization_rate), color: 'text-purple-600', info: 'Revenue conversion %' },
    'sh_kpi_pending_value': { label: 'Pending Value', value: formatCurrency(shData?.kpis?.pending_value), color: 'text-yellow-600', info: 'In-progress orders value' },
    'sh_kpi_cancelled_value': { label: 'Cancelled Value', value: formatCurrency(shData?.kpis?.cancelled_value), color: 'text-red-600', info: 'Lost order value' },
    'sh_kpi_avg_order_value': { label: 'Avg Order Value', value: formatCurrency(shData?.kpis?.avg_order_value), color: 'text-gray-600', info: 'Mean order size' },
    // Patterns KPIs
    'pat_kpi_price_anomalies': { label: 'Price Anomalies', value: formatNumber(patData?.price_anomalies), color: 'text-red-600', info: 'Parts with unusual pricing (Z>2)' },
    'pat_kpi_high_risk_suppliers': { label: 'High Risk Suppliers', value: formatNumber(patData?.high_risk_suppliers), color: 'text-orange-600', info: 'Suppliers with >15% cancellation' },
    'pat_kpi_complex_claims': { label: 'Complex Claims', value: formatNumber(patData?.complex_claims), color: 'text-purple-600', info: 'Claims with >8 parts AND >4 suppliers' },
    'pat_kpi_unique_vehicles': { label: 'Unique Vehicles', value: formatNumber(patData?.vehicle_stats?.unique_vehicles), color: 'text-blue-600', info: 'Distinct vehicles in data' },
    'pat_kpi_unique_makes': { label: 'Unique Makes', value: formatNumber(patData?.vehicle_stats?.unique_makes), color: 'text-orange-600', info: 'Distinct vehicle makes' },
    'pat_kpi_unique_models': { label: 'Unique Models', value: formatNumber(patData?.vehicle_stats?.unique_models), color: 'text-teal-600', info: 'Distinct vehicle models' },
  };

  // Get ordered visible widgets - include cross-page KPIs
  const visibleKpis = getOrderedWidgets('kpi').filter(w => {
    const widget = UNIVERSAL_WIDGETS.find(u => u.id === w.id);
    // Include dashboard KPIs or cross-page KPIs that have data loaded
    if (!widget) return false;
    if (w.id === 'kpi_suppliers_per_claim') return false;
    if (widget.source === 'dashboard') return true;
    if (widget.source === 'operations' && opsData) return true;
    if (widget.source === 'performance' && perfData) return true;
    if (widget.source === 'shareholders' && shData) return !!shData?.kpis;
    if (widget.source === 'patterns' && patData) return true;
    return false;
  });

  const visibleCharts = getOrderedWidgets('chart').filter(w => {
    const widget = UNIVERSAL_WIDGETS.find(u => u.id === w.id);
    if (!widget) return false;
    if (widget.source === 'dashboard') return true;
    if (widget.source === 'operations' && opsData) return true;
    if (widget.source === 'performance' && perfData) return true;
    if (widget.source === 'shareholders' && shData) return true;
    if (widget.source === 'patterns' && patData) return true;
    return false;
  });
  
  const visibleTables = getOrderedWidgets('table').filter(w => {
    const widget = UNIVERSAL_WIDGETS.find(u => u.id === w.id);
    if (!widget) return false;
    if (widget.source === 'dashboard') return true;
    if (widget.source === 'operations' && opsData) return true;
    if (widget.source === 'performance' && perfData) return true;
    if (widget.source === 'shareholders' && shData) return true;
    if (widget.source === 'patterns' && patData) return true;
    return false;
  });

  return (
    <div className="space-y-6">
      {/* Page Header with Widget Toggle */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-800">Dashboard</h1>
          {crossPageLoading && (
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded animate-pulse">Loading widgets...</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {user && (
            <DashboardWidgetManager
              widgets={accessibleWidgets}
              currentState={state}
              onSave={loadState}
              userKey={user.user_key}
              authFetch={authFetch}
            />
          )}
          <WidgetTogglePanel
            widgets={accessibleWidgets}
            state={state}
            onToggle={toggle}
            onMoveUp={moveUp}
            onMoveDown={moveDown}
            onShowAll={() => setAll(true)}
            onHideAll={() => setAll(false)}
            onReset={reset}
            visibleCount={accessibleVisibleCount}
            getOrderedWidgetsForPanel={getOrderedWidgetsForPanel}
          />
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          KPIs Grid - Dynamically rendered based on visibility and order
      ═══════════════════════════════════════════════════════════════════ */}
      {visibleKpis.length > 0 && (
        <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {visibleKpis.map(w => {
            const def = kpiDefinitions[w.id];
            if (!def) return null;
            return (
              <StatCard 
                key={w.id}
                label={def.label} 
                value={def.value} 
                color={def.color} 
                info={def.info} 
              />
            );
          })}
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          Charts Section - Dynamically rendered based on visibility and order
      ═══════════════════════════════════════════════════════════════════ */}
      {visibleCharts.length > 0 && (
        <section className={`grid grid-cols-1 ${visibleCharts.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} gap-6`}>
          {visibleCharts.map(w => {
            if (w.id === 'chart_claims_status') {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Distribution of claims by fulfillment status.">Claims Status Breakdown</SectionTitle>
                  {claim_fulfillment ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie 
                            data={[
                              { name: 'Complete', value: claim_fulfillment.fulfilled_claims, color: '#22c55e' },
                              { name: 'Partial', value: claim_fulfillment.partial_claims, color: '#eab308' },
                              { name: 'Pending', value: claim_fulfillment.unfulfilled_claims, color: '#ef4444' }
                            ].filter(d => d.value > 0)} 
                            dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={false}
                          >
                            {[
                              { name: 'Complete', value: claim_fulfillment.fulfilled_claims, color: '#22c55e' },
                              { name: 'Partial', value: claim_fulfillment.partial_claims, color: '#eab308' },
                              { name: 'Pending', value: claim_fulfillment.unfulfilled_claims, color: '#ef4444' }
                            ].filter(d => d.value > 0).map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Pie>
                          <Tooltip formatter={(v: number) => formatNumber(v)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <p className="text-gray-500 text-center py-8">No data</p>}
                </div>
              );
            }
            if (w.id === 'chart_part_status') {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Distribution of part orders by status.">Part Status Breakdown</SectionTitle>
                  {status_breakdown.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={status_breakdown.slice(0, 6)} dataKey="part_count" nameKey="status_name_es" cx="50%" cy="50%" outerRadius={80} labelLine={false}>
                            {status_breakdown.slice(0, 6).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v: number) => formatNumber(v)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <p className="text-gray-500 text-center py-8">No data</p>}
                </div>
              );
            }
            if (w.id === 'chart_parts_per_claim') {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Distribution of claims by number of parts.">Parts per Claim</SectionTitle>
                  {parts_distribution.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={parts_distribution}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="parts_bucket" />
                          <YAxis />
                          <Tooltip formatter={(v: number) => formatNumber(v)} />
                          <Bar dataKey="claim_count" fill="#3b82f6" name="Claims" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <p className="text-gray-500 text-center py-8">No data</p>}
                </div>
              );
            }
            // Operations Charts
            if (w.id === 'ops_chart_parts_status' && opsData?.totals) {
              const partsData = [
                { name: 'Assigned', value: opsData.totals.parts_assigned || 0, color: '#f59e0b' },
                { name: 'In Transit', value: opsData.totals.parts_in_transit || 0, color: '#3b82f6' },
                { name: 'Delivered', value: opsData.totals.parts_delivered || 0, color: '#22c55e' },
                { name: 'Cancelled', value: opsData.totals.parts_cancelled || 0, color: '#ef4444' },
              ].filter(d => d.value > 0);
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Distribution of parts by status.">Parts Status (Operations)</SectionTitle>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={partsData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                          {partsData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => formatNumber(v)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            }
            if (w.id === 'ops_chart_automation' && opsData?.automation) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Manual vs automated order processing comparison.">Automation (Operations)</SectionTitle>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="border rounded-lg p-3">
                      <div className="text-sm font-medium text-gray-600 mb-2">Manual</div>
                      <div className="text-2xl font-bold">{formatNumber(opsData.automation.manual?.total)}</div>
                      <div className="text-xs text-gray-500">{opsData.automation.manual?.pct_of_total}% of total</div>
                      <div className="text-xs text-orange-600 mt-1">Delivery: {opsData.automation.manual?.delivery_pct}%</div>
                    </div>
                    <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
                      <div className="text-sm font-medium text-blue-700 mb-2">Automated</div>
                      <div className="text-2xl font-bold text-blue-600">{formatNumber(opsData.automation.automated?.total)}</div>
                      <div className="text-xs text-gray-500">{opsData.automation.automated?.pct_of_total}% of total</div>
                      <div className="text-xs text-green-600 mt-1">Delivery: {opsData.automation.automated?.delivery_pct}%</div>
                    </div>
                  </div>
                  <div className="mt-3 text-center p-2 bg-green-50 rounded">
                    <span className="text-sm text-gray-600">Effectiveness: </span>
                    <span className={`font-bold ${opsData.automation.effectiveness >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {opsData.automation.effectiveness >= 0 ? '+' : ''}{opsData.automation.effectiveness}%
                    </span>
                  </div>
                </div>
              );
            }
            if (w.id === 'ops_chart_delivery_metrics' && opsData?.delivery) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4 lg:col-span-2">
                  <SectionTitle info="Key delivery performance metrics.">Delivery Metrics (Operations)</SectionTitle>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-3">
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-green-600">{formatNumber(opsData.delivery.on_time_orders)}</div>
                      <div className="text-xs text-gray-500">On-Time</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-red-600">{formatNumber(opsData.delivery.late_orders)}</div>
                      <div className="text-xs text-gray-500">Late</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-blue-600">{formatPercent(opsData.delivery.on_time_rate)}</div>
                      <div className="text-xs text-gray-500">On-Time Rate</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-purple-600">{formatPercent(opsData.delivery.auto_assign_rate)}</div>
                      <div className="text-xs text-gray-500">Auto-Assign</div>
                    </div>
                    <div className="bg-cyan-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-cyan-600">{formatNumber(opsData.delivery.delivered_orders)}</div>
                      <div className="text-xs text-gray-500">Delivered</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-orange-600">{formatPercent(opsData.delivery.supplier_delivery_rate)}</div>
                      <div className="text-xs text-gray-500">Delivery Rate</div>
                    </div>
                  </div>
                </div>
              );
            }
            if (w.id === 'ops_chart_weekly_trend' && opsData?.trend) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4 lg:col-span-2">
                  <SectionTitle info="Weekly trends for orders and delivery performance.">Weekly Trend (Operations)</SectionTitle>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={opsData.trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="week_start" tickFormatter={(v) => v?.slice(5, 10)} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip />
                        <Bar yAxisId="left" dataKey="orders" fill="#3b82f6" name="Orders" />
                        <Line yAxisId="right" type="monotone" dataKey="delivery_pct" stroke="#22c55e" strokeWidth={2} name="Delivery %" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            }
            // Shareholders Charts
            if (w.id === 'sh_chart_growth_trend' && shData?.daily_trend) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4 lg:col-span-2">
                  <SectionTitle info="Cumulative order value growth over time.">Order Value Growth</SectionTitle>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={shData.daily_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="order_date" tickFormatter={(v) => v?.slice(5, 10)} />
                        <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v: number) => formatCurrency(v)} />
                        <Area type="monotone" dataKey="cumulative_value" stroke="#3b82f6" fill="#93c5fd" name="Cumulative Value" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            }
            if (w.id === 'sh_chart_orders_vs_invoiced' && shData?.monthly_trend) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Comparison of total orders vs invoiced revenue.">Orders vs Invoiced</SectionTitle>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={shData.monthly_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v: number) => formatCurrency(v)} />
                        <Bar dataKey="order_value" fill="#3b82f6" name="Orders" />
                        <Bar dataKey="invoiced_value" fill="#22c55e" name="Invoiced" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            }
            if (w.id === 'sh_chart_pipeline_status' && shData?.pipeline_by_status) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Pipeline value by order status.">Pipeline by Status</SectionTitle>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={shData.pipeline_by_status} dataKey="total_value" nameKey="status_name" cx="50%" cy="50%" outerRadius={80}>
                          {shData.pipeline_by_status.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => formatCurrency(v)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            }
            if (w.id === 'sh_chart_monthly_growth' && shData?.monthly_trend) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Month-over-month growth rates.">MoM Growth</SectionTitle>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={shData.monthly_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis yAxisId="left" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} />
                        <Tooltip />
                        <Bar yAxisId="left" dataKey="order_value" fill="#3b82f6" name="Value" />
                        <Line yAxisId="right" type="monotone" dataKey="mom_growth" stroke="#f59e0b" strokeWidth={2} name="MoM %" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            }
            if (w.id === 'sh_chart_weekly_perf' && shData?.weekly_comparison) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Last 8 weeks performance comparison.">Weekly Performance</SectionTitle>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={shData.weekly_comparison}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="week_start" tickFormatter={(v) => v?.slice(5, 10)} />
                        <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v: number) => formatCurrency(v)} />
                        <Bar dataKey="total_value" fill="#8b5cf6" name="Value" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            }
            // Performance Charts
            if (w.id === 'perf_chart_delivery_distribution' && perfData?.delivery_distribution) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Distribution of orders by delivery time.">Delivery Time Distribution</SectionTitle>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={perfData.delivery_distribution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="delivery_bucket" />
                        <YAxis />
                        <Tooltip formatter={(v: number) => formatNumber(v)} />
                        <Bar dataKey="order_count" fill="#3b82f6" name="Orders" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            }
            // Patterns Charts - summary metrics as visual cards
            if (w.id === 'pat_chart_trends' && patData) {
              return (
                <div key={w.id} className="bg-white rounded-lg shadow p-4">
                  <SectionTitle info="Key pattern metrics overview.">Pattern Insights</SectionTitle>
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-3xl font-bold text-red-600">{formatNumber(patData.price_anomalies)}</div>
                      <div className="text-sm text-gray-600">Price Anomalies</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-3xl font-bold text-orange-600">{formatNumber(patData.high_risk_suppliers)}</div>
                      <div className="text-sm text-gray-600">High Risk Suppliers</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-3xl font-bold text-purple-600">{formatNumber(patData.complex_claims)}</div>
                      <div className="text-sm text-gray-600">Complex Claims</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="text-center p-3 bg-blue-50 rounded">
                      <div className="text-2xl font-bold text-blue-600">{patData.auto_assign_rate || 0}%</div>
                      <div className="text-xs text-gray-600">Auto-Assign Rate</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded">
                      <div className="text-2xl font-bold text-green-600">{patData.auto_quote_rate || 0}%</div>
                      <div className="text-xs text-gray-600">Auto-Quote Rate</div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })}
        </section>
      )}

      {/* Suppliers Per Claim */}
      {isVisible('kpi_suppliers_per_claim') && suppliers_per_claim && (
        <div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Statistics on suppliers used per claim.">Suppliers Per Claim</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mt-3">
            <div className="text-center p-3 bg-blue-50 rounded">
              <div className="text-2xl font-bold text-blue-600">{Number(suppliers_per_claim.avg_suppliers_per_claim || 0).toFixed(2)}</div>
              <div className="text-xs text-gray-600">Avg Suppliers</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded">
              <div className="text-xl font-bold">{suppliers_per_claim.min_suppliers ?? 0} - {suppliers_per_claim.max_suppliers ?? 0}</div>
              <div className="text-xs text-gray-600">Min - Max</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded">
              <div className="text-xl font-bold text-green-600">{formatNumber(suppliers_per_claim.single_supplier_claims)}</div>
              <div className="text-xs text-gray-600">Single Supplier</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded">
              <div className="text-xl font-bold text-yellow-600">{formatNumber(suppliers_per_claim.two_supplier_claims)}</div>
              <div className="text-xs text-gray-600">Two Suppliers</div>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded">
              <div className="text-xl font-bold text-orange-600">{formatNumber(suppliers_per_claim.multi_supplier_claims)}</div>
              <div className="text-xs text-gray-600">3+ Suppliers</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded col-span-2">
              <div className="text-xl font-bold text-purple-600">{formatNumber(suppliers_per_claim.total_claims)}</div>
              <div className="text-xs text-gray-600">Total Claims</div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          Tables Section - Dynamically rendered based on visibility and order
      ═══════════════════════════════════════════════════════════════════ */}
      {visibleTables.map(w => {
        if (w.id === 'table_performance_state' && state_performance && state_performance.length > 0) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Performance by State</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">State</th>
                      <th className="px-3 py-2 text-right">Auto Assign</th>
                      <th className="px-3 py-2 text-right">Manual Assign</th>
                      <th className="px-3 py-2 text-right">Total Parts</th>
                      <th className="px-3 py-2 text-right">Auto %</th>
                      <th className="px-3 py-2 text-right">Generic</th>
                      <th className="px-3 py-2 text-right">Generic %</th>
                      <th className="px-3 py-2 text-right">Original</th>
                      <th className="px-3 py-2 text-right">Original %</th>
                      <th className="px-3 py-2 text-right">Other</th>
                      <th className="px-3 py-2 text-right">Other %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {state_performance.map((s, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{s.state}</td>
                        <td className="px-3 py-2 text-right text-blue-600">{formatNumber(s.auto_assigned_parts)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{formatNumber(s.manual_assigned_parts)}</td>
                        <td className="px-3 py-2 text-right font-medium">{formatNumber(s.total_parts)}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={s.auto_assign_pct >= 50 ? 'text-green-600' : 'text-orange-600'}>
                            {Number(s.auto_assign_pct || 0)}%
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">{formatNumber(s.generic_parts)}</td>
                        <td className="px-3 py-2 text-right text-purple-600">{Number(s.generic_pct || 0)}%</td>
                        <td className="px-3 py-2 text-right">{formatNumber(s.original_parts)}</td>
                        <td className="px-3 py-2 text-right text-indigo-600">{Number(s.original_pct || 0)}%</td>
                        <td className="px-3 py-2 text-right">{formatNumber(s.other_parts)}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{s.other_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (w.id === 'table_top_suppliers') {
          return (
            <section key={w.id}>
              <div className="bg-white rounded-lg shadow p-4">
                <SectionTitle info="Top suppliers by order volume.">Top Suppliers</SectionTitle>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead><tr className="border-b">
                      <SortTh label="Supplier" sortKey="supplier_name" onSort={sortSuppliers} sortIcon={supplierIcon} className="text-left py-2 px-2" />
                      <SortTh label="Parts" sortKey="total_parts" onSort={sortSuppliers} sortIcon={supplierIcon} className="text-right py-2 px-2" />
                      <SortTh label="Auth Parts" sortKey="authorized_parts" onSort={sortSuppliers} sortIcon={supplierIcon} className="text-right py-2 px-2" />
                      <SortTh label="Auth Amount" sortKey="authorized_amount" onSort={sortSuppliers} sortIcon={supplierIcon} className="text-right py-2 px-2" />
                      <SortTh label="Avg Cost" sortKey="avg_cost" onSort={sortSuppliers} sortIcon={supplierIcon} className="text-right py-2 px-2" />
                      <SortTh label="Delivered" sortKey="delivered_parts" onSort={sortSuppliers} sortIcon={supplierIcon} className="text-right py-2 px-2" />
                      <SortTh label="Rate" sortKey="delivery_rate" onSort={sortSuppliers} sortIcon={supplierIcon} className="text-right py-2 px-2" />
                      <SortTh label="Avg Days" sortKey="avg_delivery_days" onSort={sortSuppliers} sortIcon={supplierIcon} className="text-right py-2 px-2" />
                    </tr></thead>
                    <tbody>
                      {(sortedSuppliers || []).map((s, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-2">{truncate(s.supplier_name, 20)}</td>
                          <td className="text-right py-2 px-2">{formatNumber(s.total_parts)}</td>
                          <td className="text-right py-2 px-2 text-blue-600">{formatNumber(s.authorized_parts)}</td>
                          <td className="text-right py-2 px-2 text-green-600">{formatCurrency(s.authorized_amount)}</td>
                          <td className="text-right py-2 px-2">{formatCurrency(s.avg_cost)}</td>
                          <td className="text-right py-2 px-2 text-emerald-600">{formatNumber(s.delivered_parts)}</td>
                          <td className="text-right py-2 px-2">{formatPercent(s.delivery_rate)}</td>
                          <td className="text-right py-2 px-2">{s.avg_delivery_days != null ? Number(s.avg_delivery_days).toFixed(1) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          );
        }
        if (w.id === 'table_vehicle_stats') {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Most frequently repaired vehicles.">Vehicle Statistics</SectionTitle>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead><tr className="border-b">
                    <SortTh label="Brand" sortKey="marca" onSort={sortVehicles} sortIcon={vehicleIcon} className="text-left py-2 px-2" />
                    <SortTh label="Model" sortKey="modelo" onSort={sortVehicles} sortIcon={vehicleIcon} className="text-left py-2 px-2" />
                    <SortTh label="Year" sortKey="year" onSort={sortVehicles} sortIcon={vehicleIcon} className="text-center py-2 px-2" />
                    <SortTh label="Claims" sortKey="total_claims" onSort={sortVehicles} sortIcon={vehicleIcon} className="text-right py-2 px-2" />
                    <SortTh label="Parts" sortKey="total_parts" onSort={sortVehicles} sortIcon={vehicleIcon} className="text-right py-2 px-2" />
                    <SortTh label="Delivery %" sortKey="delivery_rate" onSort={sortVehicles} sortIcon={vehicleIcon} className="text-right py-2 px-2" />
                  </tr></thead>
                  <tbody>
                    {(sortedVehicles || []).map((v, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2">{v.marca || '-'}</td>
                        <td className="py-2 px-2">{v.modelo || '-'}</td>
                        <td className="text-center py-2 px-2">{v.year || '-'}</td>
                        <td className="text-right py-2 px-2">{formatNumber(v.total_claims)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(v.total_parts)}</td>
                        <td className="text-right py-2 px-2">{formatPercent(v.delivery_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        // Operations Tables
        if (w.id === 'ops_table_supplier_ranking' && opsData?.supplier_buckets) {
          const totalBucketSuppliers = opsData.supplier_buckets.reduce((sum: number, b: any) => sum + b.supplier_count, 0);
          const colors = ['bg-green-100 text-green-700', 'bg-emerald-100 text-emerald-700', 'bg-yellow-100 text-yellow-700', 'bg-orange-100 text-orange-700', 'bg-red-100 text-red-700', 'bg-gray-100 text-gray-700'];
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Distribution of suppliers by delivery performance tier.">Supplier Ranking (Operations)</SectionTitle>
              <div className="space-y-2 mt-3">
                {opsData.supplier_buckets.map((bucket: any, idx: number) => {
                  const pct = totalBucketSuppliers > 0 ? ((bucket.supplier_count / totalBucketSuppliers) * 100).toFixed(1) : '0';
                  return (
                    <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${colors[idx] || colors[5]}`}>{bucket.bucket}</span>
                      <div className="text-right"><span className="font-bold">{bucket.supplier_count}</span><span className="text-gray-500 text-sm ml-2">({pct}%)</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        if (w.id === 'ops_table_cost_by_part' && opsData?.supplier_cost_by_part) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Average cost by part type.">Cost by Part Type (Operations)</SectionTitle>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b">
                    <th className="text-left py-2 px-2">Part</th>
                    <th className="text-right py-2 px-2">Avg Cost</th>
                    <th className="text-right py-2 px-2">Orders</th>
                  </tr></thead>
                  <tbody>
                    {opsData.supplier_cost_by_part.slice(0, 15).map((p: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2">{truncate(p.part_name, 25)}</td>
                        <td className="text-right py-2 px-2 text-green-600 font-medium">{formatCurrency(p.avg_cost)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(p.order_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (w.id === 'ops_table_cancellations' && opsData?.cancellations) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Suppliers with highest cancellation rates.">Cancellations (Operations)</SectionTitle>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b">
                    <th className="text-left py-2 px-2">Supplier</th>
                    <th className="text-right py-2 px-2">Cancellations</th>
                    <th className="text-right py-2 px-2">Total</th>
                    <th className="text-right py-2 px-2">Rate</th>
                  </tr></thead>
                  <tbody>
                    {opsData.cancellations.slice(0, 15).map((c: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2">{truncate(c.supplier_name, 20)}</td>
                        <td className="text-right py-2 px-2 text-red-600">{formatNumber(c.cancellations)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(c.total_orders)}</td>
                        <td className="text-right py-2 px-2 font-medium">{Number(c.cancel_rate || 0).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (w.id === 'ops_table_market_position' && opsData?.market_position) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Supplier pricing vs market average.">Market Position (Operations)</SectionTitle>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Above Market</span>
                  <span className="text-2xl font-bold text-red-600">{formatNumber(opsData.market_position.above_market)}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Below Market</span>
                  <span className="text-2xl font-bold text-green-600">{formatNumber(opsData.market_position.below_market)}</span>
                </div>
              </div>
            </div>
          );
        }
        if (w.id === 'ops_table_risk_indicators' && opsData?.risk) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Potential issues requiring attention.">Risk Indicators (Operations)</SectionTitle>
              <div className="space-y-3 mt-3">
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Price Anomalies</span>
                  <span className="text-xl font-bold text-yellow-600">{formatNumber(opsData.risk.price_anomalies)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">High Risk Suppliers</span>
                  <span className="text-xl font-bold text-red-600">{formatNumber(opsData.risk.high_risk_suppliers)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Complex Claims</span>
                  <span className="text-xl font-bold text-purple-600">{formatNumber(opsData.complex_claims?.total)}</span>
                </div>
              </div>
            </div>
          );
        }
        // Shareholders Tables
        if (w.id === 'sh_table_top_insurers' && shData?.top_insurers) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Top insurers by order value.">Top Insurers (Shareholders)</SectionTitle>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b">
                    <th className="text-left py-2 px-2">Insurer</th>
                    <th className="text-right py-2 px-2">Orders</th>
                    <th className="text-right py-2 px-2">Value</th>
                  </tr></thead>
                  <tbody>
                    {shData.top_insurers.map((ins: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2">{truncate(ins.insurer_name, 25)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(ins.total_orders)}</td>
                        <td className="text-right py-2 px-2 text-green-600 font-medium">{formatCurrency(ins.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (w.id === 'sh_table_top_suppliers' && shData?.top_suppliers) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Top suppliers by order value.">Top Suppliers (Shareholders)</SectionTitle>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b">
                    <th className="text-left py-2 px-2">Supplier</th>
                    <th className="text-right py-2 px-2">Orders</th>
                    <th className="text-right py-2 px-2">Value</th>
                  </tr></thead>
                  <tbody>
                    {shData.top_suppliers.map((sup: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2">{truncate(sup.supplier_name, 25)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(sup.total_orders)}</td>
                        <td className="text-right py-2 px-2 text-green-600 font-medium">{formatCurrency(sup.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        // Performance Tables
        if (w.id === 'perf_table_supplier_delivery' && perfData?.supplier_delivery) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Supplier delivery speed metrics.">Supplier Delivery (Performance)</SectionTitle>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b">
                    <th className="text-left py-2 px-2">Supplier</th>
                    <th className="text-right py-2 px-2">Orders</th>
                    <th className="text-right py-2 px-2">Avg Days</th>
                    <th className="text-right py-2 px-2">Same Day</th>
                    <th className="text-right py-2 px-2">On-Time %</th>
                  </tr></thead>
                  <tbody>
                    {perfData.supplier_delivery.slice(0, 15).map((s: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2">{truncate(s.supplier_name, 18)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(s.delivered_orders)}</td>
                        <td className="text-right py-2 px-2 font-medium">{formatDays(s.avg_delivery_days)}</td>
                        <td className="text-right py-2 px-2 text-green-600">{formatNumber(s.same_day)}</td>
                        <td className="text-right py-2 px-2">{formatPercent(s.on_time_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (w.id === 'perf_table_slowest_parts' && perfData?.part_delivery_time) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Parts with longest average delivery times.">Slowest Parts (Performance)</SectionTitle>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b">
                    <th className="text-left py-2 px-2">Part</th>
                    <th className="text-right py-2 px-2">Orders</th>
                    <th className="text-right py-2 px-2">Avg Days</th>
                    <th className="text-right py-2 px-2">Delayed</th>
                    <th className="text-right py-2 px-2">Delay %</th>
                  </tr></thead>
                  <tbody>
                    {perfData.part_delivery_time.slice(0, 15).map((p: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2" title={p.part_name}>{truncate(p.part_name, 20)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(p.total_orders)}</td>
                        <td className="text-right py-2 px-2 font-medium text-orange-600">{formatDays(p.avg_delivery_days)}</td>
                        <td className="text-right py-2 px-2 text-red-600">{formatNumber(p.delayed_count)}</td>
                        <td className="text-right py-2 px-2">{formatPercent(p.delay_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (w.id === 'perf_table_claim_cycle' && perfData?.claim_cycle_time) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Total claim duration from first order to last delivery.">Claim Cycle Time (Performance)</SectionTitle>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b">
                    <th className="text-left py-2 px-2">Claim</th>
                    <th className="text-left py-2 px-2">Insurer</th>
                    <th className="text-right py-2 px-2">Parts</th>
                    <th className="text-right py-2 px-2">Cycle Days</th>
                    <th className="text-right py-2 px-2">Value</th>
                    <th className="text-left py-2 px-2">Status</th>
                  </tr></thead>
                  <tbody>
                    {perfData.claim_cycle_time.slice(0, 15).map((c: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2 font-mono text-xs">{c.claim_number}</td>
                        <td className="py-2 px-2">{truncate(c.insurer_name, 15)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(c.total_parts)}</td>
                        <td className="text-right py-2 px-2 font-medium">{c.claim_cycle_days != null ? `${c.claim_cycle_days}d` : '-'}</td>
                        <td className="text-right py-2 px-2 text-green-600">{formatCurrency(c.total_value)}</td>
                        <td className="py-2 px-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${c.status === 'Complete' ? 'bg-green-100 text-green-700' : c.status === 'Partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{c.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (w.id === 'perf_table_pending_deadline' && perfData?.pending_parts) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Active orders past their expected delivery deadline.">Pending Past Deadline (Performance)</SectionTitle>
              {perfData.pending_parts.length === 0
                ? <p className="text-gray-500 text-center py-8">No overdue parts</p>
                : (
                  <div className="overflow-x-auto max-h-64">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-white"><tr className="border-b">
                        <th className="text-left py-2 px-2">Claim</th>
                        <th className="text-left py-2 px-2">Part</th>
                        <th className="text-left py-2 px-2">Supplier</th>
                        <th className="text-right py-2 px-2">Days Overdue</th>
                        <th className="text-right py-2 px-2">Value</th>
                      </tr></thead>
                      <tbody>
                        {perfData.pending_parts.slice(0, 15).map((p: any, i: number) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-2 font-mono text-xs">{p.claim_number}</td>
                            <td className="py-2 px-2">{truncate(p.part_name, 18)}</td>
                            <td className="py-2 px-2">{truncate(p.supplier_name, 15)}</td>
                            <td className="text-right py-2 px-2 text-red-600 font-bold">{p.days_past_deadline != null ? `+${p.days_past_deadline}d` : '-'}</td>
                            <td className="text-right py-2 px-2">{formatCurrency(p.value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              }
            </div>
          );
        }
        if (w.id === 'perf_table_suppliers_per_claim' && perfData?.suppliers_per_claim) {
          const spc = perfData.suppliers_per_claim;
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Distribution of suppliers used per claim.">Suppliers per Claim (Performance)</SectionTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mt-3">
                <div className="text-center p-3 bg-blue-50 rounded">
                  <div className="text-2xl font-bold text-blue-600">{Number(spc.avg_suppliers_per_claim || 0).toFixed(2)}</div>
                  <div className="text-xs text-gray-600">Avg Suppliers</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-xl font-bold">{spc.min_suppliers ?? 0} – {spc.max_suppliers ?? 0}</div>
                  <div className="text-xs text-gray-600">Min – Max</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded">
                  <div className="text-xl font-bold text-green-600">{formatNumber(spc.single_supplier_claims)}</div>
                  <div className="text-xs text-gray-600">Single Supplier</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded">
                  <div className="text-xl font-bold text-yellow-600">{formatNumber(spc.two_supplier_claims)}</div>
                  <div className="text-xs text-gray-600">Two Suppliers</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded">
                  <div className="text-xl font-bold text-orange-600">{formatNumber(spc.multi_supplier_claims)}</div>
                  <div className="text-xs text-gray-600">3+ Suppliers</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded col-span-2">
                  <div className="text-xl font-bold text-purple-600">{formatNumber(spc.total_claims)}</div>
                  <div className="text-xs text-gray-600">Total Claims</div>
                </div>
              </div>
            </div>
          );
        }
        if (w.id === 'perf_table_segmentation' && perfData?.supplier_segmentation) {
          const tierColors: Record<string, string> = {
            'Premium': 'bg-green-100 text-green-700',
            'Reliable': 'bg-blue-100 text-blue-700',
            'Standard': 'bg-yellow-100 text-yellow-700',
            'At Risk': 'bg-red-100 text-red-700',
          };
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Suppliers grouped by delivery performance and value tier.">Supplier Segmentation (Performance)</SectionTitle>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b">
                    <th className="text-left py-2 px-2">Supplier</th>
                    <th className="text-right py-2 px-2">Orders</th>
                    <th className="text-right py-2 px-2">Delivery %</th>
                    <th className="text-right py-2 px-2">Value</th>
                    <th className="text-left py-2 px-2">Perf Tier</th>
                    <th className="text-left py-2 px-2">Value Tier</th>
                  </tr></thead>
                  <tbody>
                    {perfData.supplier_segmentation.slice(0, 15).map((s: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2">{truncate(s.supplier_name, 18)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(s.total_orders)}</td>
                        <td className="text-right py-2 px-2">{formatPercent(s.delivery_rate)}</td>
                        <td className="text-right py-2 px-2 text-green-600">{formatCurrency(s.total_value)}</td>
                        <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs ${tierColors[s.performance_tier] || 'bg-gray-100 text-gray-600'}`}>{s.performance_tier}</span></td>
                        <td className="py-2 px-2 text-gray-600 text-xs">{s.value_tier}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        // Patterns Tables
        if (w.id === 'pat_table_complex_by_vehicle' && patData?.complex_by_vehicle) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Complex claims grouped by vehicle manufacturer.">Complex Claims by Make (Patterns)</SectionTitle>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b">
                    <th className="text-left py-2 px-2">Make</th>
                    <th className="text-right py-2 px-2">Complex</th>
                    <th className="text-right py-2 px-2">Avg Parts</th>
                    <th className="text-right py-2 px-2">Avg Suppliers</th>
                  </tr></thead>
                  <tbody>
                    {patData.complex_by_vehicle.slice(0, 15).map((v: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2 font-medium">{v.vehicle_make}</td>
                        <td className="text-right py-2 px-2 text-purple-600 font-bold">{formatNumber(v.complex_claims)}</td>
                        <td className="text-right py-2 px-2">{v.avg_parts}</td>
                        <td className="text-right py-2 px-2">{v.avg_suppliers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (w.id === 'pat_table_vehicle_stats' && patData?.vehicle_stats) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Vehicle statistics overview.">Vehicle Stats (Patterns)</SectionTitle>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
                <div className="text-center p-3 bg-blue-50 rounded">
                  <div className="text-2xl font-bold text-blue-600">{formatNumber(patData.vehicle_stats.unique_vehicles)}</div>
                  <div className="text-xs text-gray-600">Unique Vehicles</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded">
                  <div className="text-2xl font-bold text-green-600">{formatNumber(patData.vehicle_stats.total_claims)}</div>
                  <div className="text-xs text-gray-600">Claims</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded">
                  <div className="text-2xl font-bold text-purple-600">{formatNumber(patData.vehicle_stats.total_orders)}</div>
                  <div className="text-xs text-gray-600">Orders</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded">
                  <div className="text-2xl font-bold text-orange-600">{formatNumber(patData.vehicle_stats.unique_makes)}</div>
                  <div className="text-xs text-gray-600">Makes</div>
                </div>
                <div className="text-center p-3 bg-teal-50 rounded">
                  <div className="text-2xl font-bold text-teal-600">{formatNumber(patData.vehicle_stats.unique_models)}</div>
                  <div className="text-xs text-gray-600">Models</div>
                </div>
              </div>
            </div>
          );
        }
        if (w.id === 'pat_table_price_anomalies' && patData) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Parts with pricing more than 2 standard deviations from the mean (Z-score &gt; 2).">Price Anomalies (Patterns)</SectionTitle>
              <div className="flex items-center gap-6 p-6 bg-yellow-50 rounded-lg">
                <div className="text-4xl font-bold text-yellow-600">{formatNumber(patData.price_anomalies)}</div>
                <div className="text-sm text-gray-600">anomalous price records detected</div>
              </div>
            </div>
          );
        }
        if (w.id === 'pat_table_supplier_risk' && patData) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Suppliers with delivery rate below 50% (minimum 5 orders).">Supplier Risk Scoring (Patterns)</SectionTitle>
              <div className="flex items-center gap-6 p-6 bg-red-50 rounded-lg">
                <div className="text-4xl font-bold text-red-600">{formatNumber(patData.high_risk_suppliers)}</div>
                <div className="text-sm text-gray-600">high-risk suppliers detected (&lt;50% delivery rate)</div>
              </div>
            </div>
          );
        }
        if (w.id === 'pat_table_delivery_prediction' && patData) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Delivery time statistics used for lead time prediction.">Delivery Prediction (Patterns)</SectionTitle>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600">{formatDays(patData.avg_delivery_days)}</div>
                  <div className="text-sm text-gray-600">Avg Delivery Days</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600">{formatPercent(patData.delivery_accuracy)}</div>
                  <div className="text-sm text-gray-600">Orders with Delivery Data</div>
                </div>
              </div>
            </div>
          );
        }
        if (w.id === 'pat_table_part_associations' && patData) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Count of unique co-occurring part pairs across claims (market basket analysis).">Part Associations (Patterns)</SectionTitle>
              <div className="flex items-center gap-6 p-6 bg-purple-50 rounded-lg">
                <div className="text-4xl font-bold text-purple-600">{formatNumber(patData.part_associations)}</div>
                <div className="text-sm text-gray-600">unique part co-occurrence pairs detected</div>
              </div>
            </div>
          );
        }
        if (w.id === 'pat_table_supplier_clusters' && patData) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Suppliers tracked for behavioral pattern analysis.">Supplier Clusters (Patterns)</SectionTitle>
              <div className="flex items-center gap-6 p-6 bg-teal-50 rounded-lg">
                <div className="text-4xl font-bold text-teal-600">{formatNumber(patData.supplier_clusters)}</div>
                <div className="text-sm text-gray-600">active suppliers tracked for cluster analysis</div>
              </div>
            </div>
          );
        }
        if (w.id === 'pat_table_automation_impact' && patData) {
          return (
            <div key={w.id} className="bg-white rounded-lg shadow p-4">
              <SectionTitle info="Impact of automation on order assignment and quoting.">Automation Impact (Patterns)</SectionTitle>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600">{patData.auto_assign_rate || 0}%</div>
                  <div className="text-sm text-gray-600">Auto-Assign Rate</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600">{patData.auto_quote_rate || 0}%</div>
                  <div className="text-sm text-gray-600">Auto-Quote Rate</div>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

// ============================================================================
// Parts Analytics Page
// ============================================================================


const PartsAnalyticsPage: React.FC<{ data: PartsAnalyticsData | null; loading: boolean }> = ({ data, loading }) => {
  const { user } = useAuth();
  const [revokedWidgets, setRevokedWidgets] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!user?.user_key) return;
    authFetch(`/api/users/widget-permissions?user_key=${user.user_key}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(res => {
        const revoked = new Set(
          (res?.data?.permissions ?? [])
            .filter((p: any) => !p.is_visible)
            .map((p: any) => p.widget_key as string)
        );
        setRevokedWidgets(revoked);
      })
      .catch(() => {});
  }, [user?.user_key]);
  const isWidgetVisible = (key: string) => !revokedWidgets.has(key);
  const safeByVolume = data?.by_volume ?? [];
  const safeByValue = data?.by_value ?? [];
  const safePartTypes = data?.part_types ?? [];
  const safePriceVariance = data?.price_variance ?? [];
  const safeCancellations = data?.cancellations ?? [];

  const { sortedItems: sortedByVolume, requestSort: sortVol, getSortIcon: volIcon } = useSortableData(safeByVolume, { key: 'order_count', direction: 'desc' });
  const { sortedItems: sortedByValue, requestSort: sortVal, getSortIcon: valIcon } = useSortableData(safeByValue, { key: 'total_value', direction: 'desc' });
  const { sortedItems: sortedPartTypes, requestSort: sortType, getSortIcon: typeIcon } = useSortableData(safePartTypes, { key: 'order_count', direction: 'desc' });
  const { sortedItems: sortedVariance, requestSort: sortVar, getSortIcon: varIcon } = useSortableData(safePriceVariance, { key: 'variance_pct', direction: 'desc' });
  const { sortedItems: sortedCancellations, requestSort: sortCancel, getSortIcon: cancelIcon } = useSortableData(safeCancellations, { key: 'cancel_rate', direction: 'desc' });

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!data) return <div className="p-8 text-center text-gray-500">No data available</div>;

  // Compute aggregate summary from by_volume rows
  const totalParts = safeByVolume.reduce((s, r) => s + (r.order_count || 0), 0);
  const totalValue = safeByVolume.reduce((s, r) => s + (r.total_value || 0), 0);
  const uniqueParts = safeByVolume.length;
  const avgPrice = totalParts > 0 ? totalValue / totalParts : 0;
  const maxPrice = safeByVolume.reduce((m, r) => Math.max(m, r.max_price || 0), 0);
  const rawMin = safeByVolume.reduce((m, r) => (r.min_price && r.min_price > 0 ? Math.min(m, r.min_price) : m), Infinity);
  const minPrice = rawMin === Infinity ? 0 : rawMin;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Total Parts</div>
          <div className="text-2xl font-bold">{formatNumber(totalParts)}</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Unique Parts</div>
          <div className="text-2xl font-bold">{formatNumber(uniqueParts)}</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Total Value</div>
          <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Avg Price</div>
          <div className="text-2xl font-bold">{formatCurrency(avgPrice)}</div>
        </div>
        <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Max Price</div>
          <div className="text-2xl font-bold">{formatCurrency(maxPrice)}</div>
        </div>
        <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Min Price</div>
          <div className="text-2xl font-bold">{formatCurrency(minPrice)}</div>
        </div>
      </div>

      {/* Category Breakdown chart + Price Variance table */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {isWidgetVisible('parts_chart_type_pie') && (<div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Parts breakdown by type/category.">Category Breakdown</SectionTitle>
          {safePartTypes.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={safePartTypes.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="part_type" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number, name: string) => name === 'total_value' ? formatCurrency(v) : formatNumber(v)} />
                  <Legend />
                  <Bar dataKey="order_count" fill="#3b82f6" name="Count" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-gray-500 text-center py-8">No category data</p>}
        </div>)}

        {isWidgetVisible('parts_table_price_variance') && (<div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Parts with highest price variability across orders.">Price Variance</SectionTitle>
          {safePriceVariance.length > 0 ? (
            <div className="overflow-x-auto max-h-80">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-white"><tr className="border-b">
                  <SortTh label="Part" sortKey="part_name" onSort={sortVar} sortIcon={varIcon} className="text-left py-2 px-2" />
                  <SortTh label="Orders" sortKey="order_count" onSort={sortVar} sortIcon={varIcon} className="text-right py-2 px-2" />
                  <SortTh label="Min" sortKey="min_price" onSort={sortVar} sortIcon={varIcon} className="text-right py-2 px-2" />
                  <SortTh label="Max" sortKey="max_price" onSort={sortVar} sortIcon={varIcon} className="text-right py-2 px-2" />
                  <SortTh label="Var %" sortKey="variance_pct" onSort={sortVar} sortIcon={varIcon} className="text-right py-2 px-2" />
                </tr></thead>
                <tbody>
                  {(sortedVariance || []).map((p, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2" title={p.part_name}>{truncate(p.part_name, 22)}</td>
                      <td className="text-right py-2 px-2">{formatNumber(p.order_count)}</td>
                      <td className="text-right py-2 px-2">{formatCurrency(p.min_price)}</td>
                      <td className="text-right py-2 px-2">{formatCurrency(p.max_price)}</td>
                      <td className={`text-right py-2 px-2 font-medium ${p.variance_pct > 50 ? 'text-red-600' : 'text-amber-600'}`}>{formatPercent(p.variance_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-gray-500 text-center py-8">No price variance data</p>}
        </div>)}
      </div>

      {/* Top Parts by Volume + Top Parts by Value */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {isWidgetVisible('parts_table_by_volume') && (<div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Most frequently ordered parts ranked by order count.">Top Parts by Volume</SectionTitle>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <SortTh label="Part" sortKey="part_name" onSort={sortVol} sortIcon={volIcon} className="text-left py-2 px-2" />
                <SortTh label="Part #" sortKey="part_number" onSort={sortVol} sortIcon={volIcon} className="text-left py-2 px-2" />
                <SortTh label="Type" sortKey="part_type" onSort={sortVol} sortIcon={volIcon} className="text-left py-2 px-2" />
                <SortTh label="Orders" sortKey="order_count" onSort={sortVol} sortIcon={volIcon} className="text-right py-2 px-2" />
                <SortTh label="Avg Price" sortKey="avg_price" onSort={sortVol} sortIcon={volIcon} className="text-right py-2 px-2" />
                <SortTh label="Value" sortKey="total_value" onSort={sortVol} sortIcon={volIcon} className="text-right py-2 px-2" />
              </tr></thead>
              <tbody>
                {(sortedByVolume || []).map((p, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2" title={p.part_name}>{truncate(p.part_name, 18)}</td>
                    <td className="py-2 px-2 text-gray-500">{truncate(p.part_number, 10)}</td>
                    <td className="py-2 px-2 text-gray-500 text-xs">{truncate(p.part_type, 10)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(p.order_count)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(p.avg_price)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(p.total_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>)}

        {isWidgetVisible('parts_table_by_value') && (<div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Parts ranked by total procurement value.">Top Parts by Value</SectionTitle>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <SortTh label="Part" sortKey="part_name" onSort={sortVal} sortIcon={valIcon} className="text-left py-2 px-2" />
                <SortTh label="Part #" sortKey="part_number" onSort={sortVal} sortIcon={valIcon} className="text-left py-2 px-2" />
                <SortTh label="Type" sortKey="part_type" onSort={sortVal} sortIcon={valIcon} className="text-left py-2 px-2" />
                <SortTh label="Orders" sortKey="order_count" onSort={sortVal} sortIcon={valIcon} className="text-right py-2 px-2" />
                <SortTh label="Avg Price" sortKey="avg_price" onSort={sortVal} sortIcon={valIcon} className="text-right py-2 px-2" />
                <SortTh label="Value" sortKey="total_value" onSort={sortVal} sortIcon={valIcon} className="text-right py-2 px-2" />
                <SortTh label="Del. %" sortKey="delivery_rate" onSort={sortVal} sortIcon={valIcon} className="text-right py-2 px-2" />
              </tr></thead>
              <tbody>
                {(sortedByValue || []).map((p, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2" title={p.part_name}>{truncate(p.part_name, 18)}</td>
                    <td className="py-2 px-2 text-gray-500">{truncate(p.part_number, 10)}</td>
                    <td className="py-2 px-2 text-gray-500 text-xs">{truncate(p.part_type, 10)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(p.order_count)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(p.avg_price)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(p.total_value)}</td>
                    <td className="text-right py-2 px-2">{formatPercent(p.delivery_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>)}
      </div>

      {/* Category Details (part_types) */}
      {isWidgetVisible('parts_table_part_types') && (<div className="bg-white rounded-lg shadow p-4">
        <SectionTitle info="All part types with counts, values, and delivery rates.">Category Details</SectionTitle>
        <div className="overflow-x-auto max-h-96">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-white"><tr className="border-b">
              <SortTh label="Category" sortKey="part_type" onSort={sortType} sortIcon={typeIcon} className="text-left py-2 px-2" />
              <SortTh label="Count" sortKey="order_count" onSort={sortType} sortIcon={typeIcon} className="text-right py-2 px-2" />
              <SortTh label="Unique Parts" sortKey="unique_parts" onSort={sortType} sortIcon={typeIcon} className="text-right py-2 px-2" />
              <SortTh label="Total Value" sortKey="total_value" onSort={sortType} sortIcon={typeIcon} className="text-right py-2 px-2" />
              <SortTh label="Avg Price" sortKey="avg_price" onSort={sortType} sortIcon={typeIcon} className="text-right py-2 px-2" />
              <SortTh label="Del. %" sortKey="delivery_rate" onSort={sortType} sortIcon={typeIcon} className="text-right py-2 px-2" />
              <SortTh label="Cancel %" sortKey="cancel_rate" onSort={sortType} sortIcon={typeIcon} className="text-right py-2 px-2" />
            </tr></thead>
            <tbody>
              {(sortedPartTypes || []).map((c, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-2 font-medium">{c.part_type}</td>
                  <td className="text-right py-2 px-2">{formatNumber(c.order_count)}</td>
                  <td className="text-right py-2 px-2">{formatNumber(c.unique_parts)}</td>
                  <td className="text-right py-2 px-2">{formatCurrency(c.total_value)}</td>
                  <td className="text-right py-2 px-2">{formatCurrency(c.avg_price)}</td>
                  <td className="text-right py-2 px-2 text-green-600">{formatPercent(c.delivery_rate)}</td>
                  <td className="text-right py-2 px-2 text-red-600">{formatPercent(c.cancel_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>)}

      {/* Cancellations */}
      {isWidgetVisible('parts_table_cancellations') && safeCancellations.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Parts with the highest cancellation rates.">Parts Cancellations</SectionTitle>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <SortTh label="Part" sortKey="part_name" onSort={sortCancel} sortIcon={cancelIcon} className="text-left py-2 px-2" />
                <SortTh label="Part #" sortKey="part_number" onSort={sortCancel} sortIcon={cancelIcon} className="text-left py-2 px-2" />
                <SortTh label="Total Orders" sortKey="total_orders" onSort={sortCancel} sortIcon={cancelIcon} className="text-right py-2 px-2" />
                <SortTh label="Cancelled" sortKey="cancelled_count" onSort={sortCancel} sortIcon={cancelIcon} className="text-right py-2 px-2" />
                <SortTh label="Cancel %" sortKey="cancel_rate" onSort={sortCancel} sortIcon={cancelIcon} className="text-right py-2 px-2" />
                <SortTh label="Cancel Value" sortKey="cancelled_value" onSort={sortCancel} sortIcon={cancelIcon} className="text-right py-2 px-2" />
              </tr></thead>
              <tbody>
                {(sortedCancellations || []).map((c, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2" title={c.part_name}>{truncate(c.part_name, 22)}</td>
                    <td className="py-2 px-2 text-gray-500">{truncate(c.part_number, 12)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(c.total_orders)}</td>
                    <td className="text-right py-2 px-2 text-red-600">{formatNumber(c.cancelled_count)}</td>
                    <td className="text-right py-2 px-2 text-red-600 font-medium">{formatPercent(c.cancel_rate)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(c.cancelled_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Supplier Analytics Page
// ============================================================================

const SupplierAnalyticsPage: React.FC<{ data: SupplierAnalyticsData | null; loading: boolean }> = ({ data, loading }) => {
  const { sortedItems: sortedRanking, requestSort: sortRank, getSortIcon: rankIcon } = useSortableData(data?.ranking, { key: 'total_orders', direction: 'desc' });
  const { sortedItems: sortedPrice, requestSort: sortPrice, getSortIcon: priceIcon } = useSortableData(data?.price_competitiveness, { key: 'price_diff_pct', direction: 'asc' });
  const { sortedItems: sortedCancellations, requestSort: sortCancel, getSortIcon: cancelIcon } = useSortableData(data?.cancellations, { key: 'cancel_rate', direction: 'desc' });
  const { sortedItems: sortedTopSuppliers, requestSort: sortTopSup, getSortIcon: topSupIcon } = useSortableData(data?.top_suppliers, { key: 'total_parts', direction: 'desc' });
  const { sortedItems: sortedStatePerf, requestSort: sortState, getSortIcon: stateIcon } = useSortableData(data?.state_performance, { key: 'total_parts', direction: 'desc' });

  if (loading || !data) return <Loading />;

  const suppliers_per_claim = data.suppliers_per_claim;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Total Suppliers</div>
          <div className="text-3xl font-bold">{formatNumber(data.kpis.total_suppliers)}</div>
          <div className="text-xs opacity-70 mt-1">Unique suppliers with orders</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Avg Delivery Rate</div>
          <div className="text-3xl font-bold">{formatPercent(data.ranking.reduce((a, b) => a + (b.delivery_rate || 0), 0) / Math.max(data.ranking.length, 1))}</div>
          <div className="text-xs opacity-70 mt-1">Mean delivery success</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Avg Delivery Days</div>
          <div className="text-3xl font-bold">{(data.delivery_analysis.reduce((a, b) => a + (b.avg_delivery_days || 0), 0) / Math.max(data.delivery_analysis.length, 1)).toFixed(1)}</div>
          <div className="text-xs opacity-70 mt-1">Days to delivery</div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Below Market</div>
          <div className="text-3xl font-bold">{formatNumber(data.price_competitiveness.filter(p => p.price_position === 'Below Market').length)}</div>
          <div className="text-xs opacity-70 mt-1">Competitive suppliers</div>
        </div>
      </section>

      {/* Performance by State Table */}
      {data.state_performance && data.state_performance.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <div>
              <h3 className="font-semibold">Performance by State</h3>
              <p className="text-xs text-gray-500 mt-0.5">Automation rates and part type distribution by region</p>
            </div>
            <span className="text-sm text-gray-500">{data.state_performance.length} states</span>
          </div>
          <div className="overflow-x-auto p-4">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortTh label="State" sortKey="state" onSort={sortState} sortIcon={stateIcon} className="text-left px-3 py-2" info="Workshop state/region grouping" />
                  <SortTh label="Auto Assign" sortKey="auto_assigned_parts" onSort={sortState} sortIcon={stateIcon} className="text-right px-3 py-2" info="Parts automatically assigned to suppliers" />
                  <SortTh label="Manual" sortKey="manual_assigned_parts" onSort={sortState} sortIcon={stateIcon} className="text-right px-3 py-2" info="Parts manually assigned to suppliers" />
                  <SortTh label="Total Parts" sortKey="total_parts" onSort={sortState} sortIcon={stateIcon} className="text-right px-3 py-2" info="Total part orders in this state" />
                  <SortTh label="Auto %" sortKey="auto_assign_pct" onSort={sortState} sortIcon={stateIcon} className="text-right px-3 py-2" info="Percentage of parts auto-assigned = Auto / Total × 100" />
                  <SortTh label="Generic" sortKey="generic_parts" onSort={sortState} sortIcon={stateIcon} className="text-right px-3 py-2" info="Parts with type 'Generico'" />
                  <SortTh label="Generic %" sortKey="generic_pct" onSort={sortState} sortIcon={stateIcon} className="text-right px-3 py-2" info="Percentage of generic parts = Generic / Total × 100" />
                  <SortTh label="Original" sortKey="original_parts" onSort={sortState} sortIcon={stateIcon} className="text-right px-3 py-2" info="Parts with type 'Original' or 'Genuino'" />
                  <SortTh label="Original %" sortKey="original_pct" onSort={sortState} sortIcon={stateIcon} className="text-right px-3 py-2" info="Percentage of original parts = Original / Total × 100" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {(sortedStatePerf || []).map((s, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{s.state}</td>
                    <td className="px-3 py-2 text-right text-blue-600">{formatNumber(s.auto_assigned_parts)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{formatNumber(s.manual_assigned_parts)}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatNumber(s.total_parts)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={s.auto_assign_pct >= 50 ? 'text-green-600' : 'text-orange-600'}>
                    {Number(s.auto_assign_pct || 0)}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{formatNumber(s.generic_parts)}</td>
                    <td className="px-3 py-2 text-right text-purple-600">{Number(s.generic_pct || 0)}%</td>
                    <td className="px-3 py-2 text-right">{formatNumber(s.original_parts)}</td>
                    <td className="px-3 py-2 text-right text-indigo-600">{s.original_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Suppliers Table (All Suppliers) */}
      {data.top_suppliers && data.top_suppliers.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <div>
              <h3 className="font-semibold">Top Suppliers</h3>
              <p className="text-xs text-gray-500 mt-0.5">Ranked by order volume with delivery metrics</p>
            </div>
            <span className="text-sm text-gray-500">{data.top_suppliers.length} suppliers</span>
          </div>
          <div className="overflow-x-auto max-h-[500px] p-4">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <SortTh label="Supplier" sortKey="supplier_name" onSort={sortTopSup} sortIcon={topSupIcon} className="text-left py-2 px-2" info="Supplier name" />
                  <SortTh label="Parts" sortKey="total_parts" onSort={sortTopSup} sortIcon={topSupIcon} className="text-right py-2 px-2" info="Total part orders for this supplier" />
                  <SortTh label="Auth Parts" sortKey="authorized_parts" onSort={sortTopSup} sortIcon={topSupIcon} className="text-right py-2 px-2" info="Parts with Active status (awaiting delivery)" />
                  <SortTh label="Auth Amount" sortKey="authorized_amount" onSort={sortTopSup} sortIcon={topSupIcon} className="text-right py-2 px-2" info="Total value of Active status parts" />
                  <SortTh label="Avg Cost" sortKey="avg_cost" onSort={sortTopSup} sortIcon={topSupIcon} className="text-right py-2 px-2" info="Average price per part = Total Value / Total Parts" />
                  <SortTh label="Delivered" sortKey="delivered_parts" onSort={sortTopSup} sortIcon={topSupIcon} className="text-right py-2 px-2" info="Parts with Complete status" />
                  <SortTh label="Rate" sortKey="delivery_rate" onSort={sortTopSup} sortIcon={topSupIcon} className="text-right py-2 px-2" info="Delivery rate = Delivered / Total × 100" />
                  <SortTh label="Avg Days" sortKey="avg_delivery_days" onSort={sortTopSup} sortIcon={topSupIcon} className="text-right py-2 px-2" info="Average delivery days for completed orders" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {(sortedTopSuppliers || []).map((s, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="py-2 px-2">{truncate(s.supplier_name, 25)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(s.total_parts)}</td>
                    <td className="text-right py-2 px-2 text-blue-600">{formatNumber(s.authorized_parts)}</td>
                    <td className="text-right py-2 px-2 text-green-600">{formatCurrency(s.authorized_amount)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(s.avg_cost)}</td>
                    <td className="text-right py-2 px-2 text-emerald-600">{formatNumber(s.delivered_parts)}</td>
                    <td className="text-right py-2 px-2">{formatPercent(s.delivery_rate)}</td>
                    <td className="text-right py-2 px-2">{s.avg_delivery_days != null ? Number(s.avg_delivery_days).toFixed(1) : '-'}</td>
                    
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Suppliers Per Claim KPIs */}
      {suppliers_per_claim && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <div>
              <h3 className="font-semibold">Suppliers Per Claim</h3>
              <p className="text-xs text-gray-500 mt-0.5">Supplier fragmentation analysis</p>
            </div>
            <span className="text-sm text-gray-500">{formatNumber(suppliers_per_claim.total_claims)} claims</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white text-center">
              <div className="text-2xl font-bold">{Number(suppliers_per_claim.avg_suppliers_per_claim || 0).toFixed(2)}</div>
              <div className="text-xs opacity-80">Avg Suppliers</div>
            </div>
            <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg shadow-lg p-4 text-white text-center">
              <div className="text-xl font-bold">{suppliers_per_claim.min_suppliers ?? 0} - {suppliers_per_claim.max_suppliers ?? 0}</div>
              <div className="text-xs opacity-80">Min - Max</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-lg p-4 text-white text-center">
              <div className="text-xl font-bold">{formatNumber(suppliers_per_claim.single_supplier_claims)}</div>
              <div className="text-xs opacity-80">Single Supplier</div>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-4 text-white text-center">
              <div className="text-xl font-bold">{formatNumber(suppliers_per_claim.two_supplier_claims)}</div>
              <div className="text-xs opacity-80">Two Suppliers</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-4 text-white text-center">
              <div className="text-xl font-bold">{formatNumber(suppliers_per_claim.multi_supplier_claims)}</div>
              <div className="text-xs opacity-80">3+ Suppliers</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-4 text-white text-center">
              <div className="text-xl font-bold">{formatNumber(suppliers_per_claim.total_claims)}</div>
              <div className="text-xs opacity-80">Total Claims</div>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Rankings */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h3 className="font-semibold">Supplier Rankings</h3>
            <p className="text-xs text-gray-500 mt-0.5">Ranked by composite score (delivery rate, speed, volume)</p>
          </div>
          <span className="text-sm text-gray-500">Top 20</span>
        </div>
        <div className="overflow-x-auto p-4">
          <table className="min-w-full text-sm">
            <thead><tr className="border-b">
              <SortTh label="Supplier" sortKey="supplier_name" onSort={sortRank} sortIcon={rankIcon} className="text-left py-2 px-2" info="Supplier name" />
              <SortTh label="Score" sortKey="supplier_score" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="XNuuP supplier score (composite metric)" />
              <SortTh label="Orders" sortKey="total_orders" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Total part orders" />
              <SortTh label="Delivery %" sortKey="delivery_rate" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Delivery rate = Delivered / Total × 100" />
              <SortTh label="Cancel %" sortKey="cancel_rate" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Cancellation rate = Cancelled / Total × 100" />
              <SortTh label="Avg Days" sortKey="avg_delivery_days" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Average delivery days for completed orders" />
              <SortTh label="Value" sortKey="total_value" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Total value of all orders" />
            </tr></thead>
            <tbody>
              {(sortedRanking || []).slice(0, 20).map((s, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-2">{truncate(s.supplier_name, 25)}</td>
                  <td className="text-right py-2 px-2">{s.supplier_score != null ? Number(s.supplier_score).toFixed(1) : '-'}</td>
                  <td className="text-right py-2 px-2">{formatNumber(s.total_orders)}</td>
                  <td className="text-right py-2 px-2">{formatPercent(s.delivery_rate)}</td>
                  <td className="text-right py-2 px-2">{formatPercent(s.cancel_rate)}</td>
                  <td className="text-right py-2 px-2">{s.avg_delivery_days != null ? Number(s.avg_delivery_days).toFixed(1) : '-'}</td>
                  <td className="text-right py-2 px-2">{formatCurrency(s.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Price Competitiveness & High Cancellation Suppliers */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <div>
              <h3 className="font-semibold">Price Competitiveness</h3>
              <p className="text-xs text-gray-500 mt-0.5">Supplier avg vs market avg price</p>
            </div>
            <span className="text-sm text-gray-500">{(sortedPrice || []).length} suppliers</span>
          </div>
          <div className="overflow-x-auto max-h-96 p-4">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <SortTh label="Supplier" sortKey="supplier_name" onSort={sortPrice} sortIcon={priceIcon} className="text-left py-2 px-2" info="Supplier name" />
                <SortTh label="Supplier Avg" sortKey="avg_supplier_price" onSort={sortPrice} sortIcon={priceIcon} className="text-right py-2 px-2" info="Average price charged by this supplier" />
                <SortTh label="Market Avg" sortKey="avg_market_price" onSort={sortPrice} sortIcon={priceIcon} className="text-right py-2 px-2" info="Average price across all suppliers" />
                <SortTh label="Diff %" sortKey="price_diff_pct" onSort={sortPrice} sortIcon={priceIcon} className="text-right py-2 px-2" info="Price difference = (Supplier - Market) / Market × 100. Negative = below market" />
                <th className="text-center py-2 px-2">Position</th>
              </tr></thead>
              <tbody>
                {(sortedPrice || []).map((s, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2">{truncate(s.supplier_name, 18)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(s.avg_supplier_price)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(s.avg_market_price)}</td>
                    <td className={`text-right py-2 px-2 font-medium ${Number(s.price_diff_pct) < 0 ? 'text-green-600' : Number(s.price_diff_pct) > 0 ? 'text-red-600' : ''}`}>{Number(s.price_diff_pct) > 0 ? '+' : ''}{Number(s.price_diff_pct || 0).toFixed(1)}%</td>
                    <td className="text-center py-2 px-2"><span className={`px-2 py-0.5 rounded text-xs ${s.price_position === 'Below Market' ? 'bg-green-100 text-green-800' : s.price_position === 'Above Market' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>{s.price_position}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <div>
              <h3 className="font-semibold">High Cancellation Suppliers</h3>
              <p className="text-xs text-gray-500 mt-0.5">Suppliers with highest cancellation rates</p>
            </div>
            <span className="text-sm text-gray-500">{(sortedCancellations || []).length} suppliers</span>
          </div>
          <div className="overflow-x-auto max-h-96 p-4">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <SortTh label="Supplier" sortKey="supplier_name" onSort={sortCancel} sortIcon={cancelIcon} className="text-left py-2 px-2" info="Supplier name" />
                <SortTh label="Total" sortKey="total_orders" onSort={sortCancel} sortIcon={cancelIcon} className="text-right py-2 px-2" info="Total part orders" />
                <SortTh label="Cancelled" sortKey="cancelled_count" onSort={sortCancel} sortIcon={cancelIcon} className="text-right py-2 px-2" info="Orders with Cancelled status" />
                <SortTh label="Rate" sortKey="cancel_rate" onSort={sortCancel} sortIcon={cancelIcon} className="text-right py-2 px-2" info="Cancellation rate = Cancelled / Total × 100" />
                <SortTh label="Lost Value" sortKey="cancelled_value" onSort={sortCancel} sortIcon={cancelIcon} className="text-right py-2 px-2" info="Total value of cancelled orders" />
              </tr></thead>
              <tbody>
                {(sortedCancellations || []).map((s, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2">{truncate(s.supplier_name, 20)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(s.total_orders)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(s.cancelled_count)}</td>
                    <td className="text-right py-2 px-2 text-red-600 font-medium">{formatPercent(s.cancel_rate)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(s.cancelled_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Performance Analytics Page
// ============================================================================

const PerformanceAnalyticsPage: React.FC<{ data: PerformanceData | null; loading: boolean }> = ({ data, loading }) => {
  const safeSupplierDel = data?.supplier_delivery ?? [];
  const safePartDel = data?.part_delivery_time ?? [];
  const safeCycleClaim = data?.claim_cycle_time ?? [];
  const safePending = data?.pending_parts ?? [];
  const safeSegment = data?.supplier_segmentation ?? [];
  const safeDeliveryDist = data?.delivery_distribution ?? [];

  const { sortedItems: sortedSupplierDel, requestSort: sortSupDel, getSortIcon: supDelIcon } = useSortableData(safeSupplierDel, { key: 'avg_delivery_days', direction: 'asc' });
  const { sortedItems: sortedPartDel, requestSort: sortPartDel, getSortIcon: partDelIcon } = useSortableData(safePartDel, { key: 'avg_delivery_days', direction: 'desc' });
  const { sortedItems: sortedCycleClaim, requestSort: sortCycle, getSortIcon: cycleIcon } = useSortableData(safeCycleClaim, { key: 'claim_cycle_days', direction: 'desc' });
  const { sortedItems: sortedPending, requestSort: sortPending, getSortIcon: pendingIcon } = useSortableData(safePending, { key: 'days_past_deadline', direction: 'desc' });
  const { sortedItems: sortedSegment, requestSort: sortSegment, getSortIcon: segmentIcon } = useSortableData(safeSegment, { key: 'total_orders', direction: 'desc' });

  if (loading || !data) return <Loading />;

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard label="Avg Delivery Days" value={formatDays(data.kpis?.avg_delivery_days)} color="text-blue-600" info="Mean number of days from order placement to delivery." />
        <StatCard label="On-Time Rate" value={formatPercent(data.kpis?.on_time_rate)} color={(data.kpis?.on_time_rate ?? 0) >= 80 ? 'text-green-600' : 'text-yellow-600'} info="Percentage of orders delivered by or before their deadline date." />
        <StatCard label="Auto-Assign Rate" value={formatPercent(data.kpis?.auto_assign_rate)} color="text-purple-600" info="Percentage of orders where supplier was automatically assigned." />
        <StatCard label="Auto-Quote Rate" value={formatPercent(data.kpis?.auto_quote_rate)} color="text-purple-600" info="Percentage of orders where price quote was automatically generated." />
        <StatCard label="On-Time Orders" value={formatNumber(data.kpis?.on_time_orders)} color="text-green-600" info="Count of orders delivered on or before deadline." />
        <StatCard label="Late Orders" value={formatNumber(data.kpis?.late_orders)} color="text-red-600" info="Count of orders delivered after deadline." />
      </section>

      <div className="bg-white rounded-lg shadow p-4">
        <SectionTitle info="Distribution of orders by delivery time buckets.">Delivery Time Distribution</SectionTitle>
        {safeDeliveryDist.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={safeDeliveryDist}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="delivery_bucket" />
                <YAxis />
                <Tooltip formatter={(v: number) => formatNumber(v)} />
                <Bar dataKey="order_count" fill="#3b82f6" name="Orders" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="text-gray-500 text-center py-8">No data</p>}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Delivery speed by supplier.">Supplier Delivery Performance</SectionTitle>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <SortTh label="Supplier" sortKey="supplier_name" onSort={sortSupDel} sortIcon={supDelIcon} className="text-left py-2 px-2" />
                <SortTh label="Orders" sortKey="delivered_orders" onSort={sortSupDel} sortIcon={supDelIcon} className="text-right py-2 px-2" />
                <SortTh label="Avg Days" sortKey="avg_delivery_days" onSort={sortSupDel} sortIcon={supDelIcon} className="text-right py-2 px-2" />
                <SortTh label="Same Day" sortKey="same_day" onSort={sortSupDel} sortIcon={supDelIcon} className="text-right py-2 px-2" />
                <SortTh label="On-Time %" sortKey="on_time_rate" onSort={sortSupDel} sortIcon={supDelIcon} className="text-right py-2 px-2" />
              </tr></thead>
              <tbody>
                {(sortedSupplierDel || []).map((s, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2">{truncate(s.supplier_name, 18)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(s.delivered_orders)}</td>
                    <td className="text-right py-2 px-2 font-medium">{formatDays(s.avg_delivery_days)}</td>
                    <td className="text-right py-2 px-2 text-green-600">{formatNumber(s.same_day)}</td>
                    <td className="text-right py-2 px-2">{formatPercent(s.on_time_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Parts with longest average delivery times.">Slowest Parts to Deliver</SectionTitle>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <SortTh label="Part" sortKey="part_name" onSort={sortPartDel} sortIcon={partDelIcon} className="text-left py-2 px-2" />
                <SortTh label="Orders" sortKey="total_orders" onSort={sortPartDel} sortIcon={partDelIcon} className="text-right py-2 px-2" />
                <SortTh label="Avg Days" sortKey="avg_delivery_days" onSort={sortPartDel} sortIcon={partDelIcon} className="text-right py-2 px-2" />
                <SortTh label="Delayed" sortKey="delayed_count" onSort={sortPartDel} sortIcon={partDelIcon} className="text-right py-2 px-2" />
                <SortTh label="Delay %" sortKey="delay_rate" onSort={sortPartDel} sortIcon={partDelIcon} className="text-right py-2 px-2" />
              </tr></thead>
              <tbody>
                {(sortedPartDel || []).map((p, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2" title={p.part_name}>{truncate(p.part_name, 20)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(p.total_orders)}</td>
                    <td className="text-right py-2 px-2 font-medium text-orange-600">{formatDays(p.avg_delivery_days)}</td>
                    <td className="text-right py-2 px-2 text-red-600">{formatNumber(p.delayed_count)}</td>
                    <td className="text-right py-2 px-2">{formatPercent(p.delay_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <SectionTitle info="Time from first order to final delivery per claim.">Claim Cycle Time</SectionTitle>
        <div className="overflow-x-auto max-h-96">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-white"><tr className="border-b">
              <SortTh label="Claim" sortKey="claim_number" onSort={sortCycle} sortIcon={cycleIcon} className="text-left py-2 px-2" />
              <SortTh label="Insurer" sortKey="insurer_name" onSort={sortCycle} sortIcon={cycleIcon} className="text-left py-2 px-2" />
              <SortTh label="Parts" sortKey="total_parts" onSort={sortCycle} sortIcon={cycleIcon} className="text-right py-2 px-2" />
              <SortTh label="Cycle Days" sortKey="claim_cycle_days" onSort={sortCycle} sortIcon={cycleIcon} className="text-right py-2 px-2" />
              <SortTh label="Value" sortKey="total_value" onSort={sortCycle} sortIcon={cycleIcon} className="text-right py-2 px-2" />
              <th className="text-center py-2 px-2">Status</th>
            </tr></thead>
            <tbody>
              {(sortedCycleClaim || []).slice(0, 15).map((c, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-2">{truncate(c.claim_number, 15)}</td>
                  <td className="py-2 px-2">{truncate(c.insurer_name, 15)}</td>
                  <td className="text-right py-2 px-2">{formatNumber(c.total_parts)}</td>
                  <td className="text-right py-2 px-2 font-medium">{c.claim_cycle_days ?? '-'}</td>
                  <td className="text-right py-2 px-2">{formatCurrency(c.total_value)}</td>
                  <td className="text-center py-2 px-2"><span className={`px-2 py-0.5 rounded text-xs ${c.status === 'Complete' ? 'bg-green-100 text-green-800' : c.status === 'Partial' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{c.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <SectionTitle info="Orders not yet delivered that have passed their deadline.">Pending Parts - Past Deadline</SectionTitle>
        <div className="overflow-x-auto max-h-96">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-white"><tr className="border-b">
              <SortTh label="Claim" sortKey="claim_number" onSort={sortPending} sortIcon={pendingIcon} className="text-left py-2 px-2" />
              <SortTh label="Part" sortKey="part_name" onSort={sortPending} sortIcon={pendingIcon} className="text-left py-2 px-2" />
              <SortTh label="Supplier" sortKey="supplier_name" onSort={sortPending} sortIcon={pendingIcon} className="text-left py-2 px-2" />
              <SortTh label="Deadline" sortKey="deadline_date" onSort={sortPending} sortIcon={pendingIcon} className="text-center py-2 px-2" />
              <SortTh label="Days Late" sortKey="days_past_deadline" onSort={sortPending} sortIcon={pendingIcon} className="text-right py-2 px-2" />
              <SortTh label="Value" sortKey="value" onSort={sortPending} sortIcon={pendingIcon} className="text-right py-2 px-2" />
            </tr></thead>
            <tbody>
              {(sortedPending || []).map((p, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-2">{truncate(p.claim_number, 12)}</td>
                  <td className="py-2 px-2" title={p.part_name}>{truncate(p.part_name, 18)}</td>
                  <td className="py-2 px-2">{truncate(p.supplier_name, 15)}</td>
                  <td className="text-center py-2 px-2 text-xs">{formatDateOnly(p.deadline_date)}</td>
                  <td className={`text-right py-2 px-2 font-medium ${(p.days_past_deadline ?? 0) > 0 ? 'text-red-600' : ''}`}>{p.days_past_deadline ?? '-'}</td>
                  <td className="text-right py-2 px-2">{formatCurrency(p.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.suppliers_per_claim && (
        <div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Statistics on how many different suppliers are used per claim.">Suppliers per Claim</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded"><div className="text-2xl font-bold text-blue-600">{Number(data.suppliers_per_claim.avg_suppliers_per_claim || 0).toFixed(2)}</div><div className="text-xs text-gray-600">Avg Suppliers</div></div>
            <div className="text-center p-3 bg-gray-50 rounded"><div className="text-xl font-bold">{data.suppliers_per_claim.min_suppliers} - {data.suppliers_per_claim.max_suppliers}</div><div className="text-xs text-gray-600">Min - Max</div></div>
            <div className="text-center p-3 bg-green-50 rounded"><div className="text-xl font-bold text-green-600">{formatNumber(data.suppliers_per_claim.single_supplier_claims)}</div><div className="text-xs text-gray-600">Single Supplier</div></div>
            <div className="text-center p-3 bg-yellow-50 rounded"><div className="text-xl font-bold text-yellow-600">{formatNumber(data.suppliers_per_claim.two_supplier_claims)}</div><div className="text-xs text-gray-600">Two Suppliers</div></div>
            <div className="text-center p-3 bg-orange-50 rounded"><div className="text-xl font-bold text-orange-600">{formatNumber(data.suppliers_per_claim.multi_supplier_claims)}</div><div className="text-xs text-gray-600">3+ Suppliers</div></div>
            <div className="text-center p-3 bg-purple-50 rounded col-span-2"><div className="text-xl font-bold text-purple-600">{formatNumber(data.suppliers_per_claim.total_claims)}</div><div className="text-xs text-gray-600">Total Claims</div></div>
          </div>
        </div>
      )}

      {safeSegment.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <SectionTitle info="Supplier segmentation by performance, value, and reach.">Supplier Segmentation</SectionTitle>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <SortTh label="Supplier" sortKey="supplier_name" onSort={sortSegment} sortIcon={segmentIcon} className="text-left py-2 px-2" />
                <SortTh label="Orders" sortKey="total_orders" onSort={sortSegment} sortIcon={segmentIcon} className="text-right py-2 px-2" />
                <SortTh label="Claims" sortKey="claims_served" onSort={sortSegment} sortIcon={segmentIcon} className="text-right py-2 px-2" />
                <SortTh label="Delivery %" sortKey="delivery_rate" onSort={sortSegment} sortIcon={segmentIcon} className="text-right py-2 px-2" />
                <SortTh label="Value" sortKey="total_value" onSort={sortSegment} sortIcon={segmentIcon} className="text-right py-2 px-2" />
                <th className="text-center py-2 px-2">Performance</th>
                <th className="text-center py-2 px-2">Value Tier</th>
                <th className="text-center py-2 px-2">Reach</th>
              </tr></thead>
              <tbody>
                {(sortedSegment || []).map((s, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2 font-medium">{truncate(s.supplier_name, 20)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(s.total_orders)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(s.claims_served)}</td>
                    <td className="text-right py-2 px-2">{formatPercent(s.delivery_rate)}</td>
                    <td className="text-right py-2 px-2">{formatCurrency(s.total_value)}</td>
                    <td className="text-center py-2 px-2"><span className={`px-2 py-0.5 rounded text-xs ${s.performance_tier === 'Premium' ? 'bg-green-100 text-green-800' : s.performance_tier === 'Reliable' ? 'bg-blue-100 text-blue-800' : s.performance_tier === 'Standard' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}`}>{s.performance_tier}</span></td>
                    <td className="text-center py-2 px-2"><span className={`px-2 py-0.5 rounded text-xs ${s.value_tier === 'High Value' ? 'bg-purple-100 text-purple-800' : s.value_tier === 'Medium Value' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'}`}>{s.value_tier}</span></td>
                    <td className="text-center py-2 px-2"><span className={`px-2 py-0.5 rounded text-xs ${s.reach_tier === 'Broad' ? 'bg-teal-100 text-teal-800' : s.reach_tier === 'Moderate' ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-800'}`}>{s.reach_tier}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Workshops Analytics Page
// ============================================================================
const WorkshopsAnalyticsPage: React.FC<{ data: WorkshopsData | null; loading: boolean }> = ({ data, loading }) => {
  const safeRanking = data?.ranking ?? [];
  const safeDelivery = data?.delivery_performance ?? [];
  const safeMonthly = data?.monthly_trend ?? [];
  const safeByInsurer = data?.by_insurer ?? [];

  const { sortedItems: sortedRanking, requestSort: sortRank, getSortIcon: rankIcon } = useSortableData(safeRanking, { key: 'total_orders', direction: 'desc' });
  const { sortedItems: sortedDelivery, requestSort: sortDel, getSortIcon: delIcon } = useSortableData(safeDelivery, { key: 'on_time_rate', direction: 'desc' });

  if (loading || !data) return <Loading />;

  const kpis = data.kpis || { total_workshops: 0, total_claims: 0, total_orders: 0, total_value: 0, overall_delivery_rate: 0, overall_cancel_rate: 0 };

  // Aggregate monthly trend by month for the chart
  const monthlyAgg = safeMonthly.reduce<Record<string, { order_count: number; total_value: number }>>((acc, row) => {
    if (!acc[row.month]) acc[row.month] = { order_count: 0, total_value: 0 };
    acc[row.month].order_count += row.order_count;
    acc[row.month].total_value += row.total_value;
    return acc;
  }, {});
  const monthlyChartData = Object.entries(monthlyAgg).map(([month, vals]) => ({ month, ...vals })).sort((a, b) => a.month.localeCompare(b.month));

  // Top 10 by volume from ranking
  const top10 = safeRanking.slice(0, 10);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <section className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Total Workshops</div>
          <div className="text-3xl font-bold">{formatNumber(kpis.total_workshops)}</div>
          <div className="text-xs opacity-70 mt-1">Unique repair shops</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Total Orders</div>
          <div className="text-3xl font-bold">{formatNumber(kpis.total_orders)}</div>
          <div className="text-xs opacity-70 mt-1">Parts ordered</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Total Claims</div>
          <div className="text-3xl font-bold">{formatNumber(kpis.total_claims)}</div>
          <div className="text-xs opacity-70 mt-1">Claims processed</div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Total Value</div>
          <div className="text-3xl font-bold">{formatCurrency(kpis.total_value)}</div>
          <div className="text-xs opacity-70 mt-1">Procurement value</div>
        </div>
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Delivery Rate</div>
          <div className="text-3xl font-bold">{(kpis.overall_delivery_rate ?? 0).toFixed(1)}%</div>
          <div className="text-xs opacity-70 mt-1">Parts delivered on time</div>
        </div>
        <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg shadow-lg p-4 text-white">
          <div className="text-sm opacity-80">Cancel Rate</div>
          <div className="text-3xl font-bold">{(kpis.overall_cancel_rate ?? 0).toFixed(1)}%</div>
          <div className="text-xs opacity-70 mt-1">Orders cancelled</div>
        </div>
      </section>

      {/* Top 10 by Volume */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h3 className="font-semibold">Top Workshops by Volume</h3>
            <p className="text-xs text-gray-500 mt-0.5">Top 10 by order count</p>
          </div>
          <span className="text-sm text-gray-500">{top10.length} workshops</span>
        </div>
        <div className="p-4">
          {top10.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top10} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="workshop_name" width={150} tick={{ fontSize: 9 }} tickFormatter={(v: string) => v.length > 25 ? v.slice(0, 25) + '...' : v} />
                  <Tooltip formatter={(v: number, name: string) => name === 'total_value' ? formatCurrency(v) : formatNumber(v)} />
                  <Legend />
                  <Bar dataKey="total_orders" fill="#3b82f6" name="Orders" />
                  <Bar dataKey="total_claims" fill="#8b5cf6" name="Claims" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-gray-500 text-center py-8">No data</p>}
        </div>
      </div>

      {/* Monthly Trend + Delivery Performance */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Monthly Order Trend</h3>
            <p className="text-xs text-gray-500 mt-0.5">Order volume and value over time</p>
          </div>
          <div className="p-4">
            {monthlyChartData.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: number, name: string) => name === 'total_value' ? formatCurrency(v) : formatNumber(v)} />
                    <Legend />
                    <Bar dataKey="order_count" fill="#3b82f6" name="Orders" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <p className="text-gray-500 text-center py-8">No trend data</p>}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Delivery Performance</h3>
            <p className="text-xs text-gray-500 mt-0.5">On-time rate by workshop</p>
          </div>
          <div className="p-4">
            {safeDelivery.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={safeDelivery.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
                    <YAxis type="category" dataKey="workshop_name" width={130} tick={{ fontSize: 9 }} tickFormatter={(v: string) => v.length > 22 ? v.slice(0, 22) + '...' : v} />
                    <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                    <Bar dataKey="on_time_rate" fill="#22c55e" name="On-Time Rate %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <p className="text-gray-500 text-center py-8">No delivery data</p>}
          </div>
        </div>
      </div>

      {/* Workshop Ranking Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h3 className="font-semibold">Workshop Ranking</h3>
            <p className="text-xs text-gray-500 mt-0.5">All workshops with performance metrics</p>
          </div>
          <span className="text-sm text-gray-500">{(sortedRanking || []).length} workshops</span>
        </div>
        <div className="overflow-x-auto max-h-96 p-4">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-white"><tr className="border-b">
              <SortTh label="Workshop" sortKey="workshop_name" onSort={sortRank} sortIcon={rankIcon} className="text-left py-2 px-2" info="Workshop name" />
              <SortTh label="Location" sortKey="workshop_location" onSort={sortRank} sortIcon={rankIcon} className="text-left py-2 px-2" info="City/State" />
              <SortTh label="Orders" sortKey="total_orders" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Total part orders" />
              <SortTh label="Claims" sortKey="total_claims" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Total claims" />
              <SortTh label="Value" sortKey="total_value" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Total procurement value" />
              <SortTh label="Delivery %" sortKey="delivery_rate" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Delivery rate" />
              <SortTh label="Cancel %" sortKey="cancel_rate" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Cancellation rate" />
              <SortTh label="Parts/Claim" sortKey="avg_parts_per_claim" onSort={sortRank} sortIcon={rankIcon} className="text-right py-2 px-2" info="Avg parts per claim" />
            </tr></thead>
            <tbody>
              {(sortedRanking || []).map((w, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-2" title={w.workshop_name}>{truncate(w.workshop_name, 30)}</td>
                  <td className="py-2 px-2">{truncate(w.workshop_location, 20)}</td>
                  <td className="text-right py-2 px-2">{formatNumber(w.total_orders)}</td>
                  <td className="text-right py-2 px-2">{formatNumber(w.total_claims)}</td>
                  <td className="text-right py-2 px-2 text-green-600 font-medium">{formatCurrency(w.total_value)}</td>
                  <td className="text-right py-2 px-2 text-teal-600">{(w.delivery_rate ?? 0).toFixed(1)}%</td>
                  <td className="text-right py-2 px-2 text-red-600">{(w.cancel_rate ?? 0).toFixed(1)}%</td>
                  <td className="text-right py-2 px-2">{(w.avg_parts_per_claim ?? 0).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delivery Performance Table */}
      {safeDelivery.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <div>
              <h3 className="font-semibold">Delivery Performance Detail</h3>
              <p className="text-xs text-gray-500 mt-0.5">On-time vs late delivery breakdown</p>
            </div>
            <span className="text-sm text-gray-500">{safeDelivery.length} workshops</span>
          </div>
          <div className="overflow-x-auto max-h-80 p-4">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <SortTh label="Workshop" sortKey="workshop_name" onSort={sortDel} sortIcon={delIcon} className="text-left py-2 px-2" info="Workshop name" />
                <SortTh label="Orders" sortKey="total_orders" onSort={sortDel} sortIcon={delIcon} className="text-right py-2 px-2" info="Total orders" />
                <SortTh label="Avg Days" sortKey="avg_delivery_days" onSort={sortDel} sortIcon={delIcon} className="text-right py-2 px-2" info="Average delivery days" />
                <SortTh label="On-Time" sortKey="on_time_count" onSort={sortDel} sortIcon={delIcon} className="text-right py-2 px-2" info="On-time count" />
                <SortTh label="Late" sortKey="late_count" onSort={sortDel} sortIcon={delIcon} className="text-right py-2 px-2" info="Late count" />
                <SortTh label="On-Time %" sortKey="on_time_rate" onSort={sortDel} sortIcon={delIcon} className="text-right py-2 px-2" info="On-time rate" />
              </tr></thead>
              <tbody>
                {(sortedDelivery || []).map((d, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2" title={d.workshop_name}>{truncate(d.workshop_name, 30)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(d.total_orders)}</td>
                    <td className="text-right py-2 px-2">{(d.avg_delivery_days ?? 0).toFixed(1)}</td>
                    <td className="text-right py-2 px-2 text-teal-600">{formatNumber(d.on_time_count)}</td>
                    <td className="text-right py-2 px-2 text-red-600">{formatNumber(d.late_count)}</td>
                    <td className="text-right py-2 px-2 font-medium">{(d.on_time_rate ?? 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Claims Analytics Page
// ============================================================================

const ClaimsPage: React.FC<{ selectedInsurer: number | null; dateRange: DateRange }> = ({ selectedInsurer, dateRange }) => {
  const { user } = useAuth();
  const [revokedWidgets, setRevokedWidgets] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!user?.user_key) return;
    authFetch(`/api/users/widget-permissions?user_key=${user.user_key}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(res => {
        const revoked = new Set(
          (res?.data?.permissions ?? [])
            .filter((p: any) => !p.is_visible)
            .map((p: any) => p.widget_key as string)
        );
        setRevokedWidgets(revoked);
      })
      .catch(() => {});
  }, [user?.user_key]);
  const isWidgetVisible = (key: string) => !revokedWidgets.has(key);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ClaimSearchResult[]>([]);
  const [selectedClaim, setSelectedClaim] = useState<ClaimDetail | null>(null);
  const [latestClaims, setLatestClaims] = useState<LatestClaim[]>([]);
  const [claimsSummary, setClaimsSummary] = useState<ClaimsStatusSummary>({ total_claims: 0, complete_claims: 0, partial_claims: 0, pending_claims: 0 });
  const [locations, setLocations] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [locationFilter, setLocationFilter] = useState('All');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('last_order_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Hardcoded claim-level statuses (not part-level)
  const claimStatuses = ['All', 'Complete', 'Partial', 'Pending'];

  const buildParams = () => {
    const params = new URLSearchParams();
    if (selectedInsurer) params.append('insurer', selectedInsurer.toString());
    if (dateRange.startDate) params.append('startDate', dateRange.startDate);
    if (dateRange.endDate) params.append('endDate', dateRange.endDate);
    return params;
  };

  const fetchFilters = async () => {
    try {
      const locRes = await authFetch('/api/filters/workshops');
      const locJson = await locRes.json();
      if (locJson.success) setLocations(['All', ...locJson.data]);
    } catch (e) { console.error('Failed to fetch filters', e); }
  };

  const fetchLatestClaims = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      if (statusFilter !== 'All') params.append('status', statusFilter);
      if (locationFilter !== 'All') params.append('location', locationFilter);
      const res = await authFetch(`/api/claims/latest?${params}`);
      const json: ApiResponse<LatestClaimsResponse> = await res.json();
      if (json.success && json.data) {
        setLatestClaims(json.data.claims);
        setClaimsSummary(json.data.summary);
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedClaims = [...latestClaims].sort((a, b) => {
    let aVal: any, bVal: any;
    switch (sortColumn) {
      case 'total_parts': aVal = Number(a.total_parts) || 0; bVal = Number(b.total_parts) || 0; break;
      case 'supplier_count': aVal = Number(a.supplier_count) || 0; bVal = Number(b.supplier_count) || 0; break;
      case 'delivered': aVal = Number(a.delivered) || 0; bVal = Number(b.delivered) || 0; break;
      case 'pending': aVal = Number(a.pending) || 0; bVal = Number(b.pending) || 0; break;
      case 'cancelled': aVal = Number(a.cancelled) || 0; bVal = Number(b.cancelled) || 0; break;
      case 'last_order_date': aVal = a.last_order_date || ''; bVal = b.last_order_date || ''; break;
      default: return 0;
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortHeader: React.FC<{ column: string; label: string; align?: string }> = ({ column, label, align = 'right' }) => (
    <th 
      className={`px-4 py-2 text-${align} cursor-pointer hover:bg-gray-100 select-none`}
      onClick={() => handleSort(column)}
    >
      {label} {sortColumn === column ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  useEffect(() => { fetchFilters(); }, []);
  useEffect(() => { fetchLatestClaims(); }, [statusFilter, locationFilter, selectedInsurer, dateRange]);

  const searchClaims = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      params.append('q', searchQuery);
      const res = await authFetch(`/api/claims/search?${params}`);
      const json: ApiResponse<ClaimSearchResult[]> = await res.json();
      if (json.success && json.data) {
        setSearchResults(json.data);
        setSelectedClaim(null);
      } else {
        setError(json.error || 'Search failed');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadClaimDetail = async (claimKey: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/api/claims/${claimKey}`);
      const json: ApiResponse<ClaimDetail> = await res.json();
      if (json.success && json.data && json.data.header) {
        setSelectedClaim(json.data);
      } else {
        setError(json.error || 'Failed to load claim - invalid response structure');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (cat: string) => {
    if (cat === 'Complete') return 'bg-green-100 text-green-800';
    if (cat === 'Cancelled') return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold mb-3">Claim Search</h2>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchClaims()}
            placeholder="Search by claim number..."
            className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={searchClaims}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setSearchResults([]); }}
              className="px-3 py-1 border rounded text-sm"
            >
              {claimStatuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Location:</label>
            <select
              value={locationFilter}
              onChange={(e) => { setLocationFilter(e.target.value); setSearchResults([]); }}
              className="px-3 py-1 border rounded text-sm"
            >
              {locations.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
      </div>

      {searchResults.length === 0 && !selectedClaim && latestClaims.length > 0 && (
        <div className="space-y-4">
          {/* Claims Summary Stats */}
          {isWidgetVisible('claims_kpi_total') && (<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white">
              <div className="text-sm opacity-80">Total Claims</div>
              <div className="text-3xl font-bold">{formatNumber(claimsSummary.total_claims)}</div>
              <div className="text-xs opacity-70 mt-1">All claims in period</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-lg p-4 text-white">
              <div className="text-sm opacity-80">Complete</div>
              <div className="text-3xl font-bold">{formatNumber(claimsSummary.complete_claims)}</div>
              <div className="text-xs opacity-70 mt-1">All parts delivered</div>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-4 text-white">
              <div className="text-sm opacity-80">Partial</div>
              <div className="text-3xl font-bold">{formatNumber(claimsSummary.partial_claims)}</div>
              <div className="text-xs opacity-70 mt-1">Some parts pending</div>
            </div>
            <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg shadow-lg p-4 text-white">
              <div className="text-sm opacity-80">Pending</div>
              <div className="text-3xl font-bold">{formatNumber(claimsSummary.pending_claims)}</div>
              <div className="text-xs opacity-70 mt-1">No parts delivered</div>
            </div>
          </div>)}

          {isWidgetVisible('claims_table_results') && (<div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">Latest Claims</h3>
              <span className="text-sm text-gray-500">{latestClaims.length} claims</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Claim #</th>
                    <th className="px-4 py-2 text-left">Location</th>
                    <SortHeader column="total_parts" label="Parts" />
                    <SortHeader column="supplier_count" label="Suppliers" />
                    <SortHeader column="delivered" label="Delivered" />
                    <SortHeader column="pending" label="Pending" />
                    <SortHeader column="cancelled" label="Cancelled" />
                    <SortHeader column="last_order_date" label="Last Order" align="left" />
                    <th className="px-4 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(sortedClaims || []).map((claim) => (
                    <tr key={claim.claim_key} onClick={() => loadClaimDetail(claim.claim_key)} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-4 py-2 font-medium text-blue-600">{claim.claim_number}</td>
                      <td className="px-4 py-2 text-gray-600">{truncate(claim.location, 20)}</td>
                      <td className="px-4 py-2 text-right">{claim.total_parts}</td>
                      <td className="px-4 py-2 text-right text-purple-600">{claim.supplier_count}</td>
                      <td className="px-4 py-2 text-right text-green-600">{claim.delivered}</td>
                      <td className="px-4 py-2 text-right text-yellow-600">{claim.pending}</td>
                      <td className="px-4 py-2 text-right text-red-600">{claim.cancelled}</td>
                      <td className="px-4 py-2 text-gray-500">{formatDateOnly(claim.last_order_date)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          claim.claim_status === 'Complete' ? 'bg-green-100 text-green-800' :
                          claim.claim_status === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>{claim.claim_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>)}
        </div>
      )}

      {searchResults.length > 0 && !selectedClaim && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold">Search Results</h3>
            <span className="text-sm text-gray-500">{searchResults.length} claims found</span>
          </div>
          <div className="divide-y max-h-96 overflow-auto">
            {searchResults.map((claim) => (
              <div
                key={claim.claim_key}
                onClick={() => loadClaimDetail(claim.claim_key)}
                className="p-4 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-blue-600">{claim.claim_number}</div>
                    <div className="text-sm text-gray-500">{claim.insurer_name} • {claim.workshop_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatCurrency(claim.total_value)}</div>
                    <div className="text-sm text-gray-500">{claim.total_parts} parts</div>
                  </div>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {formatDateOnly(claim.first_order_date)} - {formatDateOnly(claim.last_order_date)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedClaim && selectedClaim.header && (
        <div className="space-y-4">
          <button
            onClick={() => setSelectedClaim(null)}
            className="text-sm text-blue-600 hover:underline"
          >
            ← Back to results
          </button>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-blue-600">{selectedClaim.header.claim_number}</h2>
                <div className="text-sm text-gray-500 mt-1">{selectedClaim.header.insurer_name}</div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{formatCurrency(selectedClaim.header.total_value)}</div>
                <div className="text-sm text-gray-500">{selectedClaim.header.total_parts} parts • {selectedClaim.header.supplier_count || '-'} suppliers</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-gray-50 p-3 rounded-lg">
              <div><span className="text-gray-500 block text-xs">Workshop</span> <span className="font-medium">{selectedClaim.header.workshop_name}</span></div>
              <div><span className="text-gray-500 block text-xs">Location</span> <span className="font-medium">{selectedClaim.header.workshop_location}</span></div>
              <div><span className="text-gray-500 block text-xs">Vehicle</span> <span className="font-medium">{selectedClaim.header.vehicle_make} {selectedClaim.header.vehicle_model} {selectedClaim.header.vehicle_year || ''}</span></div>
              <div><span className="text-gray-500 block text-xs">VIN</span> <span className="font-medium font-mono text-xs">{selectedClaim.header.vin || '-'}</span></div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-lg p-4 text-white text-center">
                <div className="text-3xl font-bold">{selectedClaim.header.delivered_parts}</div>
                <div className="text-sm opacity-80">Delivered</div>
              </div>
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-4 text-white text-center">
                <div className="text-3xl font-bold">{selectedClaim.header.pending_parts}</div>
                <div className="text-sm opacity-80">Pending</div>
              </div>
              <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg shadow-lg p-4 text-white text-center">
                <div className="text-3xl font-bold">{selectedClaim.header.cancelled_parts}</div>
                <div className="text-sm opacity-80">Cancelled</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold">Status Breakdown</h3>
                <span className="text-xs text-gray-400">{(selectedClaim.status_breakdown || []).length} statuses</span>
              </div>
              <div className="space-y-2">
                {(selectedClaim.status_breakdown || []).map((s, i) => (
                  <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(s.status_category)}`}>{s.status}</span>
                    <div className="text-right">
                      <span className="font-medium">{s.count}</span>
                      <span className="text-gray-400 ml-2">{formatCurrency(s.value)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold">Suppliers</h3>
                <span className="text-xs text-gray-400">{(selectedClaim.suppliers || []).length} suppliers</span>
              </div>
              <div className="space-y-2">
                {(selectedClaim.suppliers || []).map((s, i) => (
                  <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{truncate(s.supplier_name, 25)}</span>
                    <div className="text-right text-sm">
                      <span className="text-blue-600 font-medium">{s.parts} parts</span>
                      <span className="text-gray-400 ml-2">{formatPercent(s.delivery_rate)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {isWidgetVisible('claims_table_parts') && (<div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">Parts</h3>
              <span className="text-sm text-gray-500">{(selectedClaim.parts || []).length} items</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Part</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-left">Supplier</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-right">Price</th>
                    <th className="px-4 py-2 text-left">Order Date</th>
                    <th className="px-4 py-2 text-left">Delivery</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(selectedClaim.parts || []).map((part) => (
                    <tr key={part.part_order_key} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <div className="font-medium">{truncate(part.part_name || 'Unknown Part', 30)}</div>
                        <div className="text-xs text-gray-400">{part.part_number || '-'}</div>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{truncate(part.part_type || '-', 15)}</td>
                      <td className="px-4 py-2 text-gray-600">{truncate(part.supplier_name, 20)}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(part.status_category)}`}>{part.status}</span>
                      </td>
                      <td className="px-4 py-2 text-right">{formatCurrency(part.price)}</td>
                      <td className="px-4 py-2 text-gray-600">{formatDateOnly(part.order_date)}</td>
                      <td className="px-4 py-2 text-gray-600">
                        {formatDateOnly(part.delivery_date)}
                        {part.delivery_days != null && <span className="text-xs text-gray-400 ml-1">({part.delivery_days}d)</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>)}
        </div>
      )}
    </div>
  );
};



// ============================================================================
// Orders Analytics Page
// ============================================================================

const OrdersPage: React.FC<{ selectedInsurer: number | null; dateRange: DateRange }> = ({ selectedInsurer, dateRange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<OrderSearchResult[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [latestOrders, setLatestOrders] = useState<LatestOrder[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [locationFilter, setLocationFilter] = useState('All');
  const [loading, setLoading] = useState(false);
  const [ordersSummary, setOrdersSummary] = useState<any>(null);
  const [statusBreakdown, setStatusBreakdown] = useState<{status: string; count: number}[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { user: ordersUser } = useAuth();
  const [ordersRevokedWidgets, setOrdersRevokedWidgets] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!ordersUser?.user_key) return;
    authFetch(`/api/users/widget-permissions?user_key=${ordersUser.user_key}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(res => {
        const revoked = new Set(
          (res?.data?.permissions ?? [])
            .filter((p: any) => !p.is_visible)
            .map((p: any) => p.widget_key as string)
        );
        setOrdersRevokedWidgets(revoked);
      })
      .catch(() => {});
  }, [ordersUser?.user_key]);
  const isOrdersWidgetVisible = (key: string) => !ordersRevokedWidgets.has(key);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (selectedInsurer) params.append('insurer', selectedInsurer.toString());
    if (dateRange.startDate) params.append('startDate', dateRange.startDate);
    if (dateRange.endDate) params.append('endDate', dateRange.endDate);
    return params;
  };

  const fetchFilters = async () => {
    try {
      const [locRes, statusRes] = await Promise.all([
        authFetch('/api/filters/workshops'),
        authFetch('/api/filters/statuses')
      ]);
      const locJson = await locRes.json();
      const statusJson = await statusRes.json();
      if (locJson.success) setLocations(['All', ...locJson.data]);
      if (statusJson.success) setStatuses(['All', ...statusJson.data]);
    } catch (e) { console.error('Failed to fetch filters', e); }
  };

  const fetchLatestOrders = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      if (statusFilter !== 'All') params.append('status', statusFilter);
      if (locationFilter !== 'All') params.append('location', locationFilter);
      const res = await authFetch(`/api/orders/latest?${params}`);
      const json: ApiResponse<{ orders: LatestOrder[]; summary: any; status_breakdown?: any[] }> = await res.json();
      if (json.success && json.data) {
        setLatestOrders(json.data.orders);
        setOrdersSummary(json.data.summary);
        setStatusBreakdown(json.data.status_breakdown || []);
      }
      if (json.success && json.data?.orders) setLatestOrders(json.data.orders);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFilters(); }, []);
  useEffect(() => { fetchLatestOrders(); }, [statusFilter, locationFilter, selectedInsurer, dateRange]);

  const searchOrders = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      params.append('q', searchQuery);
      const res = await authFetch(`/api/orders/search?${params}`);
      const json: ApiResponse<OrderSearchResult[]> = await res.json();
      if (json.success && json.data) {
        setSearchResults(json.data);
        setSelectedOrder(null);
      } else {
        setError(json.error || 'Search failed');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOrderDetail = async (orderKey: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/api/orders/${orderKey}`);
      const json: ApiResponse<OrderDetail> = await res.json();
      if (json.success && json.data) {
        setSelectedOrder(json.data);
      } else {
        setError(json.error || 'Failed to load order');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold mb-3">Order Search</h2>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchOrders()}
            placeholder="Search by claim number or part number..."
            className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={searchOrders}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setSearchResults([]); }}
              className="px-3 py-1 border rounded text-sm"
            >
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Location:</label>
            <select
              value={locationFilter}
              onChange={(e) => { setLocationFilter(e.target.value); setSearchResults([]); }}
              className="px-3 py-1 border rounded text-sm"
            >
              {locations.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
      </div>

{searchResults.length === 0 && !selectedOrder && latestOrders.length > 0 && (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {isOrdersWidgetVisible('orders_kpi_total_orders') && (<div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white">
        <div className="text-sm opacity-80">Total Orders</div>
        <div className="text-3xl font-bold">{formatNumber(ordersSummary?.total_orders || latestOrders.length)}</div>
        <div className="text-xs opacity-70 mt-1">All time period</div>
      </div>)}
      {isOrdersWidgetVisible('orders_kpi_total_parts') && (<div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-lg p-4 text-white">
        <div className="text-sm opacity-80">Total Parts</div>
        <div className="text-3xl font-bold">{formatNumber(ordersSummary?.total_parts || 0)}</div>
        <div className="text-xs opacity-70 mt-1">Unique parts</div>
      </div>)}
      {isOrdersWidgetVisible('orders_kpi_total_suppliers') && (<div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-4 text-white">
        <div className="text-sm opacity-80">Total Suppliers</div>
        <div className="text-3xl font-bold">{formatNumber(ordersSummary?.total_suppliers || 0)}</div>
        <div className="text-xs opacity-70 mt-1">Active suppliers</div>
      </div>)}
      {isOrdersWidgetVisible('orders_kpi_avg_parts') && (<div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-4 text-white">
        <div className="text-sm opacity-80">Avg Parts/Order</div>
        <div className="text-3xl font-bold">{(ordersSummary?.avg_parts_per_order || 0).toFixed(1)}</div>
        <div className="text-xs opacity-70 mt-1">Per claim average</div>
      </div>)}
    </div>

    {statusBreakdown.length > 0 && isOrdersWidgetVisible('orders_chart_status_breakdown') && (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold mb-3">Status Breakdown</h3>
        <div className="flex flex-wrap gap-2">
          {statusBreakdown.map(s => {
            const statusLower = s.status.toLowerCase();
            let colorClass = 'bg-gray-100 text-gray-700 border-gray-300';
            if (statusLower.includes('deliver') || statusLower.includes('complete')) {
              colorClass = 'bg-green-100 text-green-800 border-green-300';
            } else if (statusLower.includes('cancel')) {
              colorClass = 'bg-red-100 text-red-800 border-red-300';
            } else if (statusLower.includes('return') || statusLower.includes('devolucion')) {
              colorClass = 'bg-orange-100 text-orange-800 border-orange-300';
            } else if (statusLower.includes('transit') || statusLower.includes('tránsito')) {
              colorClass = 'bg-blue-100 text-blue-800 border-blue-300';
            } else if (statusLower.includes('assign') || statusLower.includes('received')) {
              colorClass = 'bg-yellow-100 text-yellow-800 border-yellow-300';
            } else if (statusLower.includes('transfer')) {
              colorClass = 'bg-purple-100 text-purple-800 border-purple-300';
            }
            return (
              <span key={s.status} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${colorClass}`}>
                {s.status}: <span className="font-bold">{formatNumber(s.count)}</span>
              </span>
            );
          })}
        </div>
      </div>
    )}

    {isOrdersWidgetVisible('orders_table_results') && (<div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b"><h3 className="font-semibold">Latest Orders ({latestOrders.length})</h3></div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left">Order #</th>
              <th className="px-4 py-2 text-left">Claim #</th>
              <th className="px-4 py-2 text-left">Part</th>
              <th className="px-4 py-2 text-left">Location</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Price</th>
              <th className="px-4 py-2 text-left">Order Date</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {latestOrders.map((order) => (
              <tr key={order.part_order_key} onClick={() => loadOrderDetail(order.part_order_key)} className="hover:bg-gray-50 cursor-pointer">
                <td className="px-4 py-2 text-gray-600">{order.order_number || '-'}</td>
                <td className="px-4 py-2 font-medium text-blue-600">{order.claim_number}</td>
                <td className="px-4 py-2">{truncate(order.part_name || 'Unknown Part', 30)}</td>
                <td className="px-4 py-2 text-gray-600">{truncate(order.location || '-', 20)}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(order.status, order.status_category)}`}>
                    {order.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">{formatCurrency(order.price)}</td>
                <td className="px-4 py-2 text-gray-500">{formatDateOnly(order.order_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>)}
  </div>
)}

      {searchResults.length > 0 && !selectedOrder && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b"><h3 className="font-semibold">Search Results ({searchResults.length})</h3></div>
          <div className="divide-y max-h-96 overflow-auto">
            {searchResults.map((order) => (
              <div
                key={order.part_order_key}
                onClick={() => loadOrderDetail(order.part_order_key)}
                className="p-4 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{truncate(order.part_name, 40)}</div>
                    <div className="text-sm text-gray-500">{order.part_number} • {order.claim_number}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatCurrency(order.price)}</div>
                    <span className="text-xs px-2 py-1 rounded bg-gray-100">{order.status}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {order.supplier_name} • {formatDateOnly(order.order_date)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedOrder && (
        <div className="space-y-4">
          <button
            onClick={() => setSelectedOrder(null)}
            className="text-sm text-blue-600 hover:underline"
          >
            ← Back to results
          </button>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold">{selectedOrder.part_name || 'Unknown Part'}</h2>
                <div className="text-sm text-gray-500">{selectedOrder.part_number || '-'} • {selectedOrder.part_type || '-'}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{formatCurrency(selectedOrder.current_price)}</div>
                <span className={`px-2 py-1 rounded text-sm ${getStatusStyle(selectedOrder.status_category)}`}>
                  {selectedOrder.status}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3">Order Details</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Order ID</dt><dd>{selectedOrder.part_order_key}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Quantity</dt><dd>{selectedOrder.quantity}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Current Price</dt><dd>{formatCurrency(selectedOrder.current_price)}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Quote Days</dt><dd>{selectedOrder.quote_days ?? '-'}</dd></div>
              </dl>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3">Claim & Vehicle</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Claim</dt><dd className="text-blue-600">{selectedOrder.claim_number}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Insurer</dt><dd>{selectedOrder.insurer_name}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Workshop</dt><dd>{truncate(selectedOrder.workshop_name, 25)}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Vehicle</dt><dd>{selectedOrder.vehicle_make} {selectedOrder.vehicle_model} {selectedOrder.vehicle_year || ''}</dd></div>
              </dl>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3">Supplier</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Name</dt><dd>{selectedOrder.supplier_name}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">GUID</dt><dd className="font-mono text-xs">{selectedOrder.supplier_guid || '-'}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Score</dt><dd>{selectedOrder.supplier_score != null ? Number(selectedOrder.supplier_score).toFixed(2) : '-'}</dd></div>
              </dl>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3">Automation</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Auto Assigned</dt><dd>{selectedOrder.is_auto_assigned ? '✓' : '✗'}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Auto Quoted</dt><dd>{selectedOrder.is_auto_quoted ? '✓' : '✗'}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Auto Process</dt><dd>{selectedOrder.is_auto_process ? '✓' : '✗'}</dd></div>
                {selectedOrder.supplier_cancel_reason && <div><dt className="text-gray-500">Supplier Cancel</dt><dd className="text-red-600">{selectedOrder.supplier_cancel_reason}</dd></div>}
                {selectedOrder.insurer_reassign_reason && <div><dt className="text-gray-500">Reassign Reason</dt><dd className="text-orange-600">{selectedOrder.insurer_reassign_reason}</dd></div>}
                {selectedOrder.manual_quote_reason && <div><dt className="text-gray-500">Manual Quote</dt><dd className="text-yellow-600">{selectedOrder.manual_quote_reason}</dd></div>}
              </dl>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-3">Timeline</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex-1 min-w-[150px] bg-gray-50 p-3 rounded text-center">
                <div className="text-xs text-gray-500">Quote Date</div>
                <div className="font-medium">{formatDateOnly(selectedOrder.quote_date)}</div>
              </div>
              <div className="flex-1 min-w-[150px] bg-blue-50 p-3 rounded text-center">
                <div className="text-xs text-blue-600">Order Date</div>
                <div className="font-medium">{formatDateOnly(selectedOrder.order_date)}</div>
              </div>
              <div className="flex-1 min-w-[150px] bg-purple-50 p-3 rounded text-center">
                <div className="text-xs text-purple-600">Pickup Date</div>
                <div className="font-medium">{formatDateOnly(selectedOrder.pickup_date)}</div>
              </div>
              <div className="flex-1 min-w-[150px] bg-green-50 p-3 rounded text-center">
                <div className="text-xs text-green-600">Delivery Date</div>
                <div className="font-medium">{formatDateOnly(selectedOrder.delivery_date)}</div>
              </div>
              <div className="flex-1 min-w-[150px] bg-teal-50 p-3 rounded text-center">
                <div className="text-xs text-teal-600">Received Date</div>
                <div className="font-medium">{formatDateOnly(selectedOrder.received_date)}</div>
              </div>
              <div className="flex-1 min-w-[150px] bg-orange-50 p-3 rounded text-center">
                <div className="text-xs text-orange-600">Deadline</div>
                <div className="font-medium">{formatDateOnly(selectedOrder.deadline_date)}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-4 justify-center">
              {selectedOrder.delivery_days != null && (
                <div className="text-center">
                  <span className="text-gray-500 text-sm">Delivery Time:</span>
                  <span className="ml-2 font-bold text-blue-600">{selectedOrder.delivery_days} days</span>
                </div>
              )}
              {selectedOrder.days_vs_deadline != null && (
                <div className="text-center">
                  <span className="text-gray-500 text-sm">vs Deadline:</span>
                  <span className={`ml-2 font-bold ${selectedOrder.days_vs_deadline >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedOrder.days_vs_deadline >= 0 ? `${selectedOrder.days_vs_deadline}d early` : `${Math.abs(selectedOrder.days_vs_deadline)}d late`}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Pattern Recognition Page
// ============================================================================

const PatternsPage: React.FC<{ selectedInsurer: number | null; dateRange: DateRange }> = ({ selectedInsurer, dateRange }) => {
  const [activeTab, setActiveTab] = useState<'summary' | 'anomalies' | 'risk' | 'delivery' | 'cooccurrence' | 'groupings' | 'trends' | 'clusters' | 'automation'>('summary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [summary, setSummary] = useState<PatternsSummary | null>(null);
  const [anomalies, setAnomalies] = useState<PriceAnomaly[]>([]);
  const [riskData, setRiskData] = useState<SupplierRisk[]>([]);
  const [deliveryData, setDeliveryData] = useState<DeliveryPrediction[]>([]);
  const [cooccurrence, setCooccurrence] = useState<PartCooccurrence[]>([]);
  const [groupings, setGroupings] = useState<PartGroupingsData | null>(null);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [clusters, setClusters] = useState<SupplierCluster[]>([]);
  const [automation, setAutomation] = useState<AutomationImpact[]>([]);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (selectedInsurer) params.append('insurer', selectedInsurer.toString());
    if (dateRange.startDate) params.append('startDate', dateRange.startDate);
    if (dateRange.endDate) params.append('endDate', dateRange.endDate);
    return params;
  };

  const fetchData = async (endpoint: string, setter: (data: any) => void) => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      const res = await authFetch(`/api/patterns/${endpoint}?${params}`);
      const json = await res.json();
      if (json.success) setter(json.data);
      else setError(json.error);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'summary') fetchData('summary', setSummary);
    if (activeTab === 'anomalies') fetchData('price-anomalies', setAnomalies);
    if (activeTab === 'risk') fetchData('supplier-risk', setRiskData);
    if (activeTab === 'delivery') fetchData('delivery-prediction', setDeliveryData);
    if (activeTab === 'cooccurrence') fetchData('part-cooccurrence', setCooccurrence);
    if (activeTab === 'groupings') fetchData('part-groupings', setGroupings);
    if (activeTab === 'trends') fetchData('trends', setTrends);
    if (activeTab === 'clusters') fetchData('supplier-clusters', setClusters);
    if (activeTab === 'automation') fetchData('automation-impact', setAutomation);
  }, [activeTab, selectedInsurer, dateRange]);

  const tabs = [
    { key: 'summary', label: 'Summary' },
    { key: 'anomalies', label: 'Price Anomalies' },
    { key: 'risk', label: 'Supplier Risk' },
    { key: 'delivery', label: 'Delivery Prediction' },
    { key: 'cooccurrence', label: 'Part Associations' },
    { key: 'groupings', label: 'Part Groupings' },
    { key: 'trends', label: 'Trends' },
    { key: 'clusters', label: 'Supplier Clusters' },
    { key: 'automation', label: 'Automation Impact' },
  ];

  const riskColor = (tier: string) => tier === 'HIGH' ? 'bg-red-100 text-red-800' : tier === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800';
  const tierColor = (tier: string) => {
    // Performance tiers
    if (tier === 'Premium' || tier === 'PREMIUM') return 'bg-green-100 text-green-800';
    if (tier === 'Reliable' || tier === 'RELIABLE') return 'bg-blue-100 text-blue-800';
    if (tier === 'Standard' || tier === 'STANDARD') return 'bg-gray-100 text-gray-800';
    if (tier === 'Underperforming' || tier === 'UNDERPERFORMING') return 'bg-red-100 text-red-800';
    // Value tiers
    if (tier === 'High Value' || tier === 'KEY_ACCOUNT') return 'bg-purple-100 text-purple-800';
    if (tier === 'Medium Value' || tier === 'GROWTH') return 'bg-indigo-100 text-indigo-800';
    if (tier === 'Low Value' || tier === 'EMERGING') return 'bg-gray-100 text-gray-800';
    if (tier === 'Minimal') return 'bg-gray-50 text-gray-600';
    // Reach tiers
    if (tier === 'Broad' || tier === 'BROAD') return 'bg-teal-100 text-teal-800';
    if (tier === 'Moderate' || tier === 'MODERATE') return 'bg-cyan-100 text-cyan-800';
    if (tier === 'Specialized' || tier === 'SPECIALIZED') return 'bg-gray-100 text-gray-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold mb-4">Pattern Recognition & Analytics</h2>
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`px-3 py-1.5 rounded text-sm ${activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">{error}</div>}
      {loading && <div className="text-center py-8 text-gray-500">Loading analysis...</div>}

      {/* Summary Tab */}
      {activeTab === 'summary' && summary && !loading && (
        <div className="space-y-4">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{summary.price_anomalies}</div>
              <div className="text-sm text-gray-500 flex items-center justify-center">
                Price Anomalies
                <InfoTooltip text="Parts priced more than 2 standard deviations from the mean for their part type. Indicates potential pricing errors or outliers requiring investigation." />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-orange-600">{summary.high_risk_suppliers}</div>
              <div className="text-sm text-gray-500 flex items-center justify-center">
                High Risk Suppliers
                <InfoTooltip text="Suppliers with >15% cancellation rate. These suppliers may need performance review, renegotiation, or replacement." />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{summary.complex_claims}</div>
              <div className="text-sm text-gray-500 flex items-center justify-center">
                Complex Claims
                <InfoTooltip text="Claims with >8 parts AND >4 suppliers. These require more coordination and have higher risk of delays or issues." />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{summary.auto_assign_rate}%</div>
              <div className="text-sm text-gray-500 flex items-center justify-center">
                Auto-Assign Rate
                <InfoTooltip text="Percentage of orders where supplier was automatically assigned by the system without manual intervention. Higher = more efficient." />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{summary.auto_quote_rate}%</div>
              <div className="text-sm text-gray-500 flex items-center justify-center">
                Auto-Quote Rate
                <InfoTooltip text="Percentage of orders where price quote was automatically generated. Higher = faster quote turnaround and less manual work." />
              </div>
            </div>
          </div>

          {/* Vehicle Statistics Card */}
          {summary.vehicle_stats && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3 flex items-center">
                Vehicle Statistics
                <InfoTooltip text="Overview of vehicles in the data. Shows unique vehicles, makes, and models with claims/orders processed." />
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded">
                  <div className="text-2xl font-bold text-blue-600">{formatNumber(summary.vehicle_stats.unique_vehicles)}</div>
                  <div className="text-xs text-gray-600">Unique Vehicles</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded">
                  <div className="text-2xl font-bold text-green-600">{formatNumber(summary.vehicle_stats.total_claims)}</div>
                  <div className="text-xs text-gray-600">Claims</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded">
                  <div className="text-2xl font-bold text-purple-600">{formatNumber(summary.vehicle_stats.total_orders)}</div>
                  <div className="text-xs text-gray-600">Orders</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded">
                  <div className="text-2xl font-bold text-orange-600">{formatNumber(summary.vehicle_stats.unique_makes)}</div>
                  <div className="text-xs text-gray-600">Makes</div>
                </div>
                <div className="text-center p-3 bg-teal-50 rounded">
                  <div className="text-2xl font-bold text-teal-600">{formatNumber(summary.vehicle_stats.unique_models)}</div>
                  <div className="text-xs text-gray-600">Models</div>
                </div>
              </div>
            </div>
          )}

          {/* Complex Claims by Vehicle Type */}
          {summary.complex_by_vehicle && summary.complex_by_vehicle.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h3 className="font-semibold flex items-center">
                  Complex Claims by Vehicle Make
                  <InfoTooltip text="Claims with >5 parts or >3 suppliers grouped by vehicle manufacturer. Shows average parts/suppliers and supplier distribution." />
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Vehicle Make</th>
                      <th className="px-4 py-2 text-right">Complex Claims</th>
                      <th className="px-4 py-2 text-right">Avg Parts</th>
                      <th className="px-4 py-2 text-right">Avg Suppliers</th>
                      <th className="px-4 py-2 text-center">1 Supplier</th>
                      <th className="px-4 py-2 text-center">2 Suppliers</th>
                      <th className="px-4 py-2 text-center">3+ Suppliers</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {summary.complex_by_vehicle.map((v, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">{v.vehicle_make}</td>
                        <td className="px-4 py-2 text-right font-bold text-purple-600">{v.complex_claims}</td>
                        <td className="px-4 py-2 text-right">{v.avg_parts}</td>
                        <td className="px-4 py-2 text-right">{v.avg_suppliers}</td>
                        <td className="px-4 py-2 text-center">
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">{v.single_supplier}</span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs">{v.two_suppliers}</span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs">{v.multi_suppliers}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Price Anomalies Tab */}
      {activeTab === 'anomalies' && anomalies.length > 0 && !loading && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center">
              Price Anomalies (Z-Score &gt; 2)
              <InfoTooltip text="Z-Score measures how many standard deviations a price is from the average. Z>2 or Z<-2 means the price is unusual. HIGH = overpriced, LOW = underpriced. Investigate for errors or negotiation opportunities." />
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Claim #</th>
                  <th className="px-3 py-2 text-left">Order #</th>
                  <th className="px-3 py-2 text-center">Parts</th>
                  <th className="px-3 py-2 text-left">Part Type</th>
                  <th className="px-3 py-2 text-left">Part</th>
                  <th className="px-3 py-2 text-left">Supplier</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">Avg Price</th>
                  <th className="px-3 py-2 text-right">Z-Score</th>
                  <th className="px-3 py-2 text-center">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {anomalies.map((a, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-blue-600 font-medium">{truncate(a.claim_number || '-', 12)}</td>
                    <td className="px-3 py-2 text-gray-600">{a.order_key || '-'}</td>
                    <td className="px-3 py-2 text-center"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{a.claim_parts_count}</span></td>
                    <td className="px-3 py-2">{truncate(a.part_type, 15)}</td>
                    <td className="px-3 py-2">{truncate(a.part_name, 20)}</td>
                    <td className="px-3 py-2">{truncate(a.supplier_name, 15)}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatCurrency(a.price)}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{formatCurrency(a.avg_price)}</td>
                    <td className="px-3 py-2 text-right font-bold">{Number(a.z_score || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${a.anomaly_type === 'HIGH' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                        {a.anomaly_type}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Supplier Risk Tab */}
      {activeTab === 'risk' && riskData.length > 0 && !loading && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center">
              Supplier Risk Scoring
              <InfoTooltip text="Risk Score (0-100) combines cancellation rate, supplier cancels, delivery time, and deadline misses. Risk Tier: LOW (<5% cancel), MEDIUM (<15% cancel), HIGH (>15% cancel). Use to identify unreliable suppliers." />
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Supplier</th>
                  <th className="px-4 py-2 text-right">Orders</th>
                  <th className="px-4 py-2 text-right">Delivery %</th>
                  <th className="px-4 py-2 text-right">Cancel %</th>
                  <th className="px-4 py-2 text-right">Avg Days</th>
                  <th className="px-4 py-2 text-right">Risk Score</th>
                  <th className="px-4 py-2 text-center">Risk Tier</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {riskData.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{truncate(r.supplier_name, 25)}</td>
                    <td className="px-4 py-2 text-right">{r.total_orders}</td>
                    <td className="px-4 py-2 text-right text-green-600">{r.delivery_rate}%</td>
                    <td className="px-4 py-2 text-right text-red-600">{r.cancel_rate}%</td>
                    <td className="px-4 py-2 text-right">{r.avg_delivery_days || '-'}</td>
                    <td className="px-4 py-2 text-right font-bold">{r.risk_score}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${riskColor(r.risk_tier)}`}>{r.risk_tier}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delivery Prediction Tab */}
      {activeTab === 'delivery' && deliveryData.length > 0 && !loading && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center">
              Delivery Time Predictions by Part Type
              <InfoTooltip text="Statistical delivery time distribution per part type. P90 = 90% of deliveries complete by this many days. Use P90 for customer promises. Std Dev = variability (high = unpredictable)." />
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Part Type</th>
                  <th className="px-4 py-2 text-right">Sample</th>
                  <th className="px-4 py-2 text-right">Avg Days</th>
                  <th className="px-4 py-2 text-right">Median</th>
                  <th className="px-4 py-2 text-right">Min</th>
                  <th className="px-4 py-2 text-right">Max</th>
                  <th className="px-4 py-2 text-right">P90</th>
                  <th className="px-4 py-2 text-right">Std Dev</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {deliveryData.map((d, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{truncate(d.part_type, 25)}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{d.sample_size}</td>
                    <td className="px-4 py-2 text-right font-bold">{d.avg_days}</td>
                    <td className="px-4 py-2 text-right">{d.median_days}</td>
                    <td className="px-4 py-2 text-right text-green-600">{d.min_days}</td>
                    <td className="px-4 py-2 text-right text-red-600">{d.max_days}</td>
                    <td className="px-4 py-2 text-right text-orange-600">{d.p90_days}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{d.std_dev}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Part Co-occurrence Tab */}
      {activeTab === 'cooccurrence' && cooccurrence.length > 0 && !loading && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center">
              Part Associations (Market Basket)
              <InfoTooltip text="Parts frequently ordered together. Lift > 1 means parts appear together more than random chance. Higher lift = stronger association. Use for inventory planning and upselling." />
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Part A</th>
                  <th className="px-4 py-2 text-left">Part B</th>
                  <th className="px-4 py-2 text-right">Together</th>
                  <th className="px-4 py-2 text-right">A→B %</th>
                  <th className="px-4 py-2 text-right">B→A %</th>
                  <th className="px-4 py-2 text-right">Lift</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {cooccurrence.map((c, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{truncate(c.part_a, 20)}</td>
                    <td className="px-4 py-2">{truncate(c.part_b, 20)}</td>
                    <td className="px-4 py-2 text-right font-bold">{c.times_together}</td>
                    <td className="px-4 py-2 text-right">{c.pct_a_with_b}%</td>
                    <td className="px-4 py-2 text-right">{c.pct_b_with_a}%</td>
                    <td className="px-4 py-2 text-right font-bold text-purple-600">{c.lift}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Part Groupings Tab */}
      {activeTab === 'groupings' && groupings && !loading && (
        <div className="space-y-4">
          {/* Part Frequency Summary */}
          {groupings.part_frequency && groupings.part_frequency.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3 flex items-center">
                Most Frequently Ordered Parts
                <InfoTooltip text="Individual parts ranked by number of claims they appear in. Shows the most common parts in repair jobs." />
              </h3>
              <div className="flex flex-wrap gap-2">
                {groupings.part_frequency.slice(0, 15).map((p, i) => (
                  <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                    {truncate(p.part_name, 20)} <span className="font-bold">({p.claim_count})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Part Groupings Table */}
          {groupings.groupings && groupings.groupings.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h3 className="font-semibold flex items-center">
                  Common Part Groupings
                  <InfoTooltip text="Sets of parts that are frequently ordered together for the same claim. Shows common repair combinations. Higher claims count = more common grouping." />
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Parts Combination</th>
                      <th className="px-4 py-2 text-center"># Parts</th>
                      <th className="px-4 py-2 text-right">Claims</th>
                      <th className="px-4 py-2 text-right">Avg Value</th>
                      <th className="px-4 py-2 text-right">Total Value</th>
                      <th className="px-4 py-2 text-right">% of Groups</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {groupings.groupings.map((g, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {g.parts_group.split(' + ').map((part, j) => (
                              <span key={j} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{truncate(part, 15)}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{g.num_parts}</span>
                        </td>
                        <td className="px-4 py-2 text-right font-bold">{formatNumber(g.claims_count)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(g.avg_value)}</td>
                        <td className="px-4 py-2 text-right text-green-600">{formatCurrency(g.total_value)}</td>
                        <td className="px-4 py-2 text-right">{g.pct_of_groupings}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(!groupings.groupings || groupings.groupings.length === 0) && (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              No part groupings found with the current filters. Try adjusting the date range or insurer filter.
            </div>
          )}
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && trends.length > 0 && !loading && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-4 flex items-center">
              Weekly Order Trends (6 months)
              <InfoTooltip text="Weekly order volume over 6 months. MA4 = 4-week moving average (smoothed trend). WoW = week-over-week change. Use to identify seasonality and growth/decline patterns." />
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="order_count" fill="#3b82f6" name="Orders" />
                <Bar dataKey="ma4_orders" fill="#22c55e" name="4-Week MA" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h3 className="font-semibold">Weekly Data</h3></div>
            <div className="overflow-x-auto max-h-64">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Week</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                    <th className="px-4 py-2 text-right">Claims</th>
                    <th className="px-4 py-2 text-right">Value</th>
                    <th className="px-4 py-2 text-right">Delivery %</th>
                    <th className="px-4 py-2 text-right">WoW</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {trends.map((t, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2">{t.week}</td>
                      <td className="px-4 py-2 text-right font-medium">{t.order_count}</td>
                      <td className="px-4 py-2 text-right">{t.claim_count}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(t.total_value)}</td>
                      <td className="px-4 py-2 text-right text-green-600">{t.delivery_rate}%</td>
                      <td className={`px-4 py-2 text-right ${(t.wow_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {t.wow_change != null ? `${t.wow_change >= 0 ? '+' : ''}${t.wow_change}%` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Clusters Tab */}
      {activeTab === 'clusters' && clusters.length > 0 && !loading && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center">
              Supplier Segmentation
              <InfoTooltip text="3-axis supplier classification. Performance: PREMIUM (>90% delivery, <3 days) to UNDERPERFORMING. Value: KEY_ACCOUNT (top 10%) to EMERGING. Reach: BROAD (5+ part types, 10+ workshops) to SPECIALIZED." />
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Supplier</th>
                  <th className="px-4 py-2 text-right">Orders</th>
                  <th className="px-4 py-2 text-right">Value</th>
                  <th className="px-4 py-2 text-right">Delivery %</th>
                  <th className="px-4 py-2 text-center">Performance</th>
                  <th className="px-4 py-2 text-center">Value Tier</th>
                  <th className="px-4 py-2 text-center">Reach</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {clusters.map((c, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{truncate(c.supplier_name, 25)}</td>
                    <td className="px-4 py-2 text-right">{c.total_orders}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(c.total_value)}</td>
                    <td className="px-4 py-2 text-right">{c.delivery_rate}%</td>
                    <td className="px-4 py-2 text-center"><span className={`px-2 py-1 rounded text-xs ${tierColor(c.performance_tier)}`}>{c.performance_tier}</span></td>
                    <td className="px-4 py-2 text-center"><span className={`px-2 py-1 rounded text-xs ${tierColor(c.value_tier)}`}>{c.value_tier}</span></td>
                    <td className="px-4 py-2 text-center"><span className={`px-2 py-1 rounded text-xs ${tierColor(c.reach_tier)}`}>{c.reach_tier}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Automation Impact Tab */}
      {activeTab === 'automation' && automation.length > 0 && !loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {automation.map((a, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-4">
                <div className="text-sm font-medium text-gray-500">{a.automation_level.replace(/_/g, ' ')}</div>
                <div className="text-2xl font-bold mt-1">{formatNumber(a.order_count)}</div>
                <div className="text-sm text-gray-400">{a.pct_of_total}% of total</div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-gray-500">Delivery:</span> <span className="text-green-600 font-medium">{a.delivery_rate}%</span></div>
                  <div><span className="text-gray-500">Avg Days:</span> <span className="font-medium">{a.avg_delivery_days || '-'}</span></div>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-4 flex items-center">
              Automation Effectiveness
              <InfoTooltip text="Compares performance of automated vs manual order processing. FULL_AUTO = both auto-assigned and auto-quoted. Higher delivery % and lower days for automated = automation is working well." />
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={automation} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="automation_level" type="category" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="delivery_rate" fill="#22c55e" name="Delivery %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Reports Page - Generate and Export Reports
// ============================================================================

interface ReportColumn {
  key: string;
  label: string;
  default: boolean;
  type?: 'text' | 'number' | 'currency' | 'percent' | 'date' | 'datetime';
}

interface ReportType {
  id: string;
  name: string;
  description: string;
  columns: ReportColumn[];
  endpoint: string;
}

const REPORT_TYPES: ReportType[] = [
  {
    id: 'supplier_performance',
    name: 'Supplier Performance',
    description: 'Supplier metrics including delivery rates, volumes, and response times',
    columns: [
      { key: 'supplier_name', label: 'Supplier', default: true, type: 'text' },
      { key: 'total_orders', label: 'Total Orders', default: true, type: 'number' },
      { key: 'delivered_orders', label: 'Delivered', default: true, type: 'number' },
      { key: 'delivery_rate', label: 'Delivery Rate %', default: true, type: 'percent' },
      { key: 'cancelled_orders', label: 'Cancelled', default: true, type: 'number' },
      { key: 'total_value', label: 'Total Value', default: true, type: 'currency' },
      { key: 'avg_delivery_days', label: 'Avg Delivery Days', default: true, type: 'number' },
      { key: 'auto_assigned', label: 'Auto Assigned', default: false, type: 'number' },
      { key: 'manual_assigned', label: 'Manual Assigned', default: false, type: 'number' },
    ],
    endpoint: '/api/reports/supplier-performance'
  },
  {
    id: 'claims_summary',
    name: 'Claims Summary',
    description: 'Claims overview with status, parts count, and total values',
    columns: [
      { key: 'claim_number', label: 'Claim #', default: true, type: 'text' },
      { key: 'workshop_name', label: 'Workshop', default: true, type: 'text' },
      { key: 'insurer_name', label: 'Insurer', default: true, type: 'text' },
      { key: 'vehicle_info', label: 'Vehicle', default: true, type: 'text' },
      { key: 'parts_count', label: 'Parts', default: true, type: 'number' },
      { key: 'total_value', label: 'Total Value', default: true, type: 'currency' },
      { key: 'status', label: 'Status', default: true, type: 'text' },
      { key: 'created_at', label: 'Created Date', default: true, type: 'datetime' },
      { key: 'delivered_parts', label: 'Delivered Parts', default: false, type: 'number' },
      { key: 'pending_parts', label: 'Pending Parts', default: false, type: 'number' },
    ],
    endpoint: '/api/reports/claims-summary'
  },
  {
    id: 'parts_analytics',
    name: 'Parts Analytics',
    description: 'Part orders with pricing, categories, and supplier information',
    columns: [
      { key: 'part_description', label: 'Part', default: true, type: 'text' },
      { key: 'part_number', label: 'Part Number', default: true, type: 'text' },
      { key: 'part_type', label: 'Type', default: true, type: 'text' },
      { key: 'supplier_name', label: 'Supplier', default: true, type: 'text' },
      { key: 'quantity', label: 'Quantity', default: true, type: 'number' },
      { key: 'unit_price', label: 'Unit Price', default: true, type: 'currency' },
      { key: 'total_price', label: 'Total Price', default: true, type: 'currency' },
      { key: 'status', label: 'Status', default: true, type: 'text' },
      { key: 'order_date', label: 'Order Date', default: false, type: 'date' },
      { key: 'delivery_date', label: 'Delivery Date', default: false, type: 'date' },
    ],
    endpoint: '/api/reports/parts-analytics'
  },
  {
    id: 'delivery_metrics',
    name: 'Delivery Metrics',
    description: 'On-time delivery rates and delays by supplier and region',
    columns: [
      { key: 'supplier_name', label: 'Supplier', default: true, type: 'text' },
      { key: 'state', label: 'State', default: true, type: 'text' },
      { key: 'total_deliveries', label: 'Total Deliveries', default: true, type: 'number' },
      { key: 'on_time', label: 'On Time', default: true, type: 'number' },
      { key: 'late', label: 'Late', default: true, type: 'number' },
      { key: 'on_time_rate', label: 'On-Time %', default: true, type: 'percent' },
      { key: 'avg_delay_days', label: 'Avg Delay (days)', default: true, type: 'number' },
      { key: 'max_delay_days', label: 'Max Delay', default: false, type: 'number' },
    ],
    endpoint: '/api/reports/delivery-metrics'
  },
  {
    id: 'cost_analysis',
    name: 'Cost Analysis',
    description: 'Spending breakdown by insurer, supplier, and part type',
    columns: [
      { key: 'category', label: 'Category', default: true, type: 'text' },
      { key: 'insurer_name', label: 'Insurer', default: true, type: 'text' },
      { key: 'supplier_name', label: 'Supplier', default: true, type: 'text' },
      { key: 'part_type', label: 'Part Type', default: true, type: 'text' },
      { key: 'order_count', label: 'Orders', default: true, type: 'number' },
      { key: 'total_cost', label: 'Total Cost', default: true, type: 'currency' },
      { key: 'avg_cost', label: 'Avg Cost', default: true, type: 'currency' },
      { key: 'min_cost', label: 'Min Cost', default: false, type: 'currency' },
      { key: 'max_cost', label: 'Max Cost', default: false, type: 'currency' },
    ],
    endpoint: '/api/reports/cost-analysis'
  },
  {
    id: 'automation_report',
    name: 'Automation Report',
    description: 'Auto-quote and auto-assign rates by state and period',
    columns: [
      { key: 'state', label: 'State', default: true, type: 'text' },
      { key: 'total_parts', label: 'Total Parts', default: true, type: 'number' },
      { key: 'auto_quoted', label: 'Auto Quoted', default: true, type: 'number' },
      { key: 'manual_quoted', label: 'Manual Quoted', default: true, type: 'number' },
      { key: 'auto_quote_pct', label: 'Auto Quote %', default: true, type: 'percent' },
      { key: 'auto_assigned', label: 'Auto Assigned', default: true, type: 'number' },
      { key: 'manual_assigned', label: 'Manual Assigned', default: true, type: 'number' },
      { key: 'auto_assign_pct', label: 'Auto Assign %', default: true, type: 'percent' },
    ],
    endpoint: '/api/reports/automation'
  },
  {
    id: 'valuation_audit',
    name: 'Valuation Audit Report',
    description: 'Detailed part order audit with pricing, status, and automation flags for compliance review',
    columns: [
      { key: 'insurer_name', label: 'Insurer', default: true, type: 'text' },
      { key: 'workshop_name', label: 'Workshop', default: true, type: 'text' },
      { key: 'workshop_state', label: 'State', default: false, type: 'text' },
      { key: 'claim_number', label: 'Claim #', default: true, type: 'text' },
      { key: 'vehicle', label: 'Vehicle', default: true, type: 'text' },
      { key: 'supplier_name', label: 'Supplier', default: true, type: 'text' },
      { key: 'part_number', label: 'Part Number', default: false, type: 'text' },
      { key: 'part_description', label: 'Part Description', default: true, type: 'text' },
      { key: 'part_type', label: 'Part Type', default: true, type: 'text' },
      { key: 'quantity', label: 'Qty', default: true, type: 'number' },
      { key: 'current_price', label: 'Unit Price', default: true, type: 'currency' },
      { key: 'total_price', label: 'Total Price', default: true, type: 'currency' },
      { key: 'status', label: 'Status', default: true, type: 'text' },
      { key: 'status_category', label: 'Status Category', default: false, type: 'text' },
      { key: 'quote_date', label: 'Quote Date', default: true, type: 'date' },
      { key: 'delivered_days', label: 'Delivered Days', default: false, type: 'number' },
      { key: 'auto_quoted', label: 'Auto Quoted', default: false, type: 'text' },
      { key: 'auto_assigned', label: 'Auto Assigned', default: false, type: 'text' },
      { key: 'delivery_status', label: 'Delivery Status', default: false, type: 'text' },
      { key: 'delivery_date', label: 'Delivery Date', default: false, type: 'date' },
    ],
    endpoint: '/api/reports/valuation-audit'
  },
];

// Maps REPORT_TYPES id → user_widget_permissions widget_key.
// Only report types that appear here are subject to admin widget revocation.
const REPORT_WIDGET_MAP: Record<string, string> = {
  supplier_performance: 'reports_supplier_performance',
  claims_summary:       'reports_claims_summary',
  parts_analytics:      'reports_parts_analytics',
  valuation_audit:      'reports_valuation_audit',
};

const ReportsPage: React.FC<{
  selectedInsurer: number | null;
  dateRange: DateRange;
  insurers: Insurer[];
  userRole: string;
}> = ({ selectedInsurer, dateRange, insurers, userRole }) => {
  const { t } = useTranslation();
  const [selectedReport, setSelectedReport] = useState<string>(REPORT_TYPES[0].id);
  const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>({});
  const [reportInsurer, setReportInsurer] = useState<number | null>(selectedInsurer);
  const [reportDateRange, setReportDateRange] = useState<DateRange>(dateRange);
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showApiModal, setShowApiModal] = useState(false);
  const [generatedApiUrl, setGeneratedApiUrl] = useState('');

  const { user: reportsUser } = useAuth();
  const [reportsRevokedWidgets, setReportsRevokedWidgets] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!reportsUser?.user_key) return;
    authFetch(`/api/users/widget-permissions?user_key=${reportsUser.user_key}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(res => {
        const revoked = new Set(
          (res?.data?.permissions ?? [])
            .filter((p: any) => !p.is_visible)
            .map((p: any) => p.widget_key as string)
        );
        setReportsRevokedWidgets(revoked);
      })
      .catch(() => {});
  }, [reportsUser?.user_key]);

  const visibleReportTypes = REPORT_TYPES.filter(r =>
    !REPORT_WIDGET_MAP[r.id] || !reportsRevokedWidgets.has(REPORT_WIDGET_MAP[r.id])
  );

  // Auto-switch to first visible report if selected report becomes revoked
  useEffect(() => {
    if (visibleReportTypes.length > 0 && !visibleReportTypes.find(r => r.id === selectedReport)) {
      setSelectedReport(visibleReportTypes[0].id);
    }
  }, [reportsRevokedWidgets]);

  const currentReport = visibleReportTypes.find(r => r.id === selectedReport) || visibleReportTypes[0] || REPORT_TYPES[0];

  // Initialize columns when report type changes
  useEffect(() => {
    const cols: Record<string, boolean> = {};
    currentReport.columns.forEach(c => { cols[c.key] = c.default; });
    setSelectedColumns(cols);
    setReportData([]);
  }, [selectedReport]);

  // Update insurer when global changes
  useEffect(() => {
    if (userRole !== 'ADMIN') {
      setReportInsurer(selectedInsurer);
    }
  }, [selectedInsurer, userRole]);

  const generateReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (reportDateRange.startDate) params.append('startDate', reportDateRange.startDate);
      if (reportDateRange.endDate) params.append('endDate', reportDateRange.endDate);
      if (reportInsurer) params.append('insurer', reportInsurer.toString());
      
      const cols = Object.entries(selectedColumns).filter(([, v]) => v).map(([k]) => k);
      params.append('columns', cols.join(','));

      const res = await authFetch(`${currentReport.endpoint}?${params.toString()}`);
      const json = await res.json();
      
      if (json.success) {
        setReportData(json.data || []);
      } else {
        setError(json.message || 'Failed to generate report');
      }
    } catch (e) {
      setError('Failed to fetch report data');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (reportData.length === 0) return;

    // Get selected columns in order
    const cols = currentReport.columns.filter(c => selectedColumns[c.key]);
    
    // Format value for Excel based on column type
    const formatExcelValue = (val: any, col: ReportColumn): string | number => {
      if (val === null || val === undefined) return '';
      
      switch (col.type) {
        case 'currency':
          return typeof val === 'number' ? val : parseFloat(val) || 0;
        case 'percent':
          return typeof val === 'number' ? val : parseFloat(val) || 0;
        case 'number':
          return typeof val === 'number' ? val : parseFloat(val) || 0;
        case 'date':
        case 'datetime':
          return formatReportDateTime(val);
        default:
          return String(val);
      }
    };
    
    // Build worksheet data
    const wsData = [
      // Header row
      cols.map(c => c.label),
      // Data rows
      ...reportData.map(row => cols.map(c => formatExcelValue(row[c.key], c)))
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Main data sheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, currentReport.name);
    
    // Metadata sheet
    const metaData = [
      ['Report', currentReport.name],
      ['Generated', formatReportDateTime(new Date().toISOString())],
      ['Date Range', `${reportDateRange.startDate || 'All'} to ${reportDateRange.endDate || 'All'}`],
      ['Insurer', reportInsurer ? insurers.find(i => i.insurer_key === reportInsurer)?.insurer_name || String(reportInsurer) : 'All'],
      ['Rows', reportData.length.toString()],
    ];
    const metaWs = XLSX.utils.aoa_to_sheet(metaData);
    XLSX.utils.book_append_sheet(wb, metaWs, 'Metadata');

    // Generate filename
    const filename = `${currentReport.id}_${reportDateRange.startDate || 'all'}_${reportDateRange.endDate || 'all'}.xlsx`;
    
    // Download
    XLSX.writeFile(wb, filename);
  };

  const generateApiUrl = () => {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams();
    if (reportDateRange.startDate) params.append('startDate', reportDateRange.startDate);
    if (reportDateRange.endDate) params.append('endDate', reportDateRange.endDate);
    if (reportInsurer) params.append('insurer', reportInsurer.toString());
    const cols = Object.entries(selectedColumns).filter(([, v]) => v).map(([k]) => k);
    params.append('columns', cols.join(','));
    params.append('format', 'json');

    const url = `${baseUrl}${currentReport.endpoint}?${params.toString()}`;
    setGeneratedApiUrl(url);
    setShowApiModal(true);
  };

  // Map frontend report ID (underscores) to powerquery route ID (hyphens)
  const toPowerQueryId = (id: string): string => {
    const overrides: Record<string, string> = { automation_report: 'automation' };
    return overrides[id] ?? id.replace(/_/g, '-');
  };

  const getPowerQueryUrl = () => {
    const params = new URLSearchParams();
    if (reportDateRange.startDate) params.append('startDate', reportDateRange.startDate);
    if (reportDateRange.endDate) params.append('endDate', reportDateRange.endDate);
    if (reportInsurer) params.append('insurer', reportInsurer.toString());
    const qs = params.toString();
    return `${window.location.origin}/api/powerquery/${toPowerQueryId(currentReport.id)}${qs ? `?${qs}` : ''}`;
  };

  const generateMCode = () => {
    const url = getPowerQueryUrl();
    const cols = currentReport.columns.filter(c => selectedColumns[c.key]);
    const colNames = cols.map(c => `"${c.key}"`).join(', ');
    const colLabels = cols.map(c => `"${c.label}"`).join(', ');
    return `let
    Source = Web.Contents("${url}"),
    Json = Json.Document(Source),
    Records = Json[value],
    ToTable = Table.FromList(Records, Splitter.SplitByNothing(), null, null, ExtraValues.Ignore),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {${colNames}}, {${colLabels}})
in
    Expanded`;
  };

  // Format cell value based on column type
  const formatCellValue = (value: any, col: ReportColumn): string => {
    if (value === null || value === undefined) return '-';
    
    switch (col.type) {
      case 'currency':
        return formatCurrency(value);
      case 'percent':
        return `${Number(value || 0).toFixed(1)}%`;
      case 'date':
      case 'datetime':
        return formatReportDateTime(value);
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : String(value);
      default:
        return String(value);
    }
  };

  const visibleColumns = currentReport.columns.filter(c => selectedColumns[c.key]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold text-gray-800">{t('reports.title')}</h1>
      </div>

      {/* Configuration Panel */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">{t('reports.configuration')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.reportType')}</label>
            <select
              value={selectedReport}
              onChange={(e) => setSelectedReport(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {visibleReportTypes.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">{currentReport.description}</p>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.startDate')}</label>
            <input
              type="date"
              value={reportDateRange.startDate}
              onChange={(e) => setReportDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.endDate')}</label>
            <input
              type="date"
              value={reportDateRange.endDate}
              onChange={(e) => setReportDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Insurer Scope */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.insurer')}</label>
            <select
              value={reportInsurer || ''}
              onChange={(e) => setReportInsurer(e.target.value ? Number(e.target.value) : null)}
              disabled={userRole !== 'app_admin' && userRole !== 'insurer_analyst'}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">{t('common.allInsurers')}</option>
              {insurers.map(i => (
                <option key={i.insurer_key} value={i.insurer_key}>{i.insurer_name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Column Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports.columnsInclude')}</label>
          <div className="flex flex-wrap gap-2">
            {currentReport.columns.map(col => (
              <label key={col.key} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200">
                <input
                  type="checkbox"
                  checked={selectedColumns[col.key] || false}
                  onChange={(e) => setSelectedColumns(prev => ({ ...prev, [col.key]: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm">{col.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={generateReport}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('common.loading')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('reports.generateReport')}
              </>
            )}
          </button>

          <button
            onClick={exportToExcel}
            disabled={reportData.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t('reports.exportExcel')}
          </button>

          <button
            onClick={generateApiUrl}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {t('reports.getApiUrl')}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Report Preview */}
      {reportData.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <div>
              <h3 className="font-semibold">{currentReport.name} Preview</h3>
              <p className="text-sm text-gray-500">{reportData.length} rows</p>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[500px]">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className="px-4 py-3 text-left font-medium text-gray-700">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {reportData.slice(0, 100).map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    {visibleColumns.map(col => (
                      <td key={col.key} className="px-4 py-2">
                        {formatCellValue(row[col.key], col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {reportData.length > 100 && (
              <div className="p-4 text-center text-gray-500 bg-gray-50 border-t">
                {t('reports.showingFirst')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* API URL Modal */}
      {showApiModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">{t('reports.apiAccess')}</h3>
              <button onClick={() => setShowApiModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">

              {/* Web Connector URL */}
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                  <p className="text-sm font-semibold text-blue-800">{t('reports.webConnectorUrl')}</p>
                  <p className="text-xs text-blue-600 mt-0.5">{t('reports.webConnectorDesc')}</p>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={getPowerQueryUrl()}
                      readOnly
                      className="flex-1 px-3 py-2 border rounded-lg bg-gray-50 text-xs font-mono"
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(getPowerQueryUrl())}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm whitespace-nowrap"
                    >
                      {t('reports.copyUrl')}
                    </button>
                  </div>
                  <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                    <li>Open Excel → Data → Get Data → From Other Sources → <strong>From Web</strong></li>
                    <li>Paste the URL above and click OK</li>
                    <li>When prompted for authentication, select <strong>Basic</strong></li>
                    <li>Enter your XNuuP email (Username) and password</li>
                    <li>Click Connect → Transform Data (optional) → Close &amp; Load</li>
                  </ol>
                  <div className="pt-1">
                    <p className="text-xs text-gray-500 mb-2">Or paste this M script directly into Advanced Editor (Home → Advanced Editor):</p>
                    <div className="relative">
                      <pre className="bg-gray-900 text-green-300 text-xs p-3 rounded-lg overflow-x-auto whitespace-pre font-mono leading-relaxed">{generateMCode()}</pre>
                      <button
                        onClick={() => navigator.clipboard.writeText(generateMCode())}
                        className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-gray-200 rounded text-xs hover:bg-gray-600"
                      >
                        {t('reports.copyMCode')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* OData Feed URL */}
              <div className="border border-purple-200 rounded-lg overflow-hidden">
                <div className="bg-purple-50 px-4 py-3 border-b border-purple-200">
                  <p className="text-sm font-semibold text-purple-800">{t('reports.odataFeedUrl')}</p>
                  <p className="text-xs text-purple-600 mt-0.5">{t('reports.odataFeedDesc')}</p>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={getPowerQueryUrl()}
                      readOnly
                      className="flex-1 px-3 py-2 border rounded-lg bg-gray-50 text-xs font-mono"
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(getPowerQueryUrl())}
                      className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm whitespace-nowrap"
                    >
                      {t('reports.copyUrl')}
                    </button>
                  </div>
                  <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                    <li>Open Excel → Data → Get Data → From Other Sources → <strong>From OData Feed</strong></li>
                    <li>Paste the URL above and click OK</li>
                    <li>When prompted for authentication, select <strong>Basic</strong></li>
                    <li>Enter your XNuuP email (Username) and password</li>
                    <li>Click Connect — Excel will load the data natively</li>
                  </ol>
                </div>
              </div>

              {/* Bearer Token URL (for scripts/APIs) */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <p className="text-sm font-semibold text-gray-700">{t('reports.bearerTokenUrl')}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t('reports.bearerTokenDesc')}</p>
                </div>
                <div className="p-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={generatedApiUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border rounded-lg bg-gray-50 text-xs font-mono"
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(generatedApiUrl)}
                      className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm whitespace-nowrap"
                    >
                      {t('reports.copyUrl')}
                    </button>
                  </div>
                </div>
              </div>

            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowApiModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Shareholders Page - Executive Revenue Dashboard
// ============================================================================

interface ShareholdersData {
  claims_summary: {
    total_claims: number;
    total_parts: number;
    total_value: number;
    avg_part_price: number;
    avg_parts_per_claim: number;
    avg_suppliers_per_claim: number;
  } | null;
  network_counts: {
    total_suppliers: number;
    total_workshops: number;
    total_insurers: number;
  } | null;
  automation_metrics: {
    total_parts: number;
    auto_quoted_parts: number;
    manual_quoted_parts: number;
    auto_quote_pct: number;
    manual_quote_pct: number;
    auto_assigned_parts: number;
    manual_assigned_parts: number;
    auto_assign_pct: number;
    manual_assign_pct: number;
  } | null;
  parts_distribution: Array<{
    parts_bucket: string;
    claim_count: number;
  }>;
  status_breakdown: Array<{
    status_code: string;
    status_category: string;
    part_count: number;
    pct_of_total: number;
    total_value: number;
  }>;
  claim_fulfillment: {
    total_claims: number;
    fulfilled_claims: number;
    unfulfilled_claims: number;
    partial_claims: number;
    fulfillment_rate: number;
  } | null;
  supplier_performance: Array<{
    supplier_name: string;
    total_parts: number;
    delivery_rate: number;
    avg_delivery_days: number;
    total_value: number;
  }>;
  vehicle_stats: Array<{
    marca: string;
    modelo: string;
    año: number;
    total_parts: number;
    total_claims: number;
    delivery_rate: number;
    total_value: number;
  }>;
  insurer_performance: Array<{
    insurer_name: string;
    total_claims: number;
    total_parts: number;
    delivery_rate: number;
    total_value: number;
    unique_workshops: number;
  }>;
  suppliers_per_claim: {
    avg_suppliers_per_claim: number;
    min_suppliers: number;
    max_suppliers: number;
    single_supplier_claims: number;
    two_supplier_claims: number;
    multi_supplier_claims: number;
    total_claims: number;
  } | null;
  state_performance: Array<{
    state: string;
    total_parts: number;
    auto_assign_pct: number;
    generic_pct: number;
    original_pct: number;
    total_value: number;
    avg_price: number;
  }>;
}

const ShareholdersPage: React.FC<{ dateRange: DateRange }> = ({ dateRange }) => {
  const [data, setData] = useState<ShareholdersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('startDate', dateRange.startDate);
        if (dateRange.endDate) params.append('endDate', dateRange.endDate);

        const response = await authFetch(`/api/shareholders?${params.toString()}`);
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          setError(result.error || 'Failed to load data');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load shareholders data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading shareholders data...</div></div>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>;
  if (!data) return <div className="text-gray-500">No data available</div>;

  const summary = data.claims_summary;
  const network = data.network_counts;
  const automation = data.automation_metrics;
  const fulfillment = data.claim_fulfillment;
  const safeStatusBreakdown = data.status_breakdown ?? [];
  const safePartsDistribution = data.parts_distribution ?? [];
  const safeSupplierPerformance = data.supplier_performance ?? [];
  const safeInsurerPerformance = data.insurer_performance ?? [];
  const safeVehicleStats = data.vehicle_stats ?? [];
  const safeStatePerformance = data.state_performance ?? [];
  const suppliersPerClaim = data.suppliers_per_claim;

  const statusColors: Record<string, string> = {
    'Complete': '#10b981',
    'Cancelled': '#ef4444',
    'Returned': '#f59e0b',
    'In Progress': '#3b82f6',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">XNuuP Platform Dashboard</h2>
        <p className="text-indigo-100 text-sm">Executive view of platform operations, network, and automation</p>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-blue-100 text-xs uppercase tracking-wide">Total Value</div>
          <div className="text-2xl font-bold mt-1">{formatCurrency(summary?.total_value ?? 0)}</div>
          <div className="text-blue-100 text-xs mt-1">{formatNumber(summary?.total_parts ?? 0)} parts</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-emerald-100 text-xs uppercase tracking-wide">Total Claims</div>
          <div className="text-2xl font-bold mt-1">{formatNumber(summary?.total_claims ?? 0)}</div>
          <div className="text-emerald-100 text-xs mt-1">{(summary?.avg_parts_per_claim ?? 0).toFixed(1)} parts/claim</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-purple-100 text-xs uppercase tracking-wide">Avg Part Price</div>
          <div className="text-2xl font-bold mt-1">{formatCurrency(summary?.avg_part_price ?? 0)}</div>
          <div className="text-purple-100 text-xs mt-1">{(summary?.avg_suppliers_per_claim ?? 0).toFixed(1)} suppliers/claim</div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-amber-100 text-xs uppercase tracking-wide">Network</div>
          <div className="text-2xl font-bold mt-1">{formatNumber(network?.total_suppliers ?? 0)}</div>
          <div className="text-amber-100 text-xs mt-1">{formatNumber(network?.total_workshops ?? 0)} workshops · {formatNumber(network?.total_insurers ?? 0)} insurers</div>
        </div>
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-teal-100 text-xs uppercase tracking-wide">Auto Quote</div>
          <div className="text-2xl font-bold mt-1">{(automation?.auto_quote_pct ?? 0).toFixed(1)}%</div>
          <div className="text-teal-100 text-xs mt-1">{formatNumber(automation?.auto_quoted_parts ?? 0)} auto-quoted</div>
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-indigo-100 text-xs uppercase tracking-wide">Auto Assign</div>
          <div className="text-2xl font-bold mt-1">{(automation?.auto_assign_pct ?? 0).toFixed(1)}%</div>
          <div className="text-indigo-100 text-xs mt-1">{formatNumber(automation?.auto_assigned_parts ?? 0)} auto-assigned</div>
        </div>
      </div>

      {/* Status Breakdown & Parts Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown Pie */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Parts by Status</h3>
          {safeStatusBreakdown.length > 0 ? (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={safeStatusBreakdown}
                      dataKey="part_count"
                      nameKey="status_code"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {safeStatusBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={statusColors[entry.status_category] || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatNumber(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                {safeStatusBreakdown.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusColors[s.status_category] || COLORS[i % COLORS.length] }} />
                    <span className="text-gray-600 truncate">{s.status_code}: {s.pct_of_total.toFixed(1)}% · {formatCurrency(s.total_value)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-gray-500 text-center py-8">No data</p>}
        </div>

        {/* Parts per Claim Distribution */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Parts per Claim Distribution</h3>
          {safePartsDistribution.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={safePartsDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="parts_bucket" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => formatNumber(v)} />
                  <Bar dataKey="claim_count" fill="#8b5cf6" name="Claims" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-gray-500 text-center py-8">No data</p>}
        </div>
      </div>

      {/* Claim Fulfillment & Suppliers per Claim */}
      {(fulfillment || suppliersPerClaim) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {fulfillment && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Claim Fulfillment</h3>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-emerald-600">{fulfillment.fulfillment_rate.toFixed(1)}%</div>
                <div className="text-gray-500 text-sm">fulfillment rate</div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center text-sm">
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-xl font-bold text-green-700">{formatNumber(fulfillment.fulfilled_claims)}</div>
                  <div className="text-green-600 text-xs mt-1">Fulfilled</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="text-xl font-bold text-yellow-700">{formatNumber(fulfillment.partial_claims)}</div>
                  <div className="text-yellow-600 text-xs mt-1">Partial</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-xl font-bold text-red-700">{formatNumber(fulfillment.unfulfilled_claims)}</div>
                  <div className="text-red-600 text-xs mt-1">Unfulfilled</div>
                </div>
              </div>
            </div>
          )}
          {suppliersPerClaim && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Suppliers per Claim</h3>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-blue-600">{suppliersPerClaim.avg_suppliers_per_claim.toFixed(2)}</div>
                <div className="text-gray-500 text-sm">avg suppliers per claim</div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center text-sm">
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-xl font-bold text-blue-700">{formatNumber(suppliersPerClaim.single_supplier_claims)}</div>
                  <div className="text-blue-600 text-xs mt-1">1 Supplier</div>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3">
                  <div className="text-xl font-bold text-indigo-700">{formatNumber(suppliersPerClaim.two_supplier_claims)}</div>
                  <div className="text-indigo-600 text-xs mt-1">2 Suppliers</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <div className="text-xl font-bold text-purple-700">{formatNumber(suppliersPerClaim.multi_supplier_claims)}</div>
                  <div className="text-purple-600 text-xs mt-1">3+ Suppliers</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Insurer & Supplier Performance Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Insurers</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 text-gray-600 font-medium">Insurer</th>
                  <th className="text-right py-2 px-2 text-gray-600 font-medium">Claims</th>
                  <th className="text-right py-2 px-2 text-gray-600 font-medium">Parts</th>
                  <th className="text-right py-2 px-2 text-gray-600 font-medium">Value</th>
                  <th className="text-right py-2 px-2 text-gray-600 font-medium">Del. %</th>
                </tr>
              </thead>
              <tbody>
                {safeInsurerPerformance.map((ins, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-2 font-medium text-gray-800 truncate max-w-[130px]" title={ins.insurer_name}>{ins.insurer_name}</td>
                    <td className="py-2 px-2 text-right text-gray-600">{formatNumber(ins.total_claims)}</td>
                    <td className="py-2 px-2 text-right text-gray-600">{formatNumber(ins.total_parts)}</td>
                    <td className="py-2 px-2 text-right text-gray-800 font-medium">{formatCurrency(ins.total_value)}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${ins.delivery_rate >= 70 ? 'bg-green-100 text-green-700' : ins.delivery_rate >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                        {(ins.delivery_rate ?? 0).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Suppliers</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 text-gray-600 font-medium">Supplier</th>
                  <th className="text-right py-2 px-2 text-gray-600 font-medium">Parts</th>
                  <th className="text-right py-2 px-2 text-gray-600 font-medium">Value</th>
                  <th className="text-right py-2 px-2 text-gray-600 font-medium">Del. %</th>
                  <th className="text-right py-2 px-2 text-gray-600 font-medium">Avg Days</th>
                </tr>
              </thead>
              <tbody>
                {safeSupplierPerformance.map((sup, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-2 font-medium text-gray-800 truncate max-w-[130px]" title={sup.supplier_name}>{sup.supplier_name}</td>
                    <td className="py-2 px-2 text-right text-gray-600">{formatNumber(sup.total_parts)}</td>
                    <td className="py-2 px-2 text-right text-gray-800 font-medium">{formatCurrency(sup.total_value)}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${sup.delivery_rate >= 70 ? 'bg-green-100 text-green-700' : sup.delivery_rate >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                        {(sup.delivery_rate ?? 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right text-gray-600">{sup.avg_delivery_days?.toFixed(1) ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Vehicle Stats & State Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {safeVehicleStats.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Vehicles</h3>
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 text-gray-600 font-medium">Make / Model</th>
                    <th className="text-right py-2 px-2 text-gray-600 font-medium">Year</th>
                    <th className="text-right py-2 px-2 text-gray-600 font-medium">Claims</th>
                    <th className="text-right py-2 px-2 text-gray-600 font-medium">Del. %</th>
                  </tr>
                </thead>
                <tbody>
                  {safeVehicleStats.map((v, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-2 font-medium text-gray-800 truncate max-w-[140px]">{v.marca} {v.modelo}</td>
                      <td className="py-2 px-2 text-right text-gray-600">{v.año}</td>
                      <td className="py-2 px-2 text-right text-gray-600">{formatNumber(v.total_claims)}</td>
                      <td className="py-2 px-2 text-right">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${v.delivery_rate >= 70 ? 'bg-green-100 text-green-700' : v.delivery_rate >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                          {(v.delivery_rate ?? 0).toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {safeStatePerformance.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance by State</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={safeStatePerformance.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tickFormatter={(v: number) => `${v}%`} domain={[0, 100]} />
                  <YAxis type="category" dataKey="state" width={80} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                  <Legend />
                  <Bar dataKey="auto_assign_pct" fill="#6366f1" name="Auto Assign %" stackId="a" />
                  <Bar dataKey="generic_pct" fill="#f59e0b" name="Generic %" stackId="b" />
                  <Bar dataKey="original_pct" fill="#10b981" name="Original %" stackId="c" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const getPageTitle = (page: Page): string => {
  return i18n.t(`pageTitle.${page}`, { defaultValue: page });
};


// ============================================================================
// Main App Component
// ============================================================================

const App: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading, logout, hasPageAccess, getDataFilters } = useAuth();
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [selectedInsurer, setSelectedInsurer] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [error, setError] = useState<string | null>(null);

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [partsData, setPartsData] = useState<PartsAnalyticsData | null>(null);
  const [supplierData, setSupplierData] = useState<SupplierAnalyticsData | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
  const [workshopsData, setWorkshopsData] = useState<WorkshopsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Redirect users without dashboard access to their first available page
  useEffect(() => {
    if (isAuthenticated && user && currentPage === 'dashboard') {
      if (!hasPageAccess('dashboard')) {
        // Redirect to first available page for users without dashboard access
        if (hasPageAccess('claims')) {
          setCurrentPage('claims');
        } else if (hasPageAccess('orders')) {
          setCurrentPage('orders');
        }
      } else if (user.role_code === 'supplier_user' && user.supplier_key) {
        // Supplier users land on their profile dashboard
        setCurrentPage('supplier-profile');
      } else if (user.role_code === 'workshop_user' && user.workshop_keys?.length > 0) {
        // Workshop users land on their profile dashboard
        setCurrentPage('workshop-profile');
      }
    }
  }, [isAuthenticated, user, currentPage, hasPageAccess]);


  const buildParams = () => {
    const params = new URLSearchParams();
    if (selectedInsurer) params.set('insurer', selectedInsurer.toString());
    if (dateRange.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange.endDate) params.set('endDate', dateRange.endDate);
    return params.toString();
  };

  const fetchInsurers = useCallback(async () => {
    const token = localStorage.getItem('xnuup_token');
    if (!token) {
      console.log('fetchInsurers skipped: no token');
      return;
    }
    try {
      const res = await authFetch('/api/insurers');
      const json: ApiResponse<Insurer[]> = await res.json();
      if (json.success && json.data) setInsurers(json.data);
    } catch (e) { console.error('Failed to fetch insurers', e); }
  }, []);

  // if (user?.supplier_key && currentPage !== 'supplier-profile') {
  //     setCurrentPage('supplier-profile');
  //   }
  //   if (user?.workshop_key && currentPage !== 'workshop-profile') {
  //     setCurrentPage('workshop-profile');
  //   }
  //   if (user?.insurer_keys && user.insurer_keys.length > 0 && currentPage !== 'insurer-profile') {
  //     setCurrentPage('insurer-profile');
  //   }


  const fetchCurrentPage = useCallback(async () => {
    const token = localStorage.getItem('xnuup_token');
    if (!isAuthenticated || !token) {
      console.log('fetchCurrentPage skipped: no auth');
      return;
    }
    setLoading(true);
    const params = buildParams();
    const queryString = params ? `?${params}` : '';

    try {
      let endpoint = '/api/dashboard';
      if (currentPage === 'parts') endpoint = '/api/analytics/parts';
      if (currentPage === 'suppliers') endpoint = '/api/analytics/suppliers';
      if (currentPage === 'performance') endpoint = '/api/analytics/performance';
      if (currentPage === 'workshops') endpoint = '/api/analytics/workshops';

      const res = await authFetch(endpoint + queryString);
      const json = await res.json();

      if (json.success && json.data) {
        if (currentPage === 'dashboard') setDashboardData(json.data);
        if (currentPage === 'parts') setPartsData(json.data);
        if (currentPage === 'suppliers') setSupplierData(json.data);
        if (currentPage === 'performance') setPerformanceData(json.data);
        if (currentPage === 'workshops') setWorkshopsData(json.data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        setError(json.error || 'Failed to fetch data');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
      setCountdown(REFRESH_INTERVAL);
    }
  }, [currentPage, selectedInsurer, dateRange, isAuthenticated]);

  useEffect(() => { 
    if (isAuthenticated) fetchInsurers(); 
  }, [fetchInsurers, isAuthenticated]);
  
  useEffect(() => { 
    if (isAuthenticated && currentPage !== 'users') fetchCurrentPage(); 
  }, [fetchCurrentPage, isAuthenticated, currentPage]);

  useEffect(() => {
    if (!isAuthenticated || currentPage === 'users') return;
    const timer = setInterval(() => {
      setCountdown((p) => {
        if (p <= 1) { fetchCurrentPage(); return REFRESH_INTERVAL; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [fetchCurrentPage, isAuthenticated, currentPage]);

  const handleNavigate = (page: Page) => {
    if (hasPageAccess(page)) {
      setCurrentPage(page);
      if (page !== 'users') setLoading(true);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#181f26] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#3a7de4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

return ( 
  <div className="min-h-screen bg-gray-50">
    {/* Desktop Sidebar */}
    <Navigation
      activePage={currentPage}
      onPageChange={handleNavigate}
      collapsed={sidebarCollapsed}
      onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      onLogout={logout}
      userRole={user?.role_code || ''}
      hasPageAccess={hasPageAccess}
      user={user}
    />
    
    {/* Header (Desktop + Mobile) */}
    <Header
      pageTitle={getPageTitle(currentPage)}
      insurers={insurers}
      selectedInsurer={selectedInsurer}
      onInsurerChange={setSelectedInsurer}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      sidebarCollapsed={sidebarCollapsed}
    />

    {/* Mobile Bottom Navigation */}
    <MobileNav
      activePage={currentPage}
      onPageChange={handleNavigate}
      onLogout={logout}
      hasPageAccess={hasPageAccess}
    />

    {error && (
      <div className="fixed top-20 md:top-20 right-4 md:right-6 z-20 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 max-w-[calc(100%-2rem)] md:max-w-md">
        Error: {error}
      </div>
    )}

    {/* Main Content */}
    <main className={`pt-14 md:pt-16 pb-20 md:pb-6 min-h-screen transition-all duration-300 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
      <div className="p-4 md:p-6">
        {currentPage === 'dashboard' && <DashboardPage data={dashboardData} loading={loading} dateRange={dateRange} />}
        {currentPage === 'operations' && <OperationsPage dateRange={dateRange} insurerKey={selectedInsurer} />}
        {currentPage === 'parts' && <PartsAnalyticsPage data={partsData} loading={loading} />}
        {currentPage === 'suppliers' && <SupplierAnalyticsPage data={supplierData} loading={loading} />}
        {currentPage === 'performance' && <PerformanceAnalyticsPage data={performanceData} loading={loading} />}
        {currentPage === 'workshops' && <WorkshopsAnalyticsPage data={workshopsData} loading={loading} />}
        {currentPage === 'claims' && <ClaimsPage selectedInsurer={selectedInsurer} dateRange={dateRange} />}
        {currentPage === 'orders' && <OrdersPage selectedInsurer={selectedInsurer} dateRange={dateRange} />}
        {currentPage === 'patterns' && <PatternsPage selectedInsurer={selectedInsurer} dateRange={dateRange} />}
        {currentPage === 'reports' && (
          <ReportsPage 
            selectedInsurer={selectedInsurer} 
            dateRange={dateRange}
            insurers={insurers}
            userRole={user?.role_code || ''}
          />
        )}
        {currentPage === 'shareholders' && <ShareholdersPage dateRange={dateRange} />}
        {currentPage === 'supplier-profile' && <SupplierProfilePage dateRange={dateRange} authFetch={authFetch} />}
        {currentPage === 'insurer-profile' && <InsurerProfilePage dateRange={dateRange} authFetch={authFetch} />}
        {currentPage === 'workshop-profile' && <WorkshopProfilePage dateRange={dateRange} authFetch={authFetch} />}
        {currentPage === 'eda' && <EDADashboard />}
        {currentPage === 'etl' && <ETLDashboard />}
        {currentPage === 'users' && (
          <ProtectedRoute pageCode="users">
            <UserManagementPage 
              currentUserRole={user?.role_code || ''} 
              currentUserInsurerKeys={user?.insurer_keys}
            />
          </ProtectedRoute>
        )}
      </div>
    </main>
  </div>
);
};

export default App;